import React, { useEffect, useMemo, useState } from 'react';
import { getHeaders, listAccountCarts, cancelCart, shareCart, updateAccountProfile, createContact, updateContactPreferences, domainCheck, transferDomain, fetchAccountDomainTransfers, addTransferEpp, fetchTransferRegistrarStatus, listAccountPeople, startImpersonation, removeCustomerMfa, fetchWhoami, endImpersonation, listAccountImpersonation, listAccountEvents, getAccountEmail, getImpersonationDetail, getAccountBillingStatus, listAccountCharges, issueRefund, listAccountBillingActions, listAccountProducts, cancelEmailSubscription, getHostingSupportInfo, revealHostingAdminCredential, cancelHostingSubscription, cancelDomainSubscription, cancelPendingSetup, reconcileSubscription, listAccountCases, createAccountCase, createStaffTicket, addCaseComment, getDomainDetail } from '../api';
import DomainManagePanel from '../components/DomainManagePanel';
import HostingManagePanel from '../components/HostingManagePanel';
import VpsServerPanel from '../components/VpsServerPanel';
import HostingGoLive from '../components/HostingGoLive';
import PendingSetupForm from '../components/PendingSetupForm';
import { isPlaceholderHost } from '../lib/hostingGoLive';
import AccountManagerPanel from './AccountManagerPanel';
import { useViewContext } from '../lib/viewContext';
import DnsSyncModal from './DnsSyncModal';
import CartBuilderModal from './CartBuilderModal';
import RepCheckoutModal from './RepCheckoutModal';
import { StageChangeModal } from '../components/DealStage';
import ConversationsBrowser from '../components/ConversationsBrowser';
import { formatSfDate } from '../lib/dates';
import { registrarPresence, transferStageLabel, transferNeedsAction } from '../lib/domainDetailState';
import { receiptLines, receiptTotals, receiptNotes, findCharge, ordersSharingPayment, paymentMethodText, stripeReceiptHref } from '../lib/orderReceipt';
import { refundLineOptions, refundLinesProblem, refundLinesTotalCents, refundAttributionText, buildRefundRequest } from '../lib/refundLines';
import { newTicketBody, defaultContactId, contactLabel, deliveryText } from '../lib/caseVisibility';

const API = import.meta.env.PROD ? '' : 'https://crm-api.squish.co';

// How the sale came in — Order.Channel__c, stamped by squish-backend at
// fulfilment (squish-docs/runbooks/deploy-order-attribution.md). Five values,
// and a fifth state: blank. Blank means the order predates the field or the
// field is not readable for this user, and it renders as "—" rather than
// guessing "Website" — the CEO sales page makes the same distinction, and a
// guess here would be the one place in the CRM that disagrees with it.
const ORDER_CHANNELS = {
  Web:     { label: 'Website',  color: 'var(--text2)',  title: 'Customer checked out on squish.co.' },
  Chat:    { label: 'AI Chat',  color: '#34d399',       title: 'At least one line was added from the front-of-site AI assistant.' },
  MCP:     { label: 'MCP',      color: '#f472b6',       title: "The customer's own AI assistant (Claude, ChatGPT, Cursor...) prepared or paid for it over the Squish MCP server." },
  Rep:     { label: 'Rep',      color: '#818cf8',       title: 'A staff member placed this order (rep checkout / phone).' },
  Renewal: { label: 'Renewal',  color: '#fbbf24',       title: 'Automatic subscription renewal — nobody placed it.' },
};

function OrderChannel({ order }) {
  const meta = ORDER_CHANNELS[order?.Channel__c];
  if (!meta) {
    return (
      <span title="No origin recorded — this order predates channel tracking, or the field is not readable for your user."
        style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)' }}>—</span>
    );
  }
  const rep = order.Placed_By__r?.Name;
  return (
    <div title={rep ? `${meta.title} Placed by ${rep}.` : meta.title}>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700, color: meta.color }}>{meta.label}</span>
      {rep && <div style={{ fontSize: 10, color: 'var(--text3)' }}>{rep}</div>}
    </div>
  );
}

function Loading() {
  return <div className="loading-state"><span className="loading-dot" /><span className="loading-dot" /><span className="loading-dot" /></div>;
}

// Order.EffectiveDate is a bare calendar day and used to be rendered through
// the local zone, which put every order a day early on this page. See
// lib/dates.js.
const formatDate = (d) => formatSfDate(d);

// Money is rendered EXACTLY, to the cent. This used to round to whole dollars
// (minimumFractionDigits: 0 / maximumFractionDigits: 0), which meant the Loot
// tab showed a $1,234.56 order as "$1,235" and a $0.75 partial refund as
// "−$1 refunded" — while the Transactions tab renders the very same charge as
// `$${(c.amountCents / 100).toFixed(2)}`. Two tabs on one account disagreeing
// about one payment is worse than a long number.
//
// It also used to em-dash on `!v`, which swallows a legitimate $0.00 (a fully
// discounted order, a $0 renewal) along with null. Only a value we cannot read
// as a number is unknown; zero is a real amount and prints as $0.00.
//
// Intl currency formatting (rather than a `$` + toLocaleString template) so a
// negative reads "-$5.00" rather than "$-5.00".
const CURRENCY_EXACT = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD',
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

// Whole dollars, for figures that are an ESTIMATE rather than money that
// moved. Only Account.AnnualRevenue uses this — a firmographic number a rep
// types in from a guess, where "$5,000,000.00" implies a precision that isn't
// there. Anything that corresponds to a charge, refund, order or cart total
// must use formatCurrency instead.
const CURRENCY_ROUNDED = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD',
  minimumFractionDigits: 0, maximumFractionDigits: 0,
});

function toFiniteAmount(v) {
  if (v === null || v === undefined || v === '') return null;
  // parseFloat, not Number: same coercion the rounded version has always used,
  // so this change cannot start rejecting a value that used to render.
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

// Exported for test/currency-format.test.js — the rounding this replaced was
// invisible until you put two tabs side by side, so it is pinned.
export function formatCurrency(v) {
  const n = toFiniteAmount(v);
  return n === null ? '—' : CURRENCY_EXACT.format(n);
}

export function formatCurrencyRounded(v) {
  const n = toFiniteAmount(v);
  return n === null ? '—' : CURRENCY_ROUNDED.format(n);
}

// The line under Lifetime Spend for money the customer has paid that is on no
// Order yet. A domain transfer is charged at checkout but its Order line is
// written only when the transfer completes (~5 days), so until then Lifetime
// Spend is short of what was charged and, without this, reads as a billing bug.
// commerce.pendingTransfers comes from squish-crm-backend
// (lib/pending-transfer-spend.js); null there means "could not check" and shows
// nothing, same as none. Exported for test/pending-transfer-note.test.js.
export function pendingTransferNote(commerce) {
  const p = commerce?.pendingTransfers;
  if (!p || !(p.count > 0)) return null;
  const noun = p.count === 1 ? 'transfer' : 'transfers';
  const total = toFiniteAmount(p.total) || 0;
  // unpriced transfers carry no recorded price, so the total is a floor.
  // Kept short on purpose: at a ~975px window the card is ~170px wide and a
  // longer line wraps, making the whole stat strip a row taller.
  const text = total <= 0 ? `+ paid ${noun}`
    : p.unpriced > 0      ? `+ at least ${formatCurrency(total)} in ${noun}`
    :                       `+${formatCurrency(total)} in ${noun}`;
  const names = (p.domains || []).join(', ');
  return {
    text,
    title: `Paid at checkout, not on an order yet. Added to Lifetime Spend when ${names || `the ${noun}`} ${p.count === 1 ? 'finishes' : 'finish'} transferring in.`,
  };
}

function priorityBadge(p) {
  const map = { 'High': 'badge-red', 'Medium': 'badge-yellow', 'Low': 'badge-green' };
  return map[p] || 'badge-gray';
}

function statusBadge(s) {
  const map = { 'New': 'badge-blue', 'Working': 'badge-yellow', 'Escalated': 'badge-red', 'Closed': 'badge-green', 'Closed Won': 'badge-green', 'Closed Lost': 'badge-red' };
  return map[s] || 'badge-gray';
}

// ─── Products Tab ─────────────────────────────────────────────────────────────

// A subscription's LIVE state (from the Customer Product Adapter) can diverge
// from its Salesforce Order.Status. Cancelling from the Services tab stops the
// vendor sub + logs an Event but never rewrites the SF Order, so Loot would
// otherwise show a stale "Activated" for a product that's actually cancelled.
// These map an adapter `state` to how a matching order line should annotate.
const HARD_CANCELLED_STATES   = new Set(['CANCELLED', 'CANCELLED_BEFORE_PROVISIONING']);
const SCHEDULED_CANCEL_STATES = new Set(['CANCEL_SCHEDULED']);

// Build domain → live-cancel summary from adapter products. A domain is flagged
// only when it has products AND none are still active — so a domain that keeps
// one live product (e.g. hosting retained, email dropped) is NOT flagged. This
// errs toward under-reporting: it never paints a live product as cancelled.
function buildDomainCancelMap(products) {
  const byDomain = new Map();
  for (const p of products || []) {
    const d = (p.linkedDomain || p.productId || '').toLowerCase();
    if (!d) continue;
    (byDomain.get(d) || byDomain.set(d, []).get(d)).push(p);
  }
  const out = new Map();
  for (const [d, ps] of byDomain) {
    const anyActive = ps.some(p => !HARD_CANCELLED_STATES.has(p.state) && !SCHEDULED_CANCEL_STATES.has(p.state));
    if (anyActive) continue;
    const hard = ps.some(p => HARD_CANCELLED_STATES.has(p.state));
    out.set(d, hard ? { kind: 'cancelled', label: 'Subscription cancelled' }
                    : { kind: 'scheduled', label: "Won't renew" });
  }
  return out;
}

// The ORDERS table. Named ProductsTab until 2026-08-25, which was actively
// misleading once a real Products list existed beside it.
function OrdersTable({ accountId, accountName }) {
  const [orders, setOrders]     = useState([]);
  const [products, setProducts] = useState([]);   // live subscription state (overlay)
  const [loading, setLoading]   = useState(true);
  // The order whose receipt is open, by Id: it is looked up in `orders` each
  // render, so a refund made from the receipt shows there once the list reloads.
  const [receiptId, setReceiptId] = useState(null);
  // Two failures with different meanings, so they are two pieces of state.
  // `loadError` says the ORDER LIST is unknown — the table must not then claim
  // the account has none. `actionError` says a write failed while the list we
  // are showing is still good. Sharing one `error` meant a failed cancel and a
  // failed load overwrote each other, and neither could gate the empty row.
  const [loadError, setLoadError]     = useState('');
  const [actionError, setActionError] = useState('');
  const [cancelingOrder, setCancelingOrder] = useState(null);

  useEffect(() => { loadOrders(); }, [accountId]);

  // A 403 from the account gate and a dropped connection both used to land
  // where an empty account lands: `data.orders` undefined, `orders` left [],
  // and the table saying "No orders yet — build a cart". That is a positive
  // claim about the customer made from a request that never answered, and a
  // rep acts on it. Every exit from here either has the list or has a reason.
  async function loadOrders() {
    setLoading(true);
    setLoadError('');
    try {
      const res  = await fetch(`${API}/api/accounts/${accountId}/orders`, { headers: getHeaders() });
      // A 502 from the edge answers with HTML, not JSON. Without the catch the
      // parse threw first and the rep saw a JSON syntax error instead of the
      // status that explains it.
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Could not load orders (${res.status})`);
      setOrders(data.orders || []);
      // Best-effort live state for the cancellation overlay — a product-fetch
      // failure must not break the orders table.
      try {
        const pr = await listAccountProducts(accountId);
        setProducts(pr.products || []);
      } catch { setProducts([]); }
    } catch (e) {
      setOrders([]);
      setLoadError(e.message || 'Could not load orders');
    }
    setLoading(false);
  }

  // A cancel is a write to Salesforce and the button stays live while the
  // PATCH is in flight, so an impatient double-click sent it twice.
  async function handleCancelOrder(orderId) {
    if (cancelingOrder) return;
    if (!window.confirm('Cancel this order in Salesforce?')) return;
    setActionError('');
    setCancelingOrder(orderId);
    try {
      const res = await fetch(`${API}/api/orders/${orderId}/cancel`, { method: 'PATCH', headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to cancel');
      loadOrders();
    } catch (e) {
      setActionError(e.message);
    }
    setCancelingOrder(null);
  }

  // NET of refunds: each order carries refundedCents (from the billing ledger,
  // matched by PaymentIntent). Total Value subtracts refunds so it agrees with
  // the net Lifetime Spend card; a fully-refunded order no longer counts.
  const refundedDollars = (o) => (o.refundedCents || 0) / 100;
  const fullyRefunded   = (o) => (o.refundedCents || 0) > 0
    && (o.refundedCents || 0) >= Math.round((parseFloat(o.TotalAmount) || 0) * 100);
  const totalValue = orders.reduce(
    (sum, o) => sum + Math.max(0, (parseFloat(o.TotalAmount) || 0) - refundedDollars(o)), 0
  );

  // Live-cancellation overlay. Match each order line's Domain_Name__c to the
  // adapter's per-domain cancel summary so Loot reflects subscriptions cancelled
  // from the Services tab (which never touches the SF Order).
  const domainCancel  = buildDomainCancelMap(products);
  const lineCancel    = (item) => {
    const d = (item.Domain_Name__c || '').toLowerCase();
    return d ? domainCancel.get(d) || null : null;
  };
  const orderCancel   = (o) => {
    const flags = (o.OrderItems?.records || []).map(lineCancel).filter(Boolean);
    if (!flags.length) return null;
    return flags.some(f => f.kind === 'cancelled')
      ? { kind: 'cancelled', label: 'Subscription cancelled' }
      : { kind: 'scheduled', label: "Won't renew" };
  };
  const statusBadge = orderStatusBadge;
  const isCancelledStatus = (o) => o.effectiveStatus === 'cancelled' || o.effectiveStatus === 'cancelled_refunded';

  const activeCount = orders.filter(o =>
    o.effectiveStatus ? o.effectiveStatus === 'active'
      : (o.Status === 'Activated' && orderCancel(o)?.kind !== 'cancelled' && !fullyRefunded(o))
  ).length;

  return (
    <div>
      {/* Counted from `orders`, which is [] both for an account with none and
          for a load that failed — so on a failure these read "0 orders, $0,
          0 active", three more claims about the customer drawn from a request
          that never answered. An em-dash says we do not know. */}
      <div className="stat-grid m-tiles" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 20 }}>
        <div className="stat-card"><div className="stat-label">Total Orders</div><div className="stat-value" style={{ fontSize: 24 }}>{loadError ? '—' : orders.length}</div></div>
        <div className="stat-card"><div className="stat-label">Total Value</div><div className="stat-value green" style={{ fontSize: 22 }}>{loadError ? '—' : formatCurrency(totalValue)}</div></div>
        <div className="stat-card"><div className="stat-label">Active</div><div className="stat-value" style={{ fontSize: 24 }}>{loadError ? '—' : activeCount}</div></div>
      </div>

      <div className="table-card">
        <div className="table-header">
          <span className="table-title">Orders & Subscriptions</span>
        </div>
        {loading && <Loading />}
        {actionError && <div className="error-state">⚠ {actionError}</div>}
        {!loading && (
          <table className="m-cards">
            <thead><tr><th>Order #</th><th>Products</th><th>Origin</th><th>Status</th><th>Total</th><th>Date</th><th></th></tr></thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.Id} className="order-row" onClick={() => setReceiptId(o.Id)} title="Open the receipt">
                  <td>
                    <button type="button" className="order-open" onClick={e => { e.stopPropagation(); setReceiptId(o.Id); }}>
                      {o.OrderNumber || '—'}<span aria-hidden="true" className="order-open-arrow">›</span>
                    </button>
                  </td>
                  <td className="m-card-title" style={{ color: 'var(--text)' }}>
                    {o.OrderItems?.records?.map(i => {
                      const c = lineCancel(i);
                      return (
                        <div key={i.Id} style={{ fontSize: 12, color: c?.kind === 'cancelled' ? 'var(--text3)' : 'var(--text2)', textDecoration: c?.kind === 'cancelled' ? 'line-through' : 'none' }}>{i.Product2?.Name || i.Description || '—'} × {i.Quantity}</div>
                      );
                    }) || '—'}
                  </td>
                  <td><OrderChannel order={o} /></td>
                  <td>
                    {(() => {
                      const b = statusBadge(o);
                      return <span className={`badge ${b.cls}`} title="Effective status: SF order + refunds + live product state. The SF Order record stays unchanged for audit.">{b.label}</span>;
                    })()}
                    {/* A scheduled cancel ("won't renew") still reads Active — it lives
                        until term end — so flag it separately from the main badge. */}
                    {!isCancelledStatus(o) && orderCancel(o)?.kind === 'scheduled' && (
                      <div title="A subscription on this order is set to not renew; it stays active until term end."
                        style={{ marginTop: 4, fontSize: 10, fontFamily: 'var(--mono)', color: '#fbbf24' }}>
                        ⊘ Won't renew
                      </div>
                    )}
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', color: 'var(--accent3)' }}>
                    {formatCurrency(o.TotalAmount)}
                    {(o.refundedCents || 0) > 0 && (
                      <div style={{ fontSize: 10, color: '#fbbf24' }}>−{formatCurrency((o.refundedCents || 0) / 100)} refunded</div>
                    )}
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)' }}>{formatDate(o.EffectiveDate)}</td>
                  {/* The row opens the receipt; the action cell is not part of that. */}
                  <td onClick={e => e.stopPropagation()} style={{ cursor: 'default' }}>
                    {o.Status !== 'Cancelled' && !isCancelledStatus(o) && (
                      <button data-write onClick={() => handleCancelOrder(o.Id)} disabled={cancelingOrder === o.Id} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text3)', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: cancelingOrder === o.Id ? 'default' : 'pointer', opacity: cancelingOrder === o.Id ? 0.5 : 1, fontFamily: 'var(--mono)' }}>{cancelingOrder === o.Id ? 'Cancelling…' : 'Cancel'}</button>
                    )}
                  </td>
                </tr>
              ))}
              {orders.length === 0 && loadError && (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: '#f87171', padding: 32 }}>
                  {loadError} — this is a load failure, not an empty account. Retry before assuming there is nothing here.
                </td></tr>
              )}
              {orders.length === 0 && !loadError && (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text3)', padding: 32 }}>
                  No orders yet — build a cart from the Draft Carts section to sell something.
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {receiptId && (
        <OrderReceipt
          accountId={accountId}
          accountName={accountName}
          orderId={receiptId}
          orders={orders}
          onClose={() => setReceiptId(null)}
          onChanged={loadOrders}
        />
      )}
    </div>
  );
}

// ─── Order receipt ───────────────────────────────────────────────────────────
// One order, looked into: every line at its price, what was charged for it,
// what came back, and anything about those numbers that does not add up. Opens
// from the History orders table and from an order in the Overview's Recent
// Activity. The arithmetic and the order↔charge join are in lib/orderReceipt.js.

// The backend stamps each order with a single derived effectiveStatus
// (Active / Cancelled / Cancelled + Refunded / Partially refunded / Draft)
// from SF status + refunds + live product cancel state. That's the one badge
// every surface shows. Fall back to raw SF Status for any un-annotated order.
const ORDER_STATUS_BADGE = {
  active:             { label: 'Active',               cls: 'badge-green' },
  cancelled:          { label: 'Cancelled',            cls: 'badge-red' },
  cancelled_refunded: { label: 'Cancelled + Refunded', cls: 'badge-red' },
  partially_refunded: { label: 'Partially refunded',   cls: 'badge-yellow' },
  draft:              { label: 'Draft',                cls: 'badge-gray' },
};
function orderStatusBadge(o) {
  const b = o.effectiveStatus && ORDER_STATUS_BADGE[o.effectiveStatus];
  if (b) return { label: o.effectiveLabel || b.label, cls: b.cls };
  const s = o.Status;
  return { label: s, cls: s === 'Activated' ? 'badge-green' : s === 'Draft' ? 'badge-gray' : s === 'Cancelled' ? 'badge-red' : 'badge-yellow' };
}

// A CRM refund carries our own reason in metadata.squish_reason; Stripe's
// `reason` only has three values and is all a refund made elsewhere has.
// (A function, not a lookup table built here: REFUND_REASONS is declared
// further down this module and is not initialised yet when this line runs.)
function refundReasonText(r) {
  const ours = REFUND_REASONS.find(x => x.value === r.metadata?.squish_reason);
  if (ours) return ours.label;
  if (r.reason === 'requested_by_customer') return 'Customer request';
  if (r.reason === 'duplicate') return 'Duplicate charge';
  if (r.reason === 'fraudulent') return 'Fraudulent';
  return 'Refund';
}
const centsText = (c) => (c === null || c === undefined ? '—' : formatCurrency(c / 100));
const stampText = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

// `orders` is the account's order list when the caller already holds it (the
// History table); without it — from the Overview — the receipt fetches it,
// because the Overview's recent-orders preview carries neither line prices
// nor the PaymentIntent the charge is matched on.
function OrderReceipt({ accountId, accountName, orderId, orders: givenOrders, onClose, onChanged }) {
  const [ownOrders, setOwnOrders]     = useState(null);
  const [ordersError, setOrdersError] = useState('');
  const [charges, setCharges]         = useState(null);
  const [chargesError, setChargesError] = useState('');
  const [refundTarget, setRefundTarget] = useState(null);
  const [toast, setToast]             = useState('');
  const [copied, setCopied]           = useState(false);

  const orders = givenOrders || ownOrders;
  const order = orders?.find(o => o.Id === orderId) || null;
  const pi = order?.Payment_Intent_ID__c || null;

  async function loadOrders() {
    setOrdersError('');
    try {
      const res  = await fetch(`${API}/api/accounts/${accountId}/orders`, { headers: getHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Could not load the order (${res.status})`);
      setOwnOrders(data.orders || []);
    } catch (e) {
      setOrdersError(e.message || 'Could not load the order');
    }
  }

  // The charges list is the latest N PaymentIntents per contact email. Look in
  // the usual 25 first; only an order whose payment is not there pays for the
  // deeper (slower — every PI's refunds are paged) 100.
  async function loadCharges() {
    setChargesError('');
    try {
      let body = await listAccountCharges(accountId);
      if (pi && !(body.charges || []).some(c => c.paymentIntentId === pi)) {
        body = await listAccountCharges(accountId, { limit: 100 });
      }
      setCharges(body.charges || []);
    } catch (e) {
      setChargesError(e.message || 'Could not load charges');
    }
  }

  useEffect(() => { if (!givenOrders) loadOrders(); /* eslint-disable-next-line */ }, [accountId, orderId]);
  useEffect(() => { if (order) loadCharges(); /* eslint-disable-next-line */ }, [accountId, pi, !!order]);

  // Escape closes — unless the refund modal is up, which owns the keyboard.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !refundTarget) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [refundTarget, onClose]);

  function onRefunded(result) {
    setRefundTarget(null);
    setToast(`Refunded $${(result.amountCents / 100).toFixed(2)}. Audit: ${result.auditId}`);
    setTimeout(() => setToast(''), 6000);
    loadCharges();
    if (!givenOrders) loadOrders();
    onChanged?.();
  }

  async function copyPi() {
    try {
      await navigator.clipboard.writeText(pi);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard refused — the id is on screen to select by hand */ }
  }

  const charge   = order ? findCharge(order, charges) : null;
  const lines    = order ? receiptLines(order) : [];
  const totals   = order ? receiptTotals(order, charge) : null;
  const siblings = order ? ordersSharingPayment(order, orders) : [];
  const notes    = order ? receiptNotes(order, { charge, charges, chargesError, siblings }) : [];
  const refunds  = (charge?.refunds || []).slice().sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
  // The payment's booked lines, named from the Orders it paid: what the Refund
  // modal offers to put the money against, and what a past refund's
  // attribution is read with.
  const lineOptions = charge ? refundLineOptions(charge, orders) : [];
  const blocked  = charge ? refundBlockedReason(charge) : 'No payment found';
  const badge    = order ? orderStatusBadge(order) : null;
  const payer    = charge?.customerEmail || null;
  const paidWith = paymentMethodText(charge);
  const stripeReceipt = stripeReceiptHref(charge);

  return (
    <div className="receipt-overlay" onClick={onClose}>
      <div className="receipt-slip" role="dialog" aria-modal="true" aria-label={order ? `Order ${order.OrderNumber || ''} receipt` : 'Order receipt'} onClick={e => e.stopPropagation()}>
        <button type="button" className="receipt-close receipt-noprint" onClick={onClose} aria-label="Close">✕</button>

        {!order && !ordersError && <Loading />}
        {!order && ordersError && (
          <div className="receipt-note is-warn">{ordersError} — this is a load failure; the order itself is not known to be missing.</div>
        )}
        {!order && orders && !ordersError && (
          <div className="receipt-note is-warn">This order is not in the account's latest 50 orders.</div>
        )}

        {order && (
          <>
            <header className="receipt-head">
              <div className="receipt-kicker">squish · receipt</div>
              <div className="receipt-title-row">
                <h2 className="receipt-number">{order.OrderNumber || 'Order'}</h2>
                <span className={`badge ${badge.cls}`} title="Effective status: SF order + refunds + live product state. The SF Order record stays unchanged for audit.">{badge.label}</span>
              </div>
              <div className="receipt-meta is-dots">
                <span>{formatDate(order.EffectiveDate)}</span>
                {accountName && <span>{accountName}</span>}
                {payer && <span>{payer}</span>}
              </div>
              <div className="receipt-meta receipt-noprint">
                <span className="receipt-meta-label">Origin</span>
                {(() => {
                  const ch = ORDER_CHANNELS[order.Channel__c];
                  if (!ch) return <span title="No origin recorded — this order predates channel tracking, or the field is not readable for your user.">—</span>;
                  const rep = order.Placed_By__r?.Name;
                  return <span title={rep ? `${ch.title} Placed by ${rep}.` : ch.title}><b style={{ color: ch.color }}>{ch.label}</b>{rep ? ` · ${rep}` : ''}</span>;
                })()}
                {/* The SF record is never rewritten, so it is worth showing only
                    when it tells a different story from the badge — an order
                    cancelled in effect that Salesforce still calls live, or the
                    reverse. */}
                {order.effectiveStatus && ['cancelled', 'cancelled_refunded'].includes(order.effectiveStatus) !== (order.Status === 'Cancelled') && (
                  <><span className="receipt-meta-label">Salesforce</span><span>{order.Status}</span></>
                )}
              </div>
              {order.Description && <p className="receipt-desc">{order.Description}</p>}
            </header>

            {toast && <div className="receipt-note is-good receipt-noprint">✓ {toast}</div>}
            {notes.length > 0 && (
              <div className="receipt-notes receipt-noprint">
                {notes.map((n, i) => <div key={i} className={`receipt-note is-${n.tone}`}>{n.tone === 'warn' ? '⚠ ' : ''}{n.text}</div>)}
              </div>
            )}

            <ul className="receipt-lines">
              {lines.map(l => (
                <li key={l.id} className="receipt-line">
                  <div className="receipt-line-main">
                    <div className="receipt-line-name">{l.name}</div>
                    {(l.domain || l.detail || l.code) && (
                      <div className="receipt-line-sub">
                        {l.domain && <span className="receipt-chip">{l.domain}</span>}
                        {l.detail && l.detail !== l.domain && <span>{l.detail}</span>}
                        {l.code && <span className="receipt-code">{l.code}</span>}
                      </div>
                    )}
                  </div>
                  <div className="receipt-line-qty">{l.qty ?? '—'} × {centsText(l.unitCents)}</div>
                  <div className="receipt-line-total">{centsText(l.lineCents)}</div>
                </li>
              ))}
              {lines.length === 0 && <li className="receipt-line is-empty">No line items on this order.</li>}
            </ul>

            {(() => {
              // The breakdown only when something moved the number; a plain
              // paid order is one line, the way a till receipt is.
              const chargedDiffers = totals.chargedCents !== null && totals.chargedCents !== totals.orderCents;
              const adjusted = chargedDiffers || totals.refundedCents > 0;
              return (
                <dl className="receipt-totals">
                  {adjusted && <div><dt>Order total</dt><dd>{centsText(totals.orderCents)}</dd></div>}
                  {chargedDiffers && <div><dt>Charged</dt><dd>{centsText(totals.chargedCents)}</dd></div>}
                  {totals.refundedCents > 0 && (
                    <div className="is-refund"><dt>Refunded</dt><dd>−{centsText(totals.refundedCents)}</dd></div>
                  )}
                  <div className="is-net">
                    {/* "Paid" only when a succeeded charge says so. */}
                    <dt>{adjusted ? (charge?.status === 'succeeded' ? 'Net paid' : 'Net') : (charge?.status === 'succeeded' ? 'Total paid' : 'Total')}</dt>
                    <dd>{centsText(totals.netCents)}</dd>
                  </div>
                </dl>
              );
            })()}

            <section className="receipt-pay">
              <div className="receipt-pay-head">
                <span className="receipt-section-label">Payment</span>
                {charge && (
                  <span className={`badge ${charge.fullyRefunded ? 'badge-yellow' : charge.status === 'succeeded' ? 'badge-green' : 'badge-red'}`}>
                    {charge.fullyRefunded ? 'Refunded' : charge.status}
                  </span>
                )}
              </div>
              {!pi && <div className="receipt-pay-empty">No payment linked.</div>}
              {pi && !charge && !charges && !chargesError && <div className="receipt-pay-empty">Looking up the charge…</div>}
              {pi && (
                <div className="receipt-pay-grid">
                  {charge && <><span>Charged</span><span>{stampText(charge.createdAt)}</span></>}
                  {paidWith && <><span>Card</span><span>{paidWith}</span></>}
                  {charge?.description && <><span>Stripe says</span><span>{charge.description}</span></>}
                  <span>Payment</span>
                  <span className="receipt-pi">
                    <code>{pi}</code>
                    <button type="button" className="receipt-mini receipt-noprint" onClick={copyPi}>{copied ? 'Copied' : 'Copy'}</button>
                  </span>
                </div>
              )}
              {refunds.length > 0 && (
                <ul className="receipt-refunds">
                  {refunds.map(r => (
                    <li key={r.id}>
                      <span>{stampText(r.createdAt)}</span>
                      <span className="receipt-refund-amt">−{centsText(r.amountCents)}</span>
                      <span>
                        {refundReasonText(r)}{r.status !== 'succeeded' ? ` · ${r.status}` : ''}
                        {refundAttributionText(r, lineOptions) && (
                          <span className="receipt-refund-lines" title="The lines this refund was issued against"> · {refundAttributionText(r, lineOptions)}</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <footer className="receipt-actions receipt-noprint">
              <button type="button" className="receipt-btn" onClick={() => window.print()}>Print</button>
              {stripeReceipt && (
                <a className="receipt-btn receipt-noprint" href={stripeReceipt} target="_blank" rel="noopener noreferrer">Open Stripe receipt</a>
              )}
              <span style={{ flex: 1 }} />
              {charge && blocked === null && (
                <button type="button" data-write className="receipt-btn is-primary" onClick={() => setRefundTarget(charge)}>Refund…</button>
              )}
              {charge && blocked !== null && !charge.fullyRefunded && (
                <span className="receipt-blocked" title={blocked}>{blocked}</span>
              )}
            </footer>
          </>
        )}
      </div>

      {refundTarget && (
        <div onClick={e => e.stopPropagation()}>
          <RefundModal charge={refundTarget} accountId={accountId} lineOptions={lineOptions} onClose={() => setRefundTarget(null)} onRefunded={onRefunded} />
        </div>
      )}
    </div>
  );
}

// ─── Overview Tab ─────────────────────────────────────────────────────────────
// Top-of-account hub: lifetime stats, alerts, editable account info + billing,
// account notes, recent orders preview, recent activity, saved payment methods
// stub. This is what staff look at FIRST on any account — make it useful.

function OverviewTab({ data, accountId, onSaved, onNavigateToTicket }) {
  const { account, commerce = {}, alerts = [], cases = [], opportunities = [], tasks = [], contacts = [] } = data;
  // First contact carrying an email — the one a rep would actually write to.
  // Falls back to the first contact at all, so a phone-only record still shows.
  const primaryContact = contacts.find(c => c.Email) || contacts[0] || null;
  const [receiptId, setReceiptId] = useState(null);

  // Build a lightweight interleaved "recent activity" feed from cases, orders, tasks.
  const activity = [
    ...(cases || []).slice(0, 5).map(c => ({
      kind: 'case',
      id: c.Id,
      title: c.Subject || `Case #${c.CaseNumber}`,
      sub: `${c.Status} · ${c.Priority || 'Medium'}`,
      date: c.CreatedDate,
      badge: c.Status === 'Closed' ? 'badge-green' : 'badge-yellow',
    })),
    ...(commerce.recentOrders || []).slice(0, 5).map(o => ({
      kind: 'order',
      id: o.Id,
      title: `Order ${o.OrderNumber || ''} — ${formatCurrency(o.TotalAmount)}`,
      sub: o.Description || (o.OrderItems?.records?.[0]?.Product2?.Name) || o.Status,
      date: o.EffectiveDate || o.CreatedDate,
      badge: o.Status === 'Activated' ? 'badge-green' : 'badge-gray',
    })),
    ...(tasks || []).slice(0, 5).map(t => ({
      kind: 'task',
      id: t.Id,
      title: t.Subject || 'Task',
      sub: t.Status || '—',
      date: t.ActivityDate || t.CreatedDate,
      badge: t.Status === 'Completed' ? 'badge-green' : 'badge-yellow',
    })),
  ].filter(a => a.date).sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 6);

  return (
    <div className="overview-grid">
      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="table-card" style={{ padding: 14, borderColor: 'rgba(250,200,80,0.4)', background: 'rgba(250,200,80,0.04)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#fac850', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
            ⚠ Alerts
          </div>
          {alerts.map((a, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: i < alerts.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <span style={{ fontSize: 13, color: 'var(--text)' }}>{a.text}</span>
              <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{a.hint}</span>
            </div>
          ))}
        </div>
      )}

      {/* Account manager + Billing status first thing after alerts.
          Member Since moved to the page subtitle; Lifetime/Cases/Deals
          live in the page-level stat grid (above the tabs). Both removed
          from here to cut the duplication Casey flagged. */}
      <AccountManagerPanel accountId={accountId} account={account} onUpdated={onSaved} />

      {/* Two-column body: static account data on the left, live status +
          activity on the right. Collapses to a single column below ~1000px.
          Goal: the whole Overview fits one screen without scrolling. */}
      <div className="overview-cols">
      <div className="overview-col">
      {/* Account Info (includes billing address fields) — used to be two
          separate cards but ~80% of rep edits touch both, so a single wider
          card with the address rows below the core fields cuts the visual
          noise in half. */}
      <EditableCard
        title="Account Info"
        accountId={accountId}
        onSaved={onSaved}
        columns={2}
        fields={[
          { key: 'Name',              label: 'Name',            value: account.Name },
          { key: 'Type',              label: 'Type',            value: account.Type },
          { key: 'Industry',          label: 'Industry',        value: account.Industry },
          { key: 'Website',           label: 'Website',         value: account.Website },
          { key: 'Phone',             label: 'Phone',           value: account.Phone },
          { key: 'Fax',               label: 'Fax',             value: account.Fax },
          { key: 'NumberOfEmployees', label: 'Employees',       value: account.NumberOfEmployees, type: 'number' },
          // Rounded on purpose: this is the customer's self-reported annual
          // revenue, not money that moved through us. Every other money field
          // on this page uses formatCurrency and reads to the cent.
          { key: 'AnnualRevenue',     label: 'Annual Revenue',  value: account.AnnualRevenue, type: 'number', displayFmt: formatCurrencyRounded },
          { key: 'BillingStreet',     label: 'Billing Street',  value: account.BillingStreet },
          { key: 'BillingCity',       label: 'Billing City',    value: account.BillingCity },
          { key: 'BillingState',      label: 'Billing State',   value: account.BillingState },
          { key: 'BillingPostalCode', label: 'Billing Postal',  value: account.BillingPostalCode },
          { key: 'BillingCountry',    label: 'Billing Country', value: account.BillingCountry },
        ]}
        hideEmptyWhenIdle
        alwaysShow={['Name', 'Type']}
        readonly={[
          // The primary contact, surfaced HERE. "Account Info" that cannot tell
          // you how to reach the customer is not account info — the email was
          // only in the People panel further down the page, which is not where
          // anyone looks first for it.
          { label: 'Contact',        value: primaryContact ? [primaryContact.FirstName, primaryContact.LastName].filter(Boolean).join(' ') || null : null },
          { label: 'Email',          value: primaryContact?.Email || null },
          { label: 'Contact Phone',  value: primaryContact?.Phone || null },
          { label: 'Account Number', value: account.AccountNumber },
          { label: 'Created',        value: formatDate(account.CreatedDate) },
        ]}
      />

      {/* Notes */}
      <EditableCard
        title="Account Notes"
        accountId={accountId}
        onSaved={onSaved}
        textarea
        fields={[{ key: 'Description', label: 'Notes', value: account.Description, placeholder: 'Anything staff should know — contract quirks, escalation preferences, pet names for the dog they always mention, etc.' }]}
      />

      {/* Notes that belong on a work record, not the static Account blob */}
      <NoteToCase accountId={accountId} />
      </div>

      <div className="overview-col">
      <BillingStatusCard accountId={accountId} hasActiveOrders={(commerce.orderCount || 0) > 0} />

      {/* People on this account */}
      <PeopleSection accountId={accountId} contacts={contacts} onSaved={onSaved} />

      {/* Recent activity. The full order list lives on the Commerce tab now —
          the duplicate Recent Orders table was removed; the top Lifetime Spend
          card already summarises spend + order count. */}
      <div className="table-card">
          <div className="table-header">
            <span className="table-title">Recent Activity</span>
            <span className="table-count">{activity.length}</span>
          </div>
          {activity.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: 12 }}>
              No activity yet
            </div>
          ) : (
            <div>
              {activity.map(a => {
                // Cases open in the Tickets modal, orders open their receipt.
                // Tasks have no detail surface yet, so they stay plain rows.
                const open = a.kind === 'case' && onNavigateToTicket ? () => onNavigateToTicket(a.id)
                  : a.kind === 'order' ? () => setReceiptId(a.id)
                  : null;
                const clickable = !!open;
                return (
                  <div
                    key={`${a.kind}-${a.id}`}
                    onClick={open || undefined}
                    onKeyDown={open ? e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } } : undefined}
                    role={clickable ? 'button' : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    className={clickable ? 'activity-row is-clickable' : 'activity-row'}
                    style={{
                      padding: '10px 16px',
                      borderBottom: '1px solid rgba(42,42,56,0.5)',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10,
                      cursor: clickable ? 'pointer' : 'default',
                    }}
                    title={a.kind === 'order' ? 'Open the receipt' : clickable ? 'Open ticket' : undefined}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 12, color: clickable ? 'var(--accent)' : 'var(--text)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.title}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', marginTop: 2 }}>
                        <span style={{ textTransform: 'uppercase', marginRight: 6, color: 'var(--text2)' }}>{a.kind}</span>· {a.sub}
                      </div>
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)', flexShrink: 0 }}>{formatDate(a.date)}</div>
                  </div>
                );
              })}
            </div>
          )}
      </div>
      </div>
      </div>

      {receiptId && (
        <OrderReceipt
          accountId={accountId}
          accountName={account.Name}
          orderId={receiptId}
          onClose={() => setReceiptId(null)}
          onChanged={onSaved}
        />
      )}
    </div>
  );
}

// ─── Billing Status Card ─────────────────────────────────────────────────────
// Split helper — a shopper is "actionable" if they have no card on file OR
// any card is expired / expiring soon. Everything else is "healthy" and gets
// collapsed by default so BillingStatusCard doesn't grow linearly with
// shopper count. Reps mostly care about the actionable rows; full per-shopper
// detail is one click away.
function shopperNeedsAction(c) {
  const cards = c.paymentMethods || [];
  if (cards.length === 0) return true;
  return cards.some(pm => pm.expired || pm.expiringSoon);
}

// Per-shopper (Contact email) payment-method visibility. Surfaces cards on
// file, expiration warnings, and the critical "active products but no card"
// red alert. Stripe Customer is keyed by email (Option X architecture).
function BillingStatusCard({ accountId, hasActiveOrders }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showHealthy, setShowHealthy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      try {
        const body = await getAccountBillingStatus(accountId);
        if (!cancelled) setData(body);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [accountId]);

  if (loading) {
    return (
      <div className="table-card" style={{ padding: 16 }}>
        <div className="table-title" style={{ marginBottom: 8 }}>Billing</div>
        <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>Loading…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="table-card" style={{ padding: 16, borderColor: 'rgba(248,113,113,0.4)' }}>
        <div className="table-title" style={{ marginBottom: 8 }}>Billing</div>
        <div style={{ fontSize: 12, color: '#f87171' }}>⚠ {error}</div>
      </div>
    );
  }

  const agg = data?.aggregate || {};
  const perContact = (data?.perContact || []).filter(c => c.hasEmail);
  const shoppersWithCards = agg.contactsWithCards || 0;
  const shoppersTotal = agg.contactsWithEmail || 0;

  // Danger state: active orders but no card anywhere on the account
  const danger = hasActiveOrders && !agg.hasAnyCardOnFile;
  const warn = agg.expiringSoonCount > 0 || agg.expiredCount > 0;
  const ok = agg.hasAnyCardOnFile && !warn;

  const borderColor = danger ? 'rgba(248,113,113,0.5)' : warn ? 'rgba(251,191,36,0.4)' : ok ? 'rgba(74,222,128,0.35)' : 'var(--border)';
  const bg = danger ? 'rgba(248,113,113,0.04)' : warn ? 'rgba(251,191,36,0.04)' : ok ? 'rgba(74,222,128,0.03)' : 'transparent';

  return (
    <div className="table-card" style={{ padding: 16, borderColor, background: bg }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
        <div>
          <span className="table-title">Billing</span>
          <span style={{ marginLeft: 10, fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
            {shoppersWithCards} of {shoppersTotal} shopper{shoppersTotal === 1 ? '' : 's'} with card
          </span>
        </div>
        <HeadlineBadge danger={danger} warn={warn} ok={ok} agg={agg} />
      </div>

      {danger && (
        <div style={{ padding: 10, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 6, marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#f87171' }}>
            🚨 Active products without a card on file
          </div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4, lineHeight: 1.5 }}>
            Renewals will fail. Add a card via impersonation or have the customer complete a checkout.
          </div>
        </div>
      )}

      {perContact.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)', padding: '8px 0' }}>
          No contacts with email on this account yet.
        </div>
      ) : (() => {
        // Default view: only actionable rows. Healthy collapse behind a one-
        // line summary the rep can click to expand when triaging. Order is
        // stable (actionable first), so expanding doesn't reshuffle the rows
        // a rep was already looking at.
        const actionable = perContact.filter(shopperNeedsAction);
        const healthy = perContact.filter(c => !shopperNeedsAction(c));
        return (
          <div>
            {actionable.map(c => <BillingRow key={c.contactId} c={c} />)}
            {healthy.length > 0 && (
              <button
                type="button"
                onClick={() => setShowHealthy(v => !v)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  padding: '10px 0', background: 'none', border: 'none',
                  borderTop: actionable.length > 0 ? '1px solid var(--border)' : 'none',
                  color: 'var(--text2)', fontSize: 12, fontFamily: 'var(--mono)',
                  cursor: 'pointer', textAlign: 'left',
                }}
                title={showHealthy ? 'Collapse healthy shoppers' : 'Show all shoppers + cards'}
              >
                <span style={{ color: 'var(--text3)', width: 12 }}>{showHealthy ? '▾' : '▸'}</span>
                <span>
                  {healthy.length} shopper{healthy.length === 1 ? '' : 's'} with valid card{healthy.length === 1 ? '' : 's'} on file
                </span>
              </button>
            )}
            {showHealthy && healthy.map(c => <BillingRow key={c.contactId} c={c} />)}
          </div>
        );
      })()}
    </div>
  );
}

function HeadlineBadge({ danger, warn, ok, agg }) {
  if (danger) {
    return <span style={{ padding: '3px 10px', borderRadius: 4, background: 'rgba(248,113,113,0.15)', color: '#f87171', fontSize: 11, fontWeight: 700, fontFamily: 'var(--mono)' }}>NO CARD</span>;
  }
  if (agg.expiredCount > 0) {
    return <span style={{ padding: '3px 10px', borderRadius: 4, background: 'rgba(248,113,113,0.15)', color: '#f87171', fontSize: 11, fontWeight: 700, fontFamily: 'var(--mono)' }}>{agg.expiredCount} EXPIRED</span>;
  }
  if (agg.expiringSoonCount > 0) {
    return <span style={{ padding: '3px 10px', borderRadius: 4, background: 'rgba(251,191,36,0.15)', color: '#fbbf24', fontSize: 11, fontWeight: 700, fontFamily: 'var(--mono)' }}>{agg.expiringSoonCount} EXPIRING SOON</span>;
  }
  if (ok) {
    return <span style={{ padding: '3px 10px', borderRadius: 4, background: 'rgba(74,222,128,0.15)', color: '#4ade80', fontSize: 11, fontWeight: 700, fontFamily: 'var(--mono)' }}>✓ ON FILE</span>;
  }
  return null;
}

function BillingRow({ c }) {
  const name = [c.firstName, c.lastName].filter(Boolean).join(' ').trim() || c.email;
  const cards = c.paymentMethods || [];
  if (cards.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderTop: '1px solid var(--border)' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600 }}>{name}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{c.email}</div>
        </div>
        <span style={{ fontSize: 11, color: 'var(--text3)', fontStyle: 'italic' }}>no card on file</span>
      </div>
    );
  }
  return (
    <div style={{ padding: '10px 0', borderTop: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <div>
          <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600 }}>{name}</span>
          <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', marginLeft: 8 }}>{c.email}</span>
        </div>
        <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{cards.length} card{cards.length === 1 ? '' : 's'}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {cards.map(pm => (
          <div key={pm.id} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '4px 10px', borderRadius: 4,
            background: pm.expired ? 'rgba(248,113,113,0.08)' : pm.expiringSoon ? 'rgba(251,191,36,0.08)' : 'var(--bg)',
            border: pm.isDefault ? '1px solid rgba(99,102,241,0.4)' : '1px solid var(--border)',
          }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text2)', textTransform: 'capitalize', minWidth: 60 }}>{pm.brand}</span>
            {/* A Link payment method (shopper paid with Link) has no last4 and
                no expiry; the backend sends the Link account's email instead.
                It is chargeable for renewals like a card. */}
            {pm.type === 'link' ? (
              <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text)' }}>{pm.linkEmail || 'Link account'}</span>
            ) : (
              <>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text)' }}>•••• {pm.last4 || '????'}</span>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: pm.expired ? '#f87171' : pm.expiringSoon ? '#fbbf24' : 'var(--text3)' }}>
                  {pm.expMonth?.toString().padStart(2, '0')}/{String(pm.expYear ?? '').slice(-2)}
                  {pm.expired && ' · expired'}
                  {pm.expiringSoon && !pm.expired && ' · expiring soon'}
                </span>
              </>
            )}
            {pm.isDefault && (
              <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, color: 'var(--accent)', background: 'rgba(99,102,241,0.12)', padding: '1px 6px', borderRadius: 3, fontFamily: 'var(--mono)', letterSpacing: 0.5 }}>DEFAULT</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── People Section ───────────────────────────────────────────────────────────
// Unified People + Users view. Merges SF Contact records with the Firebase
// user registry so staff can see at a glance who has actually logged in
// (filled dot = registered user, hollow dot = contact-only). Inline "Add
// Contact" form kept from the original.
function PeopleSection({ accountId, contacts = [], onSaved }) {
  const [people, setPeople] = useState([]);
  // The 2FA-removal action is admin-only and the server enforces that (403).
  // This flag only decides whether to OFFER the button — same reasoning as
  // the OTP modal's override link: a rep shown a control that always fails
  // learns to distrust the page. Hidden by default while whoami is in flight.
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    let live = true;
    fetchWhoami().then(me => { if (live) setIsAdmin(me?.role === 'admin'); }).catch(() => {});
    return () => { live = false; };
  }, []);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ FirstName: '', LastName: '', Email: '', Phone: '', Title: '', Department: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function loadPeople() {
    if (!accountId) return;
    setLoading(true);
    try {
      const list = await listAccountPeople(accountId);
      setPeople(list);
    } catch (e) {
      // Fall back to SF-only view so the card never breaks the page.
      setPeople(contacts.map(c => ({
        email: c.Email, displayName: `${c.FirstName || ''} ${c.LastName || ''}`.trim() || c.Email,
        title: c.Title, phone: c.Phone, department: c.Department,
        sfContactId: c.Id, registered: false, loginCount: 0,
      })));
    }
    setLoading(false);
  }
  useEffect(() => { loadPeople(); /* eslint-disable-next-line */ }, [accountId]);

  function reset() {
    setForm({ FirstName: '', LastName: '', Email: '', Phone: '', Title: '', Department: '' });
    setAdding(false);
    setError('');
  }

  async function handleSave() {
    if (!form.LastName.trim()) { setError('Last name is required'); return; }
    setSaving(true);
    setError('');
    try {
      await createContact(accountId, form);
      reset();
      await loadPeople();
      onSaved?.();
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  }

  const registeredCount = people.filter(p => p.registered).length;

  return (
    <div className="table-card">
      <div className="table-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <span className="table-title">People</span>
          <span className="table-count" style={{ marginLeft: 8 }}>{people.length}</span>
          {registeredCount > 0 && (
            <span style={{ marginLeft: 10, fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
              {registeredCount} registered
            </span>
          )}
        </div>
        {!adding && (
          <button data-write onClick={() => setAdding(true)} style={{ padding: '6px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            + Add Contact
          </button>
        )}
      </div>

      {adding && (
        <div style={{ padding: 16, borderBottom: '1px solid var(--border)', background: 'var(--bg3)' }}>
          {error && <div style={{ fontSize: 12, color: '#f87171', marginBottom: 10 }}>⚠ {error}</div>}
          <div className="m-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
            <input value={form.FirstName} onChange={e => setForm({ ...form, FirstName: e.target.value })} placeholder="First name" style={peopleInput} />
            <input value={form.LastName}  onChange={e => setForm({ ...form, LastName: e.target.value })}  placeholder="Last name *" style={peopleInput} />
            <input value={form.Title}     onChange={e => setForm({ ...form, Title: e.target.value })}     placeholder="Title (e.g. Owner)" style={peopleInput} />
          </div>
          <div className="m-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
            <input value={form.Email}     onChange={e => setForm({ ...form, Email: e.target.value })}     placeholder="Email"    style={{ ...peopleInput, fontFamily: 'var(--mono)' }} />
            <input value={form.Phone}     onChange={e => setForm({ ...form, Phone: e.target.value })}     placeholder="Phone"    style={{ ...peopleInput, fontFamily: 'var(--mono)' }} />
            <input value={form.Department}onChange={e => setForm({ ...form, Department: e.target.value })}placeholder="Department" style={peopleInput} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={reset} disabled={saving} style={cancelBtn}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={saveBtn}>{saving ? 'Saving…' : 'Save Contact'}</button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 32 }}><Loading /></div>
      ) : people.length === 0 && !adding ? (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: 12 }}>
          No people on this account — click + Add Contact to create one
        </div>
      ) : people.length > 0 && (
        <table className="m-cards">
          <thead>
            <tr>
              <th style={{ width: 24 }}></th>
              <th>Name</th>
              <th>Email</th>
              <th>Title</th>
              <th>Phone</th>
              <th>Login activity</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {people.map(p => (
              <tr key={p.uid || p.sfContactId || p.email}>
                <td className="m-hide" title={p.registered ? 'Registered user' : 'Contact only — not registered'} style={{ textAlign: 'center' }}>
                  <span style={{
                    display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                    background: p.registered ? '#4ade80' : 'transparent',
                    border: p.registered ? '1px solid #4ade80' : '1px solid var(--text3)',
                  }}/>
                </td>
                <td className="m-card-title" style={{ color: 'var(--text)', fontWeight: 600 }}>{p.displayName || '—'}</td>
                <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
                  {p.email ? <a href={`mailto:${p.email}`} style={{ color: 'var(--accent)', textDecoration: 'none' }}>{p.email}</a> : '—'}
                </td>
                <td>{p.title || '—'}</td>
                <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
                  {p.phone ? <a href={`tel:${p.phone}`} style={{ color: 'var(--text2)', textDecoration: 'none' }}>{p.phone}</a> : '—'}
                </td>
                <td style={{ fontSize: 12, color: 'var(--text3)' }}>
                  {p.registered ? (
                    <span>Last login {relativeTime(p.lastSeenAt)} · {p.loginCount} {p.loginCount === 1 ? 'login' : 'logins'}</span>
                  ) : (
                    <span style={{ color: 'var(--text3)', fontStyle: 'italic' }}>Never logged in</span>
                  )}
                  {p.linkAmbiguous && (
                    <span title="Email matches multiple accounts — needs manual resolution" style={{ marginLeft: 6, color: '#fbbf24' }}>⚠</span>
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {p.sfContactId && (
                    <button data-write
                      // `people` comes from GET /api/accounts/:id/users (Contact
                      // LIMIT 200, and it selects no preference fields at all);
                      // `contacts` comes from GET /api/customer/:id (Contact
                      // LIMIT 10, which DOES select them). So this lookup misses
                      // for contact #11 onward and we hand the modal a stub with
                      // only Id + Email. That is deliberate and safe: a record
                      // with no preference keys seeds every toggle as "unknown"
                      // (see seedPreferenceForm) and Save writes only what the
                      // operator actually changed. Do NOT "helpfully" fill the
                      // preference fields in here with defaults — that is what
                      // used to silently revoke opt-outs.
                      onClick={() => CommsPrefsModal.open({
                        accountId,
                        contact: contacts.find(c => c.Id === p.sfContactId) || { Id: p.sfContactId, Email: p.email },
                        // Refresh the People table AND the account payload, so a
                        // contact whose prefs we DID load shows the saved values
                        // the next time the modal opens instead of stale ones.
                        onSaved: () => { loadPeople(); onSaved?.(); },
                      })}
                      title="Edit email opt-in preferences"
                      style={prefsBtn}
                    >
                      Prefs
                    </button>
                  )}
                  {p.registered && p.uid && (
                    <button data-write
                      onClick={() => ImpersonateModal.open({ accountId, user: p })}
                      title={`Sign into squish.co as ${p.displayName}`}
                      style={{ ...impersonateBtn, marginLeft: 6 }}
                    >
                      Impersonate
                    </button>
                  )}
                  {p.registered && p.uid && p.email && isAdmin && (
                    <button data-write
                      onClick={() => RemoveMfaModal.open({ accountId, user: p })}
                      title="Remove this user's two-factor (admin — audited, customer is notified)"
                      style={{ ...impersonateBtn, marginLeft: 6 }}
                    >
                      2FA
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <ImpersonateModal accountId={accountId} />
      <RemoveMfaModal accountId={accountId} />
      <CommsPrefsModal accountId={accountId} />
    </div>
  );
}

const impersonateBtn = {
  padding: '4px 10px', background: 'transparent', color: '#f87171',
  border: '1px solid #f87171', borderRadius: 4, fontSize: 11, fontWeight: 600,
  fontFamily: 'var(--mono)', cursor: 'pointer', letterSpacing: 0.5,
};

const prefsBtn = {
  padding: '4px 10px', background: 'transparent', color: 'var(--text2)',
  border: '1px solid var(--border)', borderRadius: 4, fontSize: 11, fontWeight: 600,
  fontFamily: 'var(--mono)', cursor: 'pointer', letterSpacing: 0.5,
};

// The three modals below are opened through module-level channels, and every
// open account tab keeps its CustomerDetail mounted (hidden) so switching is
// instant. A channel emit therefore reaches one modal per open account; each
// answers only for its own account, or a hidden tab would pop the same modal
// for the wrong customer the moment someone switched to it.
function forThisAccount(payload, accountId) {
  return !payload || payload.accountId === accountId;
}

// ─── ImpersonateModal ─────────────────────────────────────────────────────────
// Singleton modal — uses a module-level event channel so PeopleSection can
// pop it open without lifting state. Staff enters a reason; clicking Start
// opens the customer site in a new tab signed in as the target user.
const impersonateCh = {
  listeners: new Set(),
  emit(payload) { this.listeners.forEach(fn => fn(payload)); },
};
function ImpersonateModal({ accountId }) {
  const [state, setState] = useState(null); // { accountId, user } | null
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fn = (payload) => {
      if (!forThisAccount(payload, accountId)) return;
      setState(payload); setReason(''); setError('');
    };
    impersonateCh.listeners.add(fn);
    return () => impersonateCh.listeners.delete(fn);
  }, [accountId]);

  if (!state) return null;

  const trimmedReason = reason.trim();
  const reasonValid = trimmedReason.length >= 10;

  async function start() {
    if (!reasonValid) {
      setError('Reason is required (min 10 chars) — describe why you are entering this account.');
      return;
    }
    setBusy(true); setError('');
    try {
      const result = await startImpersonation(state.accountId, {
        targetUid: state.user.uid,
        reason: trimmedReason,
      });
      // Pop the customer site in a new tab. Staff stays in the CRM.
      window.open(result.redirectUrl, '_blank', 'noopener');
      setState(null);
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  }

  return (
    <div onClick={() => !busy && setState(null)} style={modalBackdrop}>
      <div onClick={e => e.stopPropagation()} style={modalCard}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
          Impersonate {state.user.displayName}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 14 }}>
          {state.user.email}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text2)', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.3)', padding: 10, borderRadius: 4, marginBottom: 14, lineHeight: 1.5 }}>
          You'll sign into squish.co as this user in a new tab. Every action is logged.
          Payment-method changes, account-email changes, and domain purchases are blocked
          during impersonation. Session auto-ends after 1 hour.
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: 'var(--mono)', display: 'block', marginBottom: 6 }}>
            Reason <span style={{ color: '#f87171' }}>*</span> (min 10 chars)
          </label>
          <input
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. Troubleshooting checkout failure on ticket #1234"
            autoFocus
            style={peopleInput}
          />
        </div>
        {error && <div style={{ fontSize: 12, color: '#f87171', marginBottom: 10 }}>⚠ {error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={() => setState(null)} disabled={busy} style={cancelBtn}>Cancel</button>
          <button onClick={start} disabled={busy || !reasonValid} style={{ ...saveBtn, background: '#dc2626', opacity: (busy || !reasonValid) ? 0.5 : 1 }}>
            {busy ? 'Opening…' : 'Start Impersonation'}
          </button>
        </div>
      </div>
    </div>
  );
}
ImpersonateModal.open = (payload) => impersonateCh.emit(payload);

// ─── RemoveMfaModal ───────────────────────────────────────────────────────────
// The staff side of the SecurityCard's recovery promise ("support can remove
// the factor after verifying it's you") — and therefore the social-engineering
// door, which is why the reason field asks HOW identity was verified, not
// whether. Admin-only (the button is gated; the server enforces), audited as
// a precondition server-side, and the customer gets the "wasn't you?" email
// the moment it succeeds. Same singleton-channel shape as ImpersonateModal.
const removeMfaCh = {
  listeners: new Set(),
  emit(payload) { this.listeners.forEach(fn => fn(payload)); },
};
function RemoveMfaModal({ accountId }) {
  const [state, setState] = useState(null); // { accountId, user } | null
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null); // { removed, notified } | null

  useEffect(() => {
    const fn = (payload) => {
      if (!forThisAccount(payload, accountId)) return;
      setState(payload); setReason(''); setError(''); setDone(null);
    };
    removeMfaCh.listeners.add(fn);
    return () => removeMfaCh.listeners.delete(fn);
  }, [accountId]);

  if (!state) return null;

  const trimmedReason = reason.trim();
  const reasonValid = trimmedReason.length >= 10;

  async function run() {
    if (!reasonValid) {
      setError("Reason is required (min 10 chars) — describe how you verified the customer's identity.");
      return;
    }
    setBusy(true); setError('');
    try {
      const result = await removeCustomerMfa(state.accountId, {
        email: state.user.email,
        reason: trimmedReason,
      });
      setDone({ removed: result.removed, notified: result.notified });
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  }

  return (
    <div onClick={() => !busy && setState(null)} style={modalBackdrop}>
      <div onClick={e => e.stopPropagation()} style={modalCard}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
          Remove two-factor — {state.user.displayName || state.user.email}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 14 }}>
          {state.user.email}
        </div>
        {done ? (
          <>
            <div style={{ fontSize: 13, color: 'var(--text)', background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.3)', padding: 10, borderRadius: 4, marginBottom: 14, lineHeight: 1.5 }}>
              Removed {done.removed} factor{done.removed === 1 ? '' : 's'}. Sessions were signed out.
              {done.notified
                ? ' The customer has been emailed a security notice.'
                : ' ⚠ The security-notice email FAILED — tell the customer yourself, now.'}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setState(null)} style={saveBtn}>Done</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 12, color: 'var(--text2)', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.3)', padding: 10, borderRadius: 4, marginBottom: 14, lineHeight: 1.5 }}>
              "I lost my authenticator" is how accounts get stolen — verify identity FIRST
              (card last4, billing address, a reply from the account email). This removes
              every second factor, signs out their sessions, emails them a "wasn't you?"
              notice, and lands an audited line on this account's Events timeline.
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: 'var(--mono)', display: 'block', marginBottom: 6 }}>
                How was identity verified? <span style={{ color: '#f87171' }}>*</span> (min 10 chars)
              </label>
              <input
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="e.g. Verified card last4 + billing ZIP on case #4512"
                autoFocus
                style={peopleInput}
              />
            </div>
            {error && <div style={{ fontSize: 12, color: '#f87171', marginBottom: 10 }}>⚠ {error}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setState(null)} disabled={busy} style={cancelBtn}>Cancel</button>
              <button onClick={run} disabled={busy || !reasonValid} style={{ ...saveBtn, background: '#dc2626', opacity: (busy || !reasonValid) ? 0.5 : 1 }}>
                {busy ? 'Removing…' : 'Remove two-factor'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
RemoveMfaModal.open = (payload) => removeMfaCh.emit(payload);

// ─── CommsPrefsModal ──────────────────────────────────────────────────────────
// Edits the comms-preferences fields on a SF Contact. Staff-only — gated
// server-side by ACCOUNT_WRITE_ROLES. Customers edit their own prefs from
// the Dashboard on squish.co (mirror endpoint: PATCH /api/my-preferences).
//
// COMPLIANCE: this modal is the staff side of the comms-preferences gate, so
// it must never write a preference it did not actually read. There are two
// different Contact reads behind the People table:
//   GET /api/accounts/:id/users   Contact LIMIT 200, selects NO pref fields
//   GET /api/customer/:id         Contact LIMIT 10,  selects all three
// The table renders the first list; the modal is seeded from the second. From
// contact #11 onward the seed lookup misses entirely, so the contact record
// reaching this modal has no preference keys on it at all. Treating "absent"
// as the documented default and then PATCHing all three fields on Save turned
// a Save-without-edits into a silent revocation of a real opt-out. The helpers
// below keep absence explicit (null = we never loaded it) and send only the
// fields the operator actually changed.
//
// (Exported so the payload decision is testable without rendering React —
// see test/comms-prefs-write.test.js.)
export const PREFERENCE_FIELDS = [
  'Email_Marketing_Opt_In__c',
  'Email_Renewal_Reminders__c',
  'Email_Service_Incidents__c',
];

// What the comms gate assumes when a Contact's checkbox is null in Salesforce.
// Marketing is explicit-opt-in (CAN-SPAM / GDPR); the other two are opt-out
// shaped. These are display defaults for a contact we DID load — they are
// never used to fill in a contact we did not load.
const PREFERENCE_DEFAULTS = {
  Email_Marketing_Opt_In__c:  false,
  Email_Renewal_Reminders__c: true,
  Email_Service_Incidents__c: true,
};

// Short names for the "will write" summary line at the bottom of the modal.
const PREFERENCE_LABELS = {
  Email_Marketing_Opt_In__c:  'Marketing',
  Email_Renewal_Reminders__c: 'Renewal reminders',
  Email_Service_Incidents__c: 'Service incidents',
};

// Seed toggle state from a Contact record. A record fetched by
// GET /api/customer/:id always carries all three keys (the SOQL select lists
// them), so a MISSING key means "these preferences were never loaded" and
// seeds as null rather than as a default. null renders indeterminate and is
// never written back unless the operator sets it themselves.
export function seedPreferenceForm(contact) {
  const form = {};
  for (const field of PREFERENCE_FIELDS) {
    if (contact && Object.prototype.hasOwnProperty.call(contact, field)) {
      const raw = contact[field];
      // A key that is present but null is a real Salesforce read of an unset
      // checkbox; the gate treats that as the field's default, so show that.
      form[field] = raw == null ? PREFERENCE_DEFAULTS[field] : raw === true;
    } else {
      form[field] = null;   // never loaded — unknown, not "opted in"
    }
  }
  return form;
}

// The PATCH body: only fields whose value the operator actually moved off what
// the modal displayed when it opened. A field still sitting at null (unknown)
// is skipped, so Save-without-edits sends nothing and cannot revoke anything.
export function changedPreferenceFields(initial, form) {
  const changed = {};
  for (const field of PREFERENCE_FIELDS) {
    const next = form?.[field];
    if (typeof next !== 'boolean') continue;   // still unknown — nothing to write
    if (next === initial?.[field]) continue;   // untouched
    changed[field] = next;
  }
  return changed;
}

// The write path. Returns { changed, skipped } so the caller — and the test —
// can see exactly what reached Salesforce. `patch` is injectable purely so the
// payload decision can be exercised without a network; the default is the real
// API call, which is what the modal below uses.
export async function savePreferenceChanges({ contactId, initial, form }, patch = updateContactPreferences) {
  const changed = changedPreferenceFields(initial, form);
  if (Object.keys(changed).length === 0) return { changed, skipped: true };
  await patch(contactId, changed);
  return { changed, skipped: false };
}

const prefsCh = {
  listeners: new Set(),
  emit(payload) { this.listeners.forEach(fn => fn(payload)); },
};
function CommsPrefsModal({ accountId }) {
  const [state, setState] = useState(null); // { contact, onSaved } | null
  const [form, setForm] = useState(null);
  // Snapshot of the seeded state, kept for the lifetime of the open modal so
  // save() can diff against what the operator was actually shown.
  const [initial, setInitial] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fn = (payload) => {
      if (!forThisAccount(payload, accountId)) return;
      setState(payload);
      setError('');
      const seeded = payload ? seedPreferenceForm(payload.contact) : null;
      setInitial(seeded);
      setForm(seeded);
    };
    prefsCh.listeners.add(fn);
    return () => prefsCh.listeners.delete(fn);
  }, [accountId]);

  if (!state || !form) return null;

  const pending = changedPreferenceFields(initial, form);
  const pendingFields = Object.keys(pending);

  async function save() {
    setBusy(true); setError('');
    try {
      await savePreferenceChanges({ contactId: state.contact.Id, initial, form });
      state.onSaved?.();
      setState(null);
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  }

  function Toggle({ field, label, hint, dangerous }) {
    const checked = form[field];
    const unknown = checked === null;
    return (
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0', borderTop: '1px solid var(--border)', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={checked === true}
          // `checked` is a two-state prop and can only lie about a preference
          // we never loaded, so drive the native indeterminate flag directly.
          ref={el => { if (el) el.indeterminate = unknown; }}
          onChange={e => setForm({ ...form, [field]: e.target.checked })}
          style={{ marginTop: 3, accentColor: dangerous ? '#dc2626' : 'var(--accent)' }}
        />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{label}</div>
          {unknown && (
            <div style={{ fontSize: 11, color: '#fbbf24', marginTop: 2, lineHeight: 1.5 }}>
              Not loaded for this contact — leave it alone and nothing is written. Ticking or unticking sets it explicitly.
            </div>
          )}
          {hint && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2, lineHeight: 1.5 }}>{hint}</div>}
        </div>
      </label>
    );
  }

  return (
    <div onClick={() => !busy && setState(null)} style={modalBackdrop}>
      <div onClick={e => e.stopPropagation()} style={modalCard}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
          Comms preferences
        </div>
        <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 14 }}>
          {state.contact.Email || state.contact.Id}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, lineHeight: 1.5 }}>
          Transactional emails (receipts, suspension notices, hosting credentials) are always sent regardless of these toggles.
        </div>
        <Toggle
          field="Email_Marketing_Opt_In__c"
          label="Marketing & product announcements"
          hint="Off by default — must be explicitly enabled per CAN-SPAM / GDPR."
        />
        <Toggle
          field="Email_Renewal_Reminders__c"
          label="Renewal reminders & dunning"
          hint="On by default. Includes pre-expiration reminders, payment-failed notices, and dunning. Strongly recommended ON."
        />
        <Toggle
          field="Email_Service_Incidents__c"
          label="Service incidents & maintenance"
          hint="On by default. Outage and maintenance-window notices. Recommended ON."
        />
        {/* Exactly what Save will send. An accidental tick on a preference we
            never loaded is otherwise invisible, so make the write list explicit
            and give the operator one click back to "write nothing". */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 12, fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text3)', lineHeight: 1.5 }}>
          <span style={{ flex: 1 }}>
            {pendingFields.length === 0
              ? 'No changes — Save writes nothing.'
              : `Will write: ${pendingFields.map(f => `${PREFERENCE_LABELS[f]} → ${pending[f] ? 'on' : 'off'}`).join(', ')}`}
          </span>
          {pendingFields.length > 0 && (
            <button onClick={() => setForm(initial)} disabled={busy} style={cancelBtn}>Reset</button>
          )}
        </div>
        {error && <div style={{ fontSize: 12, color: '#f87171', marginTop: 12 }}>⚠ {error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={() => setState(null)} disabled={busy} style={cancelBtn}>Cancel</button>
          <button onClick={save} disabled={busy} style={{ ...saveBtn, opacity: busy ? 0.5 : 1 }}>
            {busy ? 'Saving…' : 'Save preferences'}
          </button>
        </div>
      </div>
    </div>
  );
}
CommsPrefsModal.open = (payload) => prefsCh.emit(payload);

const modalBackdrop = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
};
const modalCard = {
  background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8,
  padding: 20, maxWidth: 500, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
};

function relativeTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 0) return 'just now';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

const peopleInput = { padding: '7px 10px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font)', outline: 'none', boxSizing: 'border-box', width: '100%' };

// ─── EditableCard ─────────────────────────────────────────────────────────────
// Two-mode card: read-only display (default), or edit mode with input fields.
// PATCHes /api/accounts/:id/profile on save. All fields in one card save atomically.
// `hideEmptyWhenIdle` collapses fields we have no value for while the card is
// read-only, and shows every one again the moment you hit Edit.
//
// Salesforce's standard Account carries fields we have never populated for
// anybody — Fax, Employees, Annual Revenue — so a self-serve customer's card
// rendered as a wall of em-dashes with the two real values lost inside it. The
// fields stay editable, because a rep filling in an industry by hand is a
// legitimate thing to do; they just stop shouting their own absence.
function EditableCard({ title, fields, readonly = [], accountId, onSaved, textarea = false, columns = 1, hideEmptyWhenIdle = false, alwaysShow = [] }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => Object.fromEntries(fields.map(f => [f.key, f.value ?? ''])));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function startEdit() {
    setDraft(Object.fromEntries(fields.map(f => [f.key, f.value ?? ''])));
    setError('');
    setEditing(true);
  }

  // What the card renders when it is NOT being edited.
  const isEmpty = (f) => f.value === null || f.value === undefined || String(f.value).trim() === '';
  const visibleFields = (!editing && hideEmptyWhenIdle)
    ? fields.filter(f => alwaysShow.includes(f.key) || !isEmpty(f))
    : fields;
  const hiddenCount = fields.length - visibleFields.length;

  async function save() {
    setSaving(true);
    setError('');
    try {
      await updateAccountProfile(accountId, draft);
      setEditing(false);
      onSaved?.();
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  }

  return (
    <div className="table-card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div className="table-title">{title}</div>
        {!editing ? (
          <button data-write onClick={startEdit} style={editBtn}>Edit</button>
        ) : (
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setEditing(false)} disabled={saving} style={cancelBtn}>Cancel</button>
            <button onClick={save} disabled={saving} style={saveBtn}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        )}
      </div>
      {error && <div style={{ fontSize: 12, color: '#f87171', marginBottom: 10 }}>⚠ {error}</div>}

      {/* columns > 1 packs field rows into a multi-column grid — this is what
          lets Account Info (15 rows stacked) fit in half the height. */}
      <div className="m-stack" style={columns > 1 ? { display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, columnGap: 28 } : undefined}>
      {visibleFields.map(f => (
        <div key={f.key} style={{ display: 'flex', flexDirection: textarea || editing ? 'column' : 'row', justifyContent: 'space-between', alignItems: textarea || editing ? 'stretch' : 'center', gap: 6, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{f.label}</span>
          {!editing ? (
            <span style={{ fontSize: 13, color: 'var(--text)', whiteSpace: textarea ? 'pre-wrap' : 'nowrap', textAlign: textarea ? 'left' : 'right', maxWidth: textarea ? '100%' : 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {f.displayFmt ? f.displayFmt(f.value) : (f.value || '—')}
            </span>
          ) : textarea ? (
            <textarea
              value={draft[f.key] || ''}
              onChange={e => setDraft({ ...draft, [f.key]: e.target.value })}
              placeholder={f.placeholder}
              rows={4}
              style={inputStyle}
            />
          ) : (
            <input
              type={f.type || 'text'}
              value={draft[f.key] || ''}
              onChange={e => setDraft({ ...draft, [f.key]: e.target.value })}
              placeholder={f.placeholder || ''}
              style={{ ...inputStyle, maxWidth: columns > 1 ? '100%' : 260 }}
            />
          )}
        </div>
      ))}
      {readonly.map(r => (
        <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{r.label}</span>
          <span style={{ fontSize: 13, color: 'var(--text3)', fontStyle: 'italic' }}>{r.value || '—'}</span>
        </div>
      ))}
      </div>
      {/* Say that the hidden fields EXIST. Silently omitting them would leave a
          rep unable to find where to type an industry. */}
      {hiddenCount > 0 && (
        <button data-write
          onClick={startEdit}
          style={{ marginTop: 8, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text3)' }}
          title="These fields are empty. Editing shows all of them."
        >
          + {hiddenCount} empty field{hiddenCount === 1 ? '' : 's'} — edit to fill
        </button>
      )}
    </div>
  );
}

// ─── NoteToCase ───────────────────────────────────────────────────────────────
// Account notes live in Account.Description (static blob). This composer lets a
// rep log an ad-hoc note straight onto a case timeline instead — so the note
// attaches to *work*, is mineable later, and shows up in the case history.
// Phase 1c (optional half). Reuses CasePicker + addCaseComment.
function NoteToCase({ accountId }) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState('');
  const [caseId, setCaseId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  async function post() {
    setError('');
    if (!body.trim()) { setError('Write a note first.'); return; }
    if (!caseId)      { setError('Pick or create a case to attach the note to.'); return; }
    setBusy(true);
    try {
      await addCaseComment(caseId, body.trim());
      setBody(''); setCaseId(null); setDone(true);
      setTimeout(() => setDone(false), 2500);
    } catch (e) { setError(e.message || 'Failed to post note'); }
    setBusy(false);
  }

  // Collapsed to a single header row by default — the composer is an
  // occasional-use tool and was costing ~220px of Overview height.
  return (
    <div className="table-card" style={{ padding: '12px 16px' }}>
      <div
        onClick={() => setOpen(v => !v)}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
        title={open ? 'Collapse' : 'Log a note onto a case timeline'}
      >
        <div className="table-title">
          <span style={{ color: 'var(--text3)', marginRight: 8 }}>{open ? '▾' : '▸'}</span>
          Log a note to a case
        </div>
        {done && <span style={{ fontSize: 11, color: '#4ade80', fontFamily: 'var(--mono)' }}>✓ posted</span>}
      </div>
      {open && (
        <div style={{ marginTop: 12 }}>
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="A note that belongs on the work record — call recap, follow-up, decision, etc."
            rows={3}
            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: 12 }}
          />
          <CasePicker accountId={accountId} value={caseId} onChange={setCaseId} />
          {error && <div style={{ fontSize: 12, color: '#f87171', marginBottom: 10 }}>⚠ {error}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button data-write onClick={post} disabled={busy} style={{ ...saveBtn, opacity: busy ? 0.6 : 1, cursor: busy ? 'not-allowed' : 'pointer' }}>
              {busy ? 'Posting…' : 'Post to case'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const editBtn     = { padding: '4px 12px', background: 'transparent', color: 'var(--text2)', border: '1px solid var(--border)', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--mono)' };
const saveBtn     = { padding: '4px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer' };
const cancelBtn   = { padding: '4px 12px', background: 'transparent', color: 'var(--text3)', border: '1px solid var(--border)', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' };
const inputStyle  = { padding: '6px 8px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font)', outline: 'none', resize: 'vertical' };

// ─── Transactions Tab ─────────────────────────────────────────────────────────
// Lists every Stripe charge across the account's contacts. Each charge gets
// an inline refund affordance that opens a modal (RefundModal below). All
// refunds go through the CRM — direct Stripe dashboard access is restricted.

const REFUND_REASONS = [
  { value: 'customer_request',    label: 'Customer request' },
  { value: 'duplicate',           label: 'Duplicate charge' },
  { value: 'fraudulent',          label: 'Fraudulent' },
  { value: 'service_failure',     label: 'Service failure (our fault)' },
  { value: 'provisioning_failure',label: 'Provisioning failure (upstream)' },
  { value: 'goodwill',            label: 'Goodwill' },
  { value: 'other',               label: 'Other (add note)' },
];

// Refunds strictly above $500 require a second staff member's email on file
// — blocks a single compromised session from draining funds. Backend enforces
// the same threshold + "different from requester" rule.
const SECOND_APPROVER_THRESHOLD_DOLLARS = 500;

// Attach-to-a-case picker for rep actions (Phase 1 accountability). Lists the
// account's open cases + lets you spin up a work-anchor case inline. The chosen
// caseId is reported up so the action can post a summary comment to it.
function CasePicker({ accountId, value, onChange }) {
  const [cases, setCases] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newType, setNewType] = useState('Support');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let live = true;
    listAccountCases(accountId).then(c => { if (live) setCases(c); }).catch(() => {});
    return () => { live = false; };
  }, [accountId]);

  async function create() {
    if (!newSubject.trim()) { setErr('Give the case a subject.'); return; }
    setBusy(true); setErr('');
    try {
      // A work anchor is staff work — silent, never shown or emailed to the customer.
      const { caseId, caseNumber } = await createAccountCase(accountId, { subject: newSubject.trim(), workType: newType, visibility: 'staff' });
      setCases(cs => [{ Id: caseId, Subject: `[${newType}] ${newSubject.trim()}`, CaseNumber: caseNumber || 'new' }, ...cs]);
      onChange(caseId);
      setCreating(false); setNewSubject('');
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  const lbl = { display: 'block', fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: 'var(--mono)', marginBottom: 6 };
  const inp = { width: '100%', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit' };

  return (
    <div style={{ marginBottom: 14, padding: '10px 12px', background: 'rgba(124,109,250,0.06)', border: '1px solid rgba(124,109,250,0.2)', borderRadius: 6 }}>
      <label style={lbl}>Attach to a case (accountability)</label>
      {!creating ? (
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={value || ''} onChange={e => onChange(e.target.value || null)} style={{ ...inp, flex: 1 }}>
            <option value="">— No case —</option>
            {cases.map(c => <option key={c.Id} value={c.Id}>{c.CaseNumber !== 'new' ? `#${c.CaseNumber} · ` : ''}{c.Subject || 'Case'}</option>)}
          </select>
          <button data-write onClick={() => setCreating(true)} style={{ ...inp, width: 'auto', cursor: 'pointer', whiteSpace: 'nowrap' }}>➕ New</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input value={newSubject} onChange={e => setNewSubject(e.target.value)} placeholder="What's this work about?" style={inp} />
          <div style={{ display: 'flex', gap: 8 }}>
            <select value={newType} onChange={e => setNewType(e.target.value)} style={{ ...inp, flex: 1 }}>
              {['Support', 'Deal', 'Research', 'Billing', 'Onboarding'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <button onClick={create} disabled={busy} style={{ ...inp, width: 'auto', cursor: 'pointer', background: 'var(--accent)', color: '#fff', border: 'none' }}>{busy ? '…' : 'Create'}</button>
            <button onClick={() => { setCreating(false); setErr(''); }} disabled={busy} style={{ ...inp, width: 'auto', cursor: 'pointer' }}>✕</button>
          </div>
        </div>
      )}
      {err && <div style={{ fontSize: 11, color: '#fca5a5', marginTop: 6 }}>⚠ {err}</div>}
    </div>
  );
}

// ─── NewTicketForm ────────────────────────────────────────────────────────────
// Open a ticket on the customer's behalf — on a call, say. Until this, the CRM
// could not open a ticket the customer could see: its only create was the
// CasePicker work anchor, which linked no contact, so the case never reached
// their dashboard (#00001057). Customer-visible is the default: the ticket is
// linked to their contact and they are emailed the ticket number. Staff only is
// a silent ticket they never see. The one text box is a message TO the
// customer on a visible ticket and a staff note on a silent one
// (lib/caseVisibility newTicketBody).
function NewTicketForm({ accountId, contacts, onCreated, onCancel }) {
  const [subject, setSubject]       = useState('');
  const [visibility, setVisibility] = useState('customer');
  const [contactId, setContactId]   = useState(() => defaultContactId(contacts));
  const [priority, setPriority]     = useState('Medium');
  const [message, setMessage]       = useState('');
  const [busy, setBusy]             = useState(false);
  const [err, setErr]               = useState('');
  const visible = visibility === 'customer';
  const pickable = (contacts || []).filter(c => c.Id);

  async function submit() {
    setErr('');
    if (visible && pickable.length > 1 && !contactId) { setErr('Pick the contact this ticket is for.'); return; }
    let body;
    try { body = newTicketBody({ subject, priority, visibility, contactId, message }); }
    catch (e) { setErr(e.message); return; }
    setBusy(true);
    try {
      const r = await createStaffTicket(accountId, body);
      onCreated(r);
    } catch (e) {
      setErr(e.code === 'CONTACT_REQUIRED' ? 'This account has several contacts. Pick one.' : e.message);
    }
    setBusy(false);
  }

  const lbl = { display: 'block', fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: 'var(--mono)', marginBottom: 6 };
  const inp = { width: '100%', boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit' };
  const seg = (key, label) => (
    <button
      type="button"
      onClick={() => setVisibility(key)}
      style={{
        flex: 1, padding: '7px 12px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 5, cursor: 'pointer', fontFamily: 'var(--mono)',
        background: visibility === key ? 'var(--bg3)' : 'transparent',
        color: visibility === key ? (key === 'customer' ? 'var(--accent)' : 'var(--text)') : 'var(--text3)',
      }}
    >
      {label}
    </button>
  );

  return (
    <div className="table-card" style={{ padding: '14px 16px', marginBottom: 14, border: `1px solid ${visible ? 'var(--accent)' : 'var(--border)'}` }}>
      <div className="m-stack" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: 12 }}>
        <div>
          <label style={lbl}>Subject</label>
          <input autoFocus value={subject} onChange={e => setSubject(e.target.value)} placeholder={visible ? 'What the customer will see, e.g. DKIM not enabled for example.com' : "What's this work about?"} style={inp} />
        </div>
        <div>
          <label style={lbl}>Who sees it</label>
          <div style={{ display: 'flex', gap: 2, padding: 2, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 7 }}>
            {seg('customer', '🌐 Customer')}
            {seg('staff', '🔒 Staff only')}
          </div>
        </div>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text3)', margin: '8px 0 12px', lineHeight: 1.5 }}>
        {visible
          ? 'Shows on their dashboard, and they are emailed the ticket number.'
          : 'Silent: never shown to the customer, never emailed. You can tell them later from the ticket.'}
      </div>
      <div className="m-stack" style={{ display: 'grid', gridTemplateColumns: visible ? 'minmax(0, 2fr) minmax(0, 1fr)' : 'minmax(0, 1fr)', gap: 12, marginBottom: 12 }}>
        {visible && (
          <div>
            <label style={lbl}>Contact</label>
            {pickable.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--badge-yellow)', padding: '8px 0' }}>⚠ No contact on this account, so the customer can't be shown a ticket. Add a contact, or open it Staff only.</div>
            ) : (
              <select value={contactId} onChange={e => setContactId(e.target.value)} style={inp} disabled={pickable.length === 1}>
                {pickable.length > 1 && <option value="">Which contact?</option>}
                {pickable.map(c => <option key={c.Id} value={c.Id}>{contactLabel(c)}</option>)}
              </select>
            )}
          </div>
        )}
        <div>
          <label style={lbl}>Priority</label>
          <select value={priority} onChange={e => setPriority(e.target.value)} style={inp}>
            {['Low', 'Medium', 'High'].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>
      <label style={lbl}>{visible ? 'Message to the customer (optional)' : 'Internal note (optional)'}</label>
      <textarea
        value={message}
        onChange={e => setMessage(e.target.value)}
        rows={3}
        placeholder={visible ? 'Goes on the ticket and in the email. Write it to them.' : 'Staff only: findings, next steps.'}
        style={{ ...inp, resize: 'vertical', marginBottom: 12 }}
      />
      {err && <div style={{ fontSize: 12, color: '#f87171', marginBottom: 10 }}>⚠ {err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button type="button" onClick={onCancel} disabled={busy} style={cancelBtn}>Cancel</button>
        <button type="button" onClick={submit} disabled={busy || (visible && pickable.length === 0)} style={{ ...saveBtn, opacity: busy ? 0.6 : 1, cursor: busy ? 'wait' : 'pointer' }}>
          {busy ? 'Opening…' : visible ? 'Open & email ticket #' : 'Open silent ticket'}
        </button>
      </div>
    </div>
  );
}

// `lineOptions` (src/lib/refundLines.js refundLineOptions) are the payment's
// booked lines. With any, the modal offers "Against these lines": the rep
// ticks the line(s) and the amount is the sum of what they put on each — the
// request then carries `lines`, and squish-backend books the refund per line
// (margin_ledger, Order.Refunded_Amount__c) instead of spreading it across the
// payment. "Whole payment" is the request it always was.
function RefundModal({ charge, accountId, lineOptions = [], onClose, onRefunded }) {
  const [amountDollars, setAmountDollars] = useState(((charge.amountCents - charge.refundedCents) / 100).toFixed(2));
  const [reason, setReason] = useState('customer_request');
  const [note, setNote] = useState('');
  const [approverEmail, setApproverEmail] = useState('');
  const [caseId, setCaseId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // 'whole' = the amount box against the payment; 'lines' = per-line picks.
  const [mode, setMode] = useState('whole');
  // { [lineId]: dollars string } for the lines ticked.
  const [picked, setPicked] = useState({});

  const maxRefundableCents = charge.amountCents - charge.refundedCents;
  const refundableOptions = lineOptions.filter(o => o.availableCents > 0);
  const byLines = mode === 'lines' && refundableOptions.length > 0;
  const pickCents = Object.fromEntries(Object.entries(picked).map(([id, d]) => [id, Math.round((parseFloat(d) || 0) * 100)]));
  const parsedAmount = byLines ? refundLinesTotalCents(pickCents) / 100 : (parseFloat(amountDollars) || 0);
  const needsApprover = parsedAmount > SECOND_APPROVER_THRESHOLD_DOLLARS;

  function toggleLine(o, on) {
    setPicked(prev => {
      const next = { ...prev };
      if (on) next[o.lineId] = (o.availableCents / 100).toFixed(2);
      else delete next[o.lineId];
      return next;
    });
  }

  async function submit() {
    setError('');
    const cents = Math.round(parsedAmount * 100);
    if (!cents || cents <= 0) { setError(byLines ? 'Pick at least one line, or refund the whole payment.' : 'Amount must be greater than 0'); return; }
    if (cents > maxRefundableCents) { setError(`Max refundable is $${(maxRefundableCents / 100).toFixed(2)}`); return; }
    if (byLines) {
      const problem = refundLinesProblem(pickCents, refundableOptions, cents);
      if (problem) { setError(problem); return; }
    }
    if (reason === 'other' && !note.trim()) { setError('Note required when reason is Other'); return; }
    if (needsApprover && !approverEmail.trim()) {
      setError(`Refunds over $${SECOND_APPROVER_THRESHOLD_DOLLARS} require a second approver — add a staff email.`);
      return;
    }

    setBusy(true);
    try {
      const result = await issueRefund(buildRefundRequest({
        charge, cents, reason, note,
        approverEmail: needsApprover ? approverEmail : null,
        picks: byLines ? pickCents : null,
      }));
      // Anchor the action to the chosen case (best-effort).
      if (caseId) {
        try {
          await addCaseComment(caseId, `Refunded $${(cents / 100).toFixed(2)} (${reason}) on ${charge.paymentIntentId}${note.trim() ? ` — ${note.trim()}` : ''}`);
        } catch (_) { /* refund already succeeded; comment is best-effort */ }
      }
      onRefunded(result);
    } catch (e) {
      setError(e.message || 'Refund failed');
    }
    setBusy(false);
  }

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 20, backdropFilter: 'blur(4px)',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12,
        width: '92%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', padding: 22,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Issue refund</div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text2)', fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 14, lineHeight: 1.5 }}>
          Charge <code style={{ fontFamily: 'monospace' }}>{charge.paymentIntentId}</code><br />
          Customer: <strong>{charge.customerEmail}</strong><br />
          Original: ${(charge.amountCents / 100).toFixed(2)}
          {charge.refundedCents > 0 && <> · Already refunded: ${(charge.refundedCents / 100).toFixed(2)}</>}
        </div>

        {accountId && <CasePicker accountId={accountId} value={caseId} onChange={setCaseId} />}

        {isDomainCharge(charge) && (
          <div style={{
            padding: '10px 12px', marginBottom: 14,
            background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.3)',
            borderRadius: 6, color: '#fbbf24', fontSize: 12, lineHeight: 1.5,
          }}>
            ⚠ <strong>Domain refund — it still has to be cancelled at NameSilo.</strong> A Stripe refund here returns the customer's money but does NOT cancel the domain at NameSilo (there is no API for it). Each domain on this payment is added to the <strong>NameSilo cancellations</strong> list on the Bleed page with its deadline: cancel it at namesilo.com → Order History within the 3-day window, then tick it there. Skipped, the customer gets their refund AND keeps the domain, at our expense.
          </div>
        )}

        {refundableOptions.length > 0 && (
          <div style={{ display: 'flex', gap: 14, marginBottom: 10, fontSize: 12, color: 'var(--text)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="radio" name="refund-mode" checked={!byLines} onChange={() => setMode('whole')} /> Whole payment
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }} title="The refund is booked against the line(s) you pick — the Order and the receipt show which product the money came off.">
              <input type="radio" name="refund-mode" checked={byLines} onChange={() => setMode('lines')} /> Against these lines
            </label>
          </div>
        )}

        {byLines ? (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Lines</div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg)' }}>
              {refundableOptions.map(o => {
                const on = o.lineId in picked;
                return (
                  <label key={o.lineId} style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr) 96px', gap: 10, alignItems: 'center', padding: '7px 10px', borderBottom: '1px solid var(--border)', cursor: 'pointer', fontSize: 12 }}>
                    <input type="checkbox" checked={on} onChange={e => toggleLine(o, e.target.checked)} />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ color: 'var(--text)', fontWeight: 600 }}>{o.name}</span>
                      {o.detail && <span style={{ color: 'var(--text2)' }}> · {o.detail}</span>}
                      {o.orderNumber && <span style={{ color: 'var(--text3)' }}> · #{o.orderNumber}</span>}
                      <div style={{ fontSize: 11, color: 'var(--text2)' }}>
                        Paid ${(o.inflowCents / 100).toFixed(2)}{o.refundedCents > 0 && <> · already refunded ${(o.refundedCents / 100).toFixed(2)}</>} · up to ${(o.availableCents / 100).toFixed(2)}
                      </div>
                    </span>
                    <input
                      type="number" step="0.01" min="0.01" max={(o.availableCents / 100).toFixed(2)}
                      value={on ? picked[o.lineId] : ''} disabled={!on}
                      onClick={e => e.preventDefault()}
                      onChange={e => setPicked(prev => ({ ...prev, [o.lineId]: e.target.value }))}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, boxSizing: 'border-box', opacity: on ? 1 : 0.4 }}
                    />
                  </label>
                );
              })}
              {lineOptions.length > refundableOptions.length && (
                <div style={{ padding: '6px 10px', fontSize: 11, color: 'var(--text3)' }}>
                  {lineOptions.length - refundableOptions.length} line{lineOptions.length - refundableOptions.length === 1 ? ' is' : 's are'} already refunded in full.
                </div>
              )}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4 }}>
              Total: ${parsedAmount.toFixed(2)} · Max: ${(maxRefundableCents / 100).toFixed(2)}
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Amount ($)</div>
            <input
              type="number"
              step="0.01"
              min="0.01"
              max={(maxRefundableCents / 100).toFixed(2)}
              value={amountDollars}
              onChange={e => setAmountDollars(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 14, boxSizing: 'border-box' }}
            />
            <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4 }}>
              Max: ${(maxRefundableCents / 100).toFixed(2)}{refundableOptions.length > 1 && <> · split across the payment's {refundableOptions.length} lines in proportion</>}
            </div>
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Reason</div>
          <select
            value={reason}
            onChange={e => setReason(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 14, boxSizing: 'border-box' }}
          >
            {REFUND_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Note {reason === 'other' ? '(required)' : '(optional)'}</div>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="What happened? Visible only to staff."
            style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }}
          />
        </div>

        {needsApprover && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
              Second approver email <span style={{ color: '#f87171' }}>*</span>
            </div>
            <input
              type="email"
              value={approverEmail}
              onChange={e => setApproverEmail(e.target.value)}
              placeholder="coworker@squish.co"
              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid rgba(248,113,113,0.3)', background: 'var(--bg)', color: 'var(--text)', fontSize: 14, boxSizing: 'border-box' }}
            />
            <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4, lineHeight: 1.4 }}>
              Refunds over ${SECOND_APPROVER_THRESHOLD_DOLLARS} need a second staffer's sign-off. Must be a different staff member than you.
            </div>
          </div>
        )}

        {error && (
          <div style={{ padding: '10px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#fca5a5', fontSize: 12, marginBottom: 12 }}>
            ⚠ {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={busy} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text2)', fontSize: 13, cursor: busy ? 'wait' : 'pointer' }}>
            Cancel
          </button>
          <button onClick={submit} disabled={busy} style={{ padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--accent)', color: 'white', fontSize: 13, fontWeight: 600, cursor: busy ? 'wait' : 'pointer' }}>
            {busy ? 'Refunding…' : `Refund $${parsedAmount.toFixed(2)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Is this charge for one or more domain registrations / transfers?
// Stripe metadata set by /api/create-payment-intent carries a comma-
// separated `domains` list when the cart has any DOMAIN line. Transfers
// go through a separate endpoint but still land PIs with the same
// metadata convention, so checking `domains` covers both.
function isDomainCharge(charge) {
  return !!(charge?.metadata?.domains);
}

// NameSilo refund windows:
//   - Domain registrations: 3 days
//   - Domain transfers:     59 days
// https://www.namesilo.com/support/v2/articles/policies/cancelling-orders
const REGISTRATION_WINDOW_MS = 3  * 24 * 60 * 60 * 1000;
const TRANSFER_WINDOW_MS     = 59 * 24 * 60 * 60 * 1000;

// Stripe metadata convention: cart items are passed as `domains` (comma-
// separated) and `transfers` if any. We only have `domains` today because
// transfers hit /api/domains/transfers rather than /api/register-domains,
// so most charges with `domains` metadata are registrations. Treat them
// that way — if we wire a distinct transfer-charge convention later, add
// detection here.
function windowForCharge(charge) {
  if (!isDomainCharge(charge)) return null;
  // Heuristic: if metadata explicitly marks this as a transfer order,
  // use the longer window. Otherwise registration window.
  const isTransfer = charge?.metadata?.orderType === 'transfer' || charge?.metadata?.kind === 'transfer';
  return isTransfer ? TRANSFER_WINDOW_MS : REGISTRATION_WINDOW_MS;
}

function hoursLeftInRefundWindow(charge) {
  const windowMs = windowForCharge(charge);
  if (windowMs == null || !charge?.createdAt) return null;
  const ageMs = Date.now() - new Date(charge.createdAt).getTime();
  const remainingMs = windowMs - ageMs;
  return Math.max(0, Math.floor(remainingMs / (60 * 60 * 1000)));
}

// Returns null if refundable, otherwise a human-readable reason.
function refundBlockedReason(charge) {
  if (charge.fullyRefunded)                        return 'Already refunded';
  if (charge.status !== 'succeeded')               return 'Not eligible';
  if (charge.amountCents - charge.refundedCents <= 0) return 'Nothing left to refund';
  if (isDomainCharge(charge)) {
    const hrsLeft = hoursLeftInRefundWindow(charge);
    if (hrsLeft === 0) {
      const windowDays = (windowForCharge(charge) || 0) / (24 * 60 * 60 * 1000);
      return `Domain — past NameSilo ${windowDays}-day cancellation window`;
    }
  }
  return null; // refundable
}

// Group identical charges (same shopper, description, amount, status,
// refund state) into a single summary row. Reps looking at an account with
// 36 monthly email renewals don't want to scroll past 36 near-identical
// rows — but they DO need individual rows when issuing a refund, so groups
// expand on click. Singleton "groups" render unchanged.
function groupChargesForTable(charges) {
  const map = new Map();
  for (const c of charges) {
    const desc = c.description || c.metadata?.domains || '';
    const refundFlag = c.fullyRefunded ? 'R' : (c.refundedCents > 0 ? `P${c.refundedCents}` : '');
    const key = `${c.customerEmail || ''}__${desc}__${c.amountCents}__${c.status}__${refundFlag}`;
    let g = map.get(key);
    if (!g) {
      g = { key, charges: [], totalCents: 0, latestCreatedAt: 0 };
      map.set(key, g);
    }
    g.charges.push(c);
    g.totalCents += c.amountCents || 0;
    if ((c.createdAt || 0) > g.latestCreatedAt) g.latestCreatedAt = c.createdAt || 0;
  }
  const groups = Array.from(map.values());
  for (const g of groups) {
    g.charges.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }
  groups.sort((a, b) => b.latestCreatedAt - a.latestCreatedAt);
  return groups;
}

function TransactionsTab({ accountId }) {
  const [charges, setCharges]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [refundTarget, setRefundTarget] = useState(null);
  const [toast, setToast]         = useState('');
  // Default view: settled outcomes (succeeded or refunded) AND failures.
  //
  // Failures used to be hidden here too, and that was wrong. A declined card is
  // not clutter — it is the single most important thing that can happen on a
  // customer's payment history, and hiding it behind a dropdown meant nobody
  // saw it. Cory R's card declined on 2026-09-08, we charged it again on 09-11,
  // and the only visible trace on his account was a transaction count reading
  // "2 of 3".
  //
  // The genuinely transient statuses — processing, requires_*, canceled — stay
  // behind 'all', which is what this filter was actually for.
  const [statusFilter, setStatusFilter] = useState('settled');
  // Per-group expand state — set of group keys currently showing their
  // individual charges. Survives re-renders but resets on accountId change
  // via the load() useEffect dependency (charges array identity shifts).
  const [openGroupKeys, setOpenGroupKeys] = useState(() => new Set());
  function toggleGroup(key) {
    setOpenGroupKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  async function load() {
    setLoading(true); setError('');
    try {
      const body = await listAccountCharges(accountId);
      setCharges(body.charges || []);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [accountId]);

  function onRefunded(result) {
    setRefundTarget(null);
    setToast(`Refunded $${(result.amountCents / 100).toFixed(2)}. Audit: ${result.auditId}`);
    setTimeout(() => setToast(''), 6000);
    load();
  }

  // NOTE: hooks must run unconditionally — compute visibleCharges + groups
  // BEFORE the early returns below, or the useMemo is skipped on the loading
  // render and runs once charges arrive → React error #310 (hook-count
  // mismatch), which black-screened the Transactions tab.
  const isFailed = (c) => c.status === 'failed';
  const visibleCharges = statusFilter === 'all'
    ? charges
    : charges.filter(c => c.status === 'succeeded' || c.fullyRefunded || c.refundedCents > 0 || isFailed(c));
  // Plain derived values, NOT hooks — see the note above about #310.
  const failedCharges = charges.filter(isFailed);
  const failedTotal = failedCharges.reduce((sum, c) => sum + (c.amountCents || 0), 0);

  const groups = useMemo(() => groupChargesForTable(visibleCharges), [visibleCharges]);

  if (loading) return <div style={{ color: 'var(--text2)', padding: 20 }}>Loading transactions…</div>;
  if (error)   return <div style={{ color: '#fca5a5', padding: 20 }}>⚠ {error}</div>;
  if (!charges.length) return <div style={{ color: 'var(--text2)', padding: 20 }}>No Stripe charges found for any contact on this account.</div>;

  // Single-charge row used for singleton groups AND for individuals rendered
  // beneath an expanded multi-charge group. `nested` adds a small left rule
  // + subtle background so it's visually obvious which summary they belong to.
  const renderChargeRow = (c, nested = false) => {
    const dt = c.createdAt ? new Date(c.createdAt).toLocaleString() : '—';
    const amt = `$${(c.amountCents / 100).toFixed(2)}`;
    const refunded = c.refundedCents > 0;
    const blockedReason = refundBlockedReason(c);
    const rowStyle = nested
      ? { background: 'rgba(124,109,250,0.04)', borderLeft: '2px solid rgba(124,109,250,0.3)' }
      : undefined;
    return (
      <tr key={c.paymentIntentId} style={rowStyle}>
        <td style={{ fontSize: 12, color: 'var(--text2)', paddingLeft: nested ? 24 : undefined }}>{dt}</td>
        <td style={{ fontSize: 12 }}>{c.customerEmail}</td>
        <td style={{ fontSize: 12 }}>
          {amt}
          {refunded && (
            <div style={{ fontSize: 10, color: '#fbbf24' }}>
              −${(c.refundedCents / 100).toFixed(2)} refunded
            </div>
          )}
        </td>
        <td>
          <span style={{
            fontSize: 10, padding: '2px 6px', borderRadius: 4,
            background: c.fullyRefunded ? 'rgba(251,191,36,0.15)' : c.status === 'succeeded' ? 'rgba(74,222,128,0.15)' : 'rgba(107,114,128,0.15)',
            color: c.fullyRefunded ? '#fbbf24' : c.status === 'succeeded' ? '#4ade80' : '#9ca3af',
          }}>
            {c.fullyRefunded ? 'Refunded' : c.status}
          </span>
        </td>
        <td style={{ fontSize: 11, color: 'var(--text2)' }}>
          {c.description || c.metadata?.domains || '—'}
        </td>
        <td style={{ textAlign: 'right' }}>
          {blockedReason === null ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
              <button data-write
                onClick={() => setRefundTarget(c)}
                style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)', fontSize: 11, cursor: 'pointer' }}
              >
                Refund
              </button>
              {isDomainCharge(c) && (() => {
                const hrs = hoursLeftInRefundWindow(c);
                if (hrs == null) return null;
                const warn = hrs < 24;
                return (
                  <span style={{ fontSize: 9, color: warn ? '#fbbf24' : 'var(--text3)', fontFamily: 'var(--mono)' }}>
                    {hrs}h left
                  </span>
                );
              })()}
            </div>
          ) : (
            <span
              title={blockedReason}
              style={{ fontSize: 10, color: 'var(--text3)', fontStyle: 'italic', cursor: 'help' }}
            >
              {c.fullyRefunded ? 'Done' : isDomainCharge(c) ? 'Past window' : '—'}
            </span>
          )}
        </td>
      </tr>
    );
  };

  // Summary row for a multi-charge group. Click toggles expansion of the
  // individual rows. Renders aggregate amount + count badge; refund action
  // is intentionally absent — refunds happen at the individual-charge level
  // and require expanding the group first.
  const renderGroupSummaryRow = (g) => {
    const rep = g.charges[0];
    const isOpen = openGroupKeys.has(g.key);
    const aggAmount = `$${(g.totalCents / 100).toFixed(2)}`;
    const oldest = g.charges[g.charges.length - 1]?.createdAt;
    const dateRange = oldest && oldest !== rep.createdAt
      ? `${new Date(oldest).toLocaleDateString()} – ${new Date(rep.createdAt).toLocaleDateString()}`
      : (rep.createdAt ? new Date(rep.createdAt).toLocaleString() : '—');
    return (
      <tr
        key={g.key}
        onClick={() => toggleGroup(g.key)}
        style={{ cursor: 'pointer', background: isOpen ? 'rgba(124,109,250,0.05)' : undefined }}
        title={isOpen ? 'Hide individual charges' : 'Show individual charges'}
      >
        <td style={{ fontSize: 12, color: 'var(--text2)' }}>{dateRange}</td>
        <td style={{ fontSize: 12 }}>{rep.customerEmail}</td>
        <td style={{ fontSize: 12, fontWeight: 600 }}>
          {aggAmount}
          <div style={{ fontSize: 10, color: 'var(--text3)' }}>total · {g.charges.length} charges</div>
        </td>
        <td>
          <span style={{
            fontSize: 10, padding: '2px 6px', borderRadius: 4,
            background: rep.fullyRefunded ? 'rgba(251,191,36,0.15)' : rep.status === 'succeeded' ? 'rgba(74,222,128,0.15)' : 'rgba(107,114,128,0.15)',
            color: rep.fullyRefunded ? '#fbbf24' : rep.status === 'succeeded' ? '#4ade80' : '#9ca3af',
          }}>
            {rep.fullyRefunded ? 'Refunded' : rep.status}
          </span>
        </td>
        <td style={{ fontSize: 11, color: 'var(--text2)' }}>
          {rep.description || rep.metadata?.domains || '—'}
          <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)', background: 'var(--surface2)', padding: '1px 5px', borderRadius: 3 }}>
            × {g.charges.length}
          </span>
        </td>
        <td style={{ textAlign: 'right', fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
          {isOpen ? '▾ hide' : '▸ expand'}
        </td>
      </tr>
    );
  };

  return (
    <div>
      {toast && (
        <div style={{ padding: '10px 14px', background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.3)', borderRadius: 8, color: '#86efac', fontSize: 13, marginBottom: 12 }}>
          ✓ {toast}
        </div>
      )}

      {failedCharges.length > 0 && (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
            padding: '8px 12px', borderRadius: 6,
            background: 'var(--surface2)', border: '1px solid var(--danger, #b4443a)',
            fontSize: 12, color: 'var(--text)',
          }}
        >
          <strong style={{ fontFamily: 'var(--mono)' }}>
            {failedCharges.length} failed payment{failedCharges.length === 1 ? '' : 's'}
          </strong>
          <span style={{ color: 'var(--text3)' }}>
            ${(failedTotal / 100).toFixed(2)} attempted and declined — listed below.
          </span>
        </div>
      )}

      <div className="table-card">
        <div className="table-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="table-title">Transactions</span>
          <span className="table-count">{visibleCharges.length}{statusFilter === 'settled' && charges.length > visibleCharges.length ? ` of ${charges.length}` : ''}</span>
          <span style={{ flex: 1 }} />
          <label style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Status</label>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={{ padding: '4px 8px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 5, color: 'var(--text)', fontSize: 12, fontFamily: 'var(--font)' }}
          >
            <option value="settled">Settled &amp; failed</option>
            <option value="all">All statuses</option>
          </select>
        </div>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Customer</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Description</th>
              <th style={{ textAlign: 'right' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {groups.map(g => {
              // Singleton "group" = one charge total. Render the original row
              // shape unchanged (no expand affordance, refund button inline).
              if (g.charges.length === 1) return renderChargeRow(g.charges[0]);
              // Multi-charge group: summary row, optionally followed by the
              // individual rows when expanded.
              const isOpen = openGroupKeys.has(g.key);
              return (
                <React.Fragment key={g.key}>
                  {renderGroupSummaryRow(g)}
                  {isOpen && g.charges.map(c => renderChargeRow(c, true))}
                </React.Fragment>
              );
            })}
            {visibleCharges.length === 0 && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'var(--text3)', fontSize: 12 }}>
                  No transactions match the current filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {refundTarget && (
        <RefundModal
          charge={refundTarget}
          accountId={accountId}
          onClose={() => setRefundTarget(null)}
          onRefunded={onRefunded}
        />
      )}
    </div>
  );
}

// ─── Subscriptions Panel (Customer Product Adapter view) ─────────────────────
// Renders every customer-owned product across all services in one list. Reads
// from /api/accounts/:accountId/products, which fans out to every registered
// product service. Email today; hosting + domains + future products as they
// adopt the adapter.
//
// See squish-docs/architecture/customer-product-adapter.md.
// Everything the customer owns, from the Customer Product Adapter: domains,
// hosting, email and AI Visibility in one list. Was "SubscriptionsPanel" and
// sat beside separate Orders and Domains tabs saying much the same thing.
function ProductsPanel({ accountId, tenantId, accountName, onTenantSaved }) {
  const [products, setProducts] = useState(null);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [syncTarget, setSyncTarget] = useState(null);   // domain, when the M365 DNS modal is open
  const [manualDomain, setManualDomain] = useState('');
  // The tenant the email subscriptions carry: { tenantId, candidates }.
  const [subTenant, setSubTenant] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setError('');
      try {
        const r = await listAccountProducts(accountId);
        if (!cancelled) {
          setProducts(r.products || []);
          setSubTenant(r.m365Tenant || null);
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [accountId, refreshKey]);

  if (products === null && !error) return <Loading />;
  if (error) return <div className="error-state">⚠ {error}</div>;

  if (products.length === 0) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: 12, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg3)' }}>
        No customer-owned products on this account.
      </div>
    );
  }

  // Does this account own email? Gates the Microsoft 365 tooling, which used to
  // render for everyone — the first real customer bought one domain and was
  // shown a tenant-link panel and an M365 DNS button for a product he had
  // never had.
  const hasEmail = products.some(p => p.productKind === 'email');

  // The Account's tenant field is only a rep's manual link; a self-serve
  // customer's tenant is recorded on their email subscription and never copied
  // there. Fall back to it, the same order the backend resolves DNS sync in
  // (squish-crm-backend lib/m365-tenant.js), so this panel and the modal show
  // the tenant the sync will actually use.
  const fromSubscription = !tenantId && !!subTenant?.tenantId;
  const effectiveTenant = tenantId || subTenant?.tenantId || '';
  const tenantCandidates = subTenant?.candidates?.length || 0;

  // Group by productKind so the panel reads like "Email · 2", "Hosting · 1".
  const byKind = {};
  for (const p of products) (byKind[p.productKind] || (byKind[p.productKind] = [])).push(p);

  return (
    <div>
      {Object.entries(byKind).map(([kind, items]) => (
        <div key={kind} className="table-card" style={{ marginBottom: 16 }}>
          <div className="table-header">
            <span className="table-title" style={{ textTransform: 'capitalize' }}>{kind}</span>
            <span className="table-count">{items.length}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {items.map(p => (
              <ProductRow
                key={`${p.productKind}:${p.productId}`}
                accountId={accountId}
                product={p}
                hasEmail={hasEmail}
                onSetupM365Dns={(d) => setSyncTarget(d)}
                onCancel={() => setCancelTarget(p)}
                onReconciled={() => setRefreshKey(k => k + 1)}
              />
            ))}
          </div>
        </div>
      ))}
      {/* The linked-tenant fact only means anything to an account that has
          mailboxes, so it travels with the M365 tooling rather than sitting at
          the top of the page for everyone. */}
      {hasEmail && (
        <div className="table-card" style={{ padding: 16, marginTop: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 4 }}>LINKED M365 TENANT</div>
              <div style={{ fontSize: 14, fontFamily: 'var(--mono)', color: effectiveTenant ? 'var(--accent)' : 'var(--text3)' }}>
                {effectiveTenant || (!tenantId && tenantCandidates > 1
                  ? `— ${tenantCandidates} tenants on subscriptions, link one —`
                  : '— not linked —')}
              </div>
              {fromSubscription && (
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>from the email subscription</div>
              )}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', maxWidth: 420 }}>
              Used by "Set up M365 DNS" to fetch Microsoft's required records. You can override it per-run in the modal.
            </div>
          </div>
        </div>
      )}
      {/* Runs a sync for a domain that is not on this account's product list —
          a name the customer holds elsewhere, or one mid-transfer. Kept from
          the old Domains tab; gated on email like the rest of the M365 tools,
          which is the only thing that changed about it. */}
      {hasEmail && (
        <div className="table-card" style={{ padding: 16, marginTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 8 }}>Run M365 DNS sync for any domain</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={manualDomain}
              onChange={e => setManualDomain(e.target.value.toLowerCase().trim())}
              placeholder="example.com"
              style={{ flex: 1, padding: '8px 10px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 13, fontFamily: 'var(--mono)' }}
            />
            <button data-write
              onClick={() => manualDomain && setSyncTarget(manualDomain)}
              disabled={!manualDomain}
              style={{ padding: '8px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: manualDomain ? 'pointer' : 'not-allowed', opacity: manualDomain ? 1 : 0.5 }}
            >
              Set up DNS →
            </button>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8 }}>
            Only works for domains registered on NameSilo. Domain must already be added to the linked M365 tenant (or we'll add it during Full Sync).
          </div>
        </div>
      )}
      {syncTarget && (
        <DnsSyncModal
          accountId={accountId}
          domain={syncTarget}
          initialTenantId={effectiveTenant}
          onClose={() => setSyncTarget(null)}
          onTenantSaved={onTenantSaved}
        />
      )}
      {cancelTarget && (
        <RepCancelSubscriptionModal
          product={cancelTarget}
          accountId={accountId}
          onClose={() => setCancelTarget(null)}
          onCancelled={() => { setCancelTarget(null); setRefreshKey(k => k + 1); }}
        />
      )}
    </div>
  );
}

function ProductRow({ accountId, product, onCancel, onReconciled, hasEmail = false, onSetupM365Dns }) {
  const stateColor = product.state === 'DELIVERED' || product.state === 'GDAP_ACCEPTED' || product.state === 'ACTIVE'
    ? '#4ade80'
    : product.state === 'FAILED' || product.state === 'HOLD'
      ? '#f87171'
      : product.state?.startsWith('AWAITING') || product.state?.includes('PENDING')
        ? '#fbbf24'
        : 'var(--text2)';
  const isTerminal = product.state === 'CANCELLED'
    || product.state === 'CANCELLED_BEFORE_PROVISIONING'
    || product.state === 'FAILED';
  const cancellable = !isTerminal && (product.actions || []).some(a => a.kind === 'cancel');
  // Pending (paid-but-unprovisioned) setups have no vendor state to reconcile.
  const reconcilable = !product.pendingSetup && (product.productKind === 'email' || product.productKind === 'hosting');

  // Reconcile-now state lives in the row (vs hoisting to the panel) so each
  // row's busy/result indicator stays scoped. Lightweight enough to inline.
  const [reconcileBusy, setReconcileBusy] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);

  // Support toolkit: live Plesk status/usage for shared hosting rows —
  // answers the routine "is it up / are they full?" without panel SSO.
  const healthCapable = product.productKind === 'hosting' && !product.pendingSetup && !isTerminal;
  const [healthBusy, setHealthBusy] = useState(false);
  const [health, setHealth] = useState(null);

  // Whether a row HAS an admin password is a fact only the registry knows —
  // it is a Managed Cloud thing and shared sites have none. Rather than guess
  // from the fields a product row happens to carry, offer it on every live
  // hosting row and let the server answer 409 with the pointer it already
  // writes for exactly this case.
  const [credentialOpen, setCredentialOpen] = useState(false);

  // A hosting site still on its build-time placeholder name can be put live on
  // the customer's domain from here (the dashboard's "Set domain", for staff).
  const goLiveCapable = healthCapable && isPlaceholderHost(product.productId);
  const [goLiveOpen, setGoLiveOpen] = useState(false);

  // A paid hosting / email purchase the customer never set up can be finished
  // from here (the dashboard's setup wizard, for staff).
  const setupCapable = product.pendingSetup && !isTerminal
    && (product.productKind === 'hosting' || product.productKind === 'email');
  const [setupOpen, setSetupOpen] = useState(false);

  // Domain rows expand into registrar + DNS detail. Registrar-backed only:
  // a domain we merely have an order for (a transfer still in flight) has no
  // NameSilo record to read, and the panel says so rather than inventing one.
  const isDomain = product.productKind === 'domain';
  const [detailOpen, setDetailOpen] = useState(false);
  // Manage = change DNS / nameservers / auto-renew / forwarding / EPP as the
  // signed-in staffer, without impersonating (impersonation denies all domain
  // writes). Only for a domain that is live on this account.
  const manageable = isDomain && !isTerminal && !product.pendingSetup;
  const [manageOpen, setManageOpen] = useState(false);
  // Hosting rows get the same Manage toggle: restore points and CDN.
  const hostingManageable = healthCapable;

  async function handleHealth() {
    setHealthBusy(true);
    setHealth(null);
    try {
      setHealth(await getHostingSupportInfo(product.productId));
    } catch (e) {
      setHealth({ error: e.message });
    }
    setHealthBusy(false);
  }

  const fmtBytes = (b) => {
    if (b === null || b === undefined) return '—';
    if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(1)} GB`;
    if (b >= 1024 ** 2) return `${(b / 1024 ** 2).toFixed(1)} MB`;
    return `${Math.round(b / 1024)} KB`;
  };

  async function handleReconcile() {
    setReconcileBusy(true);
    setReconcileResult(null);
    try {
      const r = await reconcileSubscription(product.productKind, product.productId);
      setReconcileResult(r);
      // Notify parent so it refreshes — drift might have been auto-healed.
      onReconciled?.();
    } catch (e) {
      setReconcileResult({ error: e.message });
    }
    setReconcileBusy(false);
  }

  const btn = {
    padding: '5px 10px', background: 'transparent',
    border: '1px solid var(--border)', borderRadius: 4,
    color: 'var(--text2)', fontSize: 11, fontWeight: 600,
    fontFamily: 'var(--mono)', cursor: 'pointer',
  };

  return (
    <div style={{ borderTop: '1px solid var(--border)' }}>
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '12px 16px',
      flexWrap: 'wrap',
    }}>
      <div style={{ flex: '1 1 280px', minWidth: 0 }}>
        <div style={{ color: 'var(--text)', fontWeight: 600, fontSize: 13 }}>
          {product.label}
        </div>
        <div style={{ color: 'var(--text3)', fontSize: 11, marginTop: 4, fontFamily: 'var(--mono)' }}>
          {product.linkedDomain ? <>{product.linkedDomain} · </> : null}
          {product.billingTerm ? `${product.billingTerm} · ` : ''}
          {product.nextBillingAt ? <>renews {new Date(product.nextBillingAt).toLocaleDateString()}</>
            : product.endsAt ? <>ends {new Date(product.endsAt).toLocaleDateString()}</>
            : null}
        </div>
        <div style={{ color: stateColor, fontSize: 11, marginTop: 4, fontFamily: 'var(--mono)' }}>
          {product.stateLabel || product.state}
          {product.refundEligible ? ' · refund eligible (within NCE 7-day window)' : ''}
        </div>
        {health && (
          <div style={{
            fontSize: 11, marginTop: 6, fontFamily: 'var(--mono)',
            color: health.error ? '#f87171'
                  : health.domainStatus === 'OK' ? '#4ade80'
                  : '#fbbf24',
          }}>
            {health.error
              ? `⚠ ${health.error}`
              : <>
                  {health.domainStatus === 'OK' ? '✓ Serving' : `⚠ Plesk status: ${health.domainStatus || 'unknown'}`}
                  {' · disk '}{fmtBytes(health.diskUsedBytes)}{health.diskLimit ? ` / ${health.diskLimit}` : ''}
                  {health.trafficThisMonth ? ` · traffic ${health.trafficThisMonth}` : ''}
                  {health.registryStatus && health.registryStatus !== 'active' ? ` · registry: ${health.registryStatus}` : ''}
                </>}
          </div>
        )}
        {reconcileResult && (
          <div style={{
            fontSize: 11, marginTop: 6, fontFamily: 'var(--mono)',
            color: reconcileResult.error ? '#f87171'
                  : reconcileResult.drift ? '#fbbf24'
                  : '#4ade80',
          }}>
            {reconcileResult.error
              ? `⚠ Reconcile failed: ${reconcileResult.error}`
              : reconcileResult.drift
                ? `⚠ Drift: ${reconcileResult.drift.summary || reconcileResult.drift.type}${reconcileResult.healed ? ' (auto-healed)' : ''}`
                : '✓ Vendor matches internal state'}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', maxWidth: '100%' }}>
        {healthCapable && (
          <button
            onClick={handleHealth}
            disabled={healthBusy}
            title="Live Plesk status + disk/traffic usage (read-only)"
            style={{ ...btn, cursor: healthBusy ? 'wait' : 'pointer', opacity: healthBusy ? 0.6 : 1 }}
          >
            {healthBusy ? 'Checking…' : 'Health'}
          </button>
        )}
        {healthCapable && (
          <button
            onClick={() => setCredentialOpen(true)}
            title="Reveal the server admin password — logged against you with a reason and note"
            style={btn}
            data-write
          >
            Admin password
          </button>
        )}
        {setupCapable && (
          <button data-write onClick={() => setSetupOpen(o => !o)} style={btn} title="Finish this purchase's setup for the customer — logged against you">
            {setupOpen ? '▾ Set up' : '▸ Set up'}
          </button>
        )}
        {goLiveCapable && (
          <button data-write onClick={() => setGoLiveOpen(o => !o)} style={btn} title="Give this placeholder site the customer's real domain — logged against you">
            {goLiveOpen ? '▾ Go live' : '▸ Go live'}
          </button>
        )}
        {reconcilable && (
          <button data-write
            onClick={handleReconcile}
            disabled={reconcileBusy}
            title="Re-check vendor state for this subscription"
            style={{ ...btn, cursor: reconcileBusy ? 'wait' : 'pointer', opacity: reconcileBusy ? 0.6 : 1 }}
          >
            {reconcileBusy ? 'Reconciling…' : 'Reconcile'}
          </button>
        )}
        {isDomain && (
          <button onClick={() => setDetailOpen(o => !o)} style={btn} title="Registrar status, expiry, lock, nameservers and live DNS">
            {detailOpen ? '▾ Details' : '▸ Details'}
          </button>
        )}
        {hostingManageable && (
          <button data-write onClick={() => setManageOpen(o => !o)} style={btn} title="Restore points and CDN — logged against you">
            {manageOpen ? '▾ Manage' : '▸ Manage'}
          </button>
        )}
        {manageable && (
          <button data-write onClick={() => setManageOpen(o => !o)} style={btn} title="Change DNS records, nameservers, auto-renew, forwarding or send the EPP code — logged against you">
            {manageOpen ? '▾ Manage' : '▸ Manage'}
          </button>
        )}
        {/* M365 DNS setup is an EMAIL workflow. It used to render for every
            account, including ones that have never bought a mailbox — the tool
            was on the only domain-shaped screen we had, not on the accounts it
            applies to. */}
        {isDomain && hasEmail && (
          <button data-write onClick={() => onSetupM365Dns?.(product.productId)} style={btn} title="Point this domain's DNS at the linked Microsoft 365 tenant">
            Set up M365 DNS
          </button>
        )}
        {cancellable && (
          <button data-write onClick={onCancel} style={btn}>
            {product.productKind === 'domain' ? 'Stop auto-renew' : 'Cancel'}
          </button>
        )}
      </div>
      {credentialOpen && (
        <AdminCredentialModal product={product} onClose={() => setCredentialOpen(false)} />
      )}
    </div>
    {setupCapable && setupOpen && (
      <div style={{ padding: '0 16px', background: 'var(--bg)' }}>
        <PendingSetupForm accountId={accountId} product={product} onDone={() => { setSetupOpen(false); onReconciled?.(); }} />
      </div>
    )}
    {goLiveCapable && goLiveOpen && (
      <div style={{ padding: '0 16px', background: 'var(--bg)' }}>
        <HostingGoLive accountId={accountId} placeholder={product.productId} onDone={() => { setGoLiveOpen(false); onReconciled?.(); }} />
      </div>
    )}
    {isDomain && detailOpen && (
      <div style={{ padding: '0 16px', background: 'var(--bg)' }}>
        <DomainDetailPanel domain={product.productId} />
      </div>
    )}
    {manageable && manageOpen && (
      <div style={{ padding: '0 16px', background: 'var(--bg)' }}>
        <DomainManagePanel accountId={accountId} domain={product.productId} />
      </div>
    )}
    {hostingManageable && manageOpen && (
      <div style={{ padding: '0 16px', background: 'var(--bg)' }}>
        <HostingManagePanel accountId={accountId} domain={product.productId} />
      </div>
    )}
    {/* A VPS names its server on the card: IP, droplet, panel, live DNS and
        HTTPS. Shown, not behind a click: the IP is what a rep setting up a
        server needs first. Attaching the domain is Go live, above. */}
    {product.productKind === 'hosting' && product.server && !isTerminal && (
      <div style={{ padding: '0 16px', background: 'var(--bg)' }}>
        <VpsServerPanel domain={product.productId} summary={product.server} />
      </div>
    )}
    </div>
  );
}

// ─── Admin credential reveal ──────────────────────────────────────────────────
//
// The Plesk admin password for a Managed Cloud customer's own server. Reps need
// it for triage — the subscription record deliberately answers with a boolean
// only — but a plaintext credential outlives the session it was read in, so the
// read is priced: a reason, a note saying what it is actually for, and a
// durable audit row squish-backend writes before it will answer.
//
// The UI's job here is to be honest about that, not to hide it. The rep is told
// what is being recorded BEFORE they ask, and what was recorded after.
//
// The taxonomy is the server's; these labels are the human reading of it. If
// the two ever drift the server refuses the value, which is the right way round
// — the alternative is a picker offering options that do not work.
const CREDENTIAL_REASONS = [
  { value: 'triage',           label: 'Triage — the site is down or misbehaving' },
  { value: 'support',          label: 'Support — working a case with the customer' },
  { value: 'service',          label: 'Service — planned maintenance or a change we owe them' },
  { value: 'customer_request', label: 'Customer request — they asked us to go in' },
];
const CREDENTIAL_NOTE_MIN = 15;

function AdminCredentialModal({ product, onClose }) {
  const [reason, setReason] = useState('triage');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [errCode, setErrCode] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const noteShort = note.trim().length < CREDENTIAL_NOTE_MIN;

  async function handleReveal() {
    if (busy) return;
    if (noteShort) {
      setErr(`Add a note of at least ${CREDENTIAL_NOTE_MIN} characters saying what you are doing with it.`);
      return;
    }
    setBusy(true);
    setErr('');
    setErrCode(null);
    try {
      setResult(await revealHostingAdminCredential(product.productId, { reason, note: note.trim() }));
    } catch (e) {
      setErr(e.message);
      // Branch on the CODE, never on the wording — the server owns both, and
      // only one of them is a contract.
      setErrCode(e.code || null);
    }
    setBusy(false);
  }

  async function copyPassword() {
    try {
      await navigator.clipboard.writeText(result.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is permission-gated and can simply refuse. The password is
      // on screen either way, so say so rather than failing silently.
      setErr('Could not copy — select the password above instead.');
    }
  }

  const label = { display: 'block', fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: 'var(--mono)', marginBottom: 6 };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10,
        width: '92%', maxWidth: 480, padding: 24, maxHeight: '92vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Server admin password</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--text3)', marginBottom: 18 }}>
          {product.label} · {product.productId}
        </div>

        {result ? (
          <>
            <div style={{ padding: '12px 14px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, marginBottom: 14 }}>
              <div style={{ ...label, marginBottom: 4 }}>Login</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--text)', marginBottom: 12, wordBreak: 'break-all' }}>{result.login}</div>
              <div style={{ ...label, marginBottom: 4 }}>Password</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <code style={{ fontFamily: 'var(--mono)', fontSize: 14, color: 'var(--text)', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, padding: '6px 8px', wordBreak: 'break-all', flex: '1 1 200px' }}>
                  {result.password}
                </code>
                <button onClick={copyPassword} style={{
                  background: 'transparent', border: '1px solid var(--border)', borderRadius: 4,
                  padding: '6px 10px', color: 'var(--text2)', fontSize: 11, fontWeight: 600,
                  fontFamily: 'var(--mono)', cursor: 'pointer',
                }}>{copied ? 'Copied ✓' : 'Copy'}</button>
              </div>
              {result.panelUrl && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ ...label, marginBottom: 4 }}>Panel</div>
                  <a href={result.panelUrl} target="_blank" rel="noreferrer" style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--accent)', wordBreak: 'break-all' }}>
                    {result.panelUrl}
                  </a>
                </div>
              )}
            </div>
            {/* What was recorded, echoed from the server rather than restated
                here — the rule is the server's and this should not be a second
                copy of it that can drift. */}
            <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.5, marginBottom: 14 }}>
              Recorded against <strong style={{ color: 'var(--text2)' }}>{result.recorded?.readByEmail}</strong> as
              {' '}<strong style={{ color: 'var(--text2)' }}>{result.recorded?.reason}</strong>, with your note.
              {result.auditId && <> Audit <code style={{ fontSize: 11 }}>{result.auditId}</code>.</>}
            </div>
            {err && <div style={{ padding: '10px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 6, color: '#fca5a5', fontSize: 12, marginBottom: 12 }}>⚠ {err}</div>}
            <button onClick={onClose} style={{
              width: '100%', background: 'transparent', border: '1px solid var(--border)', borderRadius: 6,
              padding: '10px', color: 'var(--text2)', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
            }}>Done</button>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 14, padding: '10px 12px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>
              This is the customer's <strong>own server</strong> admin password, and it keeps working after
              you close this window. Reading it is <strong>logged against you</strong> with the reason and
              note below. If you only need panel access for this session, use <strong>SSO</strong> instead —
              that one expires.
            </div>

            <label style={label}>Reason (required)</label>
            <select
              value={reason}
              onChange={e => setReason(e.target.value)}
              style={{
                width: '100%', background: 'var(--bg2)', border: '1px solid var(--border)',
                borderRadius: 6, padding: '8px 10px', color: 'var(--text)', fontSize: 13,
                fontFamily: 'inherit', marginBottom: 14,
              }}
            >
              {CREDENTIAL_REASONS.map(r => (
                // Native select popups render white-on-white in dark mode
                // unless the option carries its own colours.
                <option key={r.value} value={r.value} style={{ background: 'var(--bg2)', color: 'var(--text)' }}>
                  {r.label}
                </option>
              ))}
            </select>

            <label style={label}>Note (required — what are you doing with it?)</label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="e.g., customer reports 500s on every page; checking Plesk service state"
              rows={3}
              style={{
                width: '100%', background: 'var(--bg2)', border: '1px solid var(--border)',
                borderRadius: 6, padding: '8px 10px', color: 'var(--text)', fontSize: 13,
                fontFamily: 'inherit', marginBottom: 6, resize: 'vertical',
              }}
            />
            <div style={{ fontSize: 11, fontFamily: 'var(--mono)', color: noteShort ? 'var(--text3)' : '#4ade80', marginBottom: 14 }}>
              {noteShort
                ? `${note.trim().length} / ${CREDENTIAL_NOTE_MIN} characters`
                : '✓ note is long enough'}
            </div>

            {err && (
              <div style={{ padding: '10px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 6, color: '#fca5a5', fontSize: 12, marginBottom: 12, lineHeight: 1.5 }}>
                ⚠ {err}
                {/* A shared site has no admin password to reveal at all, so
                    saying "try again" would be wrong — point at what works. */}
                {errCode === 'NO_ADMIN_CREDENTIAL' && (
                  <div style={{ marginTop: 6, color: '#fcd34d' }}>
                    Use panel SSO for this one, or the customer login on the subscription record.
                  </div>
                )}
                {errCode === 'CREDENTIAL_AUDIT_FAILED' && (
                  <div style={{ marginTop: 6, color: '#fcd34d' }}>
                    Nothing was disclosed — the read is refused when it cannot be recorded. Try again.
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onClose} disabled={busy} style={{
                flex: 1, background: 'transparent', border: '1px solid var(--border)', borderRadius: 6,
                padding: '10px', color: 'var(--text2)', fontSize: 13, fontWeight: 500, cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
              }}>Cancel</button>
              <button onClick={handleReveal} disabled={busy || noteShort} style={{
                flex: 1, background: noteShort ? 'var(--bg3)' : 'var(--accent)', border: 'none', borderRadius: 6,
                padding: '10px', color: noteShort ? 'var(--text3)' : '#fff', fontSize: 13, fontWeight: 600,
                cursor: busy ? 'wait' : noteShort ? 'default' : 'pointer', fontFamily: 'inherit',
              }}>{busy ? 'Recording…' : 'Reveal password'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function RepCancelSubscriptionModal({ product, accountId, onClose, onCancelled }) {
  const [atPeriodEnd, setAtPeriodEnd] = useState(false);
  const [reason, setReason] = useState('');
  const [caseId, setCaseId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);

  // The cancel action declares HOW this product cancels:
  //   'subscription'   — email/hosting recurring sub (immediate / at-period-end)
  //   'auto-renew'     — domains: lapses at term end, no immediate stop
  //   'cancel-pending' — paid-but-unprovisioned setup: mark cancelled, refund separate
  const cancelMode = (product.actions || []).find(a => a.kind === 'cancel')?.mode || 'subscription';
  const isAutoRenew = cancelMode === 'auto-renew';
  const isPending = cancelMode === 'cancel-pending';
  const noPeriodChoice = isAutoRenew || isPending;   // these modes have no immediate/at-period-end toggle
  const title = isAutoRenew ? 'Stop auto-renew' : isPending ? 'Cancel purchase' : 'Cancel subscription';

  async function handleCancel() {
    if (!reason.trim() || reason.trim().length < 3) {
      setErr('Reason required (3+ chars; this lands in the audit log).');
      return;
    }
    setBusy(true); setErr('');
    try {
      // Route by cancel mode / productKind. Pending setups mark-cancelled (no
      // sub); domains disable auto-renew (no atPeriodEnd); email/hosting cancel
      // the recurring sub.
      let result;
      if (isPending) {
        result = await cancelPendingSetup(product.productKind, product.productId, { reason: reason.trim() });
      } else if (product.productKind === 'domain') {
        result = await cancelDomainSubscription(product.productId, { reason: reason.trim() });
      } else {
        const cancelFn = product.productKind === 'hosting'
          ? cancelHostingSubscription
          : cancelEmailSubscription;
        result = await cancelFn(product.productId, { reason: reason.trim(), atPeriodEnd });
      }
      // Anchor the action to the chosen case (best-effort).
      if (caseId) {
        try { await addCaseComment(caseId, `${title}: ${product.label || product.productId} — reason: ${reason.trim()}`); } catch (_) { /* cancel already done */ }
      }
      setDone(result);
      onCancelled?.();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10,
        width: '92%', maxWidth: 480, padding: 24, maxHeight: '92vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--text3)', marginBottom: 18 }}>
          {product.label} · {product.productId}
        </div>

        {done ? (
          <div style={{ padding: '12px 14px', background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.25)', borderRadius: 6, color: '#86efac', fontSize: 13, lineHeight: 1.5 }}>
            {isAutoRenew
              ? `✓ Auto-renew disabled. The domain stays active until its paid term ends, then lapses.`
              : isPending
                ? <>✓ Purchase cancelled. No money moved — if the customer is owed a refund, issue it via the Refund button{done.paymentIntentId ? <> (charge <code style={{ fontSize: 11 }}>{done.paymentIntentId}</code>)</> : ''}.</>
                : <>✓ Cancelled. {done.atPeriodEnd ? `Service ends at period end.` : `Stripe sub cancelled immediately.`}</>}
            {!noPeriodChoice && done.refundEligible && !done.atPeriodEnd && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#fcd34d' }}>
                Within NCE 7-day window — issue the refund separately via the Refund button.
                Microsoft will refund Pax8; refund the customer through the existing /refund flow with full audit.
              </div>
            )}
          </div>
        ) : (
          <>
            {accountId && <CasePicker accountId={accountId} value={caseId} onChange={setCaseId} />}
            {isPending ? (
              <div style={{ marginBottom: 14, padding: '10px 12px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>
                This purchase was <strong>paid but never provisioned</strong> (setup didn't finish). Cancelling marks it cancelled and stops the customer being pushed to finish setup. <strong>No money moves here.</strong> The customer paid and got nothing, so if they're owed a refund, issue it separately via the Refund button{product.paymentIntentId ? <> (charge <code style={{ fontSize: 11 }}>{product.paymentIntentId}</code>)</> : ''}.
              </div>
            ) : isAutoRenew ? (
              <div style={{ marginBottom: 14, padding: '10px 12px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>
                NameSilo has no immediate cancel. This <strong>disables auto-renew</strong> — the domain stays active until its paid term ends, then lapses. <strong>No automatic refund.</strong> If you're inside NameSilo's 3-day cancellable window and the customer is owed money, issue the refund separately via the Refund button and cancel the registration at NameSilo.
              </div>
            ) : (
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text)', cursor: 'pointer', marginBottom: 8 }}>
                  <input type="radio" checked={!atPeriodEnd} onChange={() => setAtPeriodEnd(false)} />
                  <span><strong>Cancel immediately.</strong> Stops Pax8 NCE + Stripe right now. Will fail with Pax8 422 if past 7-day NCE window.</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text)', cursor: 'pointer' }}>
                  <input type="radio" checked={atPeriodEnd} onChange={() => setAtPeriodEnd(true)} />
                  <span><strong>Cancel at period end.</strong> Stripe sets cancel_at_period_end; Pax8 cancel deferred to renewal cron. Customer keeps service until current paid period ends.</span>
                </label>
              </div>
            )}

            <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: 'var(--mono)', marginBottom: 6 }}>
              Reason (required — audit log)
            </label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g., customer requested via support call #12345"
              rows={2}
              style={{
                width: '100%', background: 'var(--bg2)', border: '1px solid var(--border)',
                borderRadius: 6, padding: '8px 10px', color: 'var(--text)', fontSize: 13,
                fontFamily: 'inherit', marginBottom: 14, resize: 'vertical',
              }}
            />

            {err && <div style={{ padding: '10px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 6, color: '#fca5a5', fontSize: 12, marginBottom: 12 }}>⚠ {err}</div>}

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onClose} disabled={busy} style={{
                flex: 1, background: 'transparent', border: '1px solid var(--border)', borderRadius: 6,
                padding: '10px', color: 'var(--text2)', fontSize: 13, fontWeight: 500, cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
              }}>Keep</button>
              <button onClick={handleCancel} disabled={busy} style={{
                flex: 1, background: '#dc2626', border: 'none', borderRadius: 6,
                padding: '10px', color: '#fff', fontSize: 13, fontWeight: 600, cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
              }}>{busy ? 'Working…' : isAutoRenew ? 'Stop auto-renew' : isPending ? 'Cancel purchase' : (atPeriodEnd ? 'Schedule cancellation' : 'Cancel now')}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Events Tab ───────────────────────────────────────────────────────────────
// Unified account timeline: orders, cases, tasks, carts, impersonation sessions,
// user registrations. Filters + expand-to-drill-in for impersonation audit logs.
const EVENT_META = {
  order:           { color: '#4ade80', glyph: '$',   label: 'Order' },
  activated:       { color: '#4ade80', glyph: '✓',   label: 'Activated' },
  case:            { color: '#fbbf24', glyph: '!',   label: 'Case' },
  closed:          { color: '#6b7280', glyph: '✕',   label: 'Closed' },
  task:            { color: '#60a5fa', glyph: '•',   label: 'Task' },
  cart:            { color: '#a78bfa', glyph: '◨',   label: 'Cart' },
  shared:          { color: '#a78bfa', glyph: '→',   label: 'Shared' },
  paid:            { color: '#4ade80', glyph: '$',   label: 'Paid' },
  impersonate:     { color: '#f87171', glyph: '◎',   label: 'Impersonation' },
  'end-impersonate':{ color: '#6b7280', glyph: '■',  label: 'Ended' },
  user:            { color: '#60a5fa', glyph: '◉',   label: 'User' },
  access:          { color: '#64748b', glyph: '👁',  label: 'Access' },
  // Break-glass unlock — an admin opened the account with no customer
  // confirmation. Red and named for what it is: this line existing is the
  // whole accountability story for that access.
  override:        { color: '#f87171', glyph: '⚠',   label: 'Override' },
  refund:          { color: '#fbbf24', glyph: '↺',   label: 'Refund' },
  // Every email we sent the account's people (squish-backend email_log).
  email:           { color: '#38bdf8', glyph: '✉',   label: 'Email' },
  // Customer Product Adapter event icons (set in squish-crm-backend's
  // events aggregator from the per-kind dispatch). See:
  //   squish-docs/architecture/customer-product-adapter.md
  product:         { color: '#a78bfa', glyph: '◇',   label: 'Product' },
  alert:           { color: '#f87171', glyph: '!',   label: 'Error' },
  cancel:          { color: '#9ca3af', glyph: '⊘',   label: 'Cancelled' },
  check:           { color: '#4ade80', glyph: '✓',   label: 'Active' },
  default:         { color: '#6b7280', glyph: '·',   label: 'Event' },
};
const FILTER_GROUPS = [
  { id: 'all',           label: 'All',          types: null },
  { id: 'orders',        label: 'Orders',       types: ['order.created', 'order.activated'] },
  { id: 'cases',         label: 'Cases',        types: ['case.opened', 'case.closed'] },
  { id: 'tasks',         label: 'Tasks',        types: ['task.logged'] },
  { id: 'carts',         label: 'Carts',        types: ['cart.created', 'cart.shared', 'cart.accepted', 'cart.paid', 'cart.cancelled'] },
  { id: 'impersonation', label: 'Impersonation',types: ['impersonation.started', 'impersonation.ended'] },
  { id: 'access',        label: 'Access',       types: ['access.viewed', 'access.override', 'access.view_only'] },
  { id: 'users',         label: 'Users',        types: ['user.registered'] },
  { id: 'refunds',       label: 'Refunds',      types: ['billing.refund', 'billing.refund_failed'] },
  // Sent, failed, suppressed by preferences, blocked: every status is a type
  // under 'email.'.
  { id: 'emails',        label: 'Emails',       prefix: 'email.' },
  // Customer Product Adapter — events from email + future hosting/domains/
  // managed-cloud as they adopt the adapter. type starts with 'product.'.
  // Filtering by prefix keeps this filter forward-compatible without
  // updating an explicit kind list every time a new event kind ships.
  { id: 'products',      label: 'Products',     prefix: 'product.' },
];

function EventsTab({ accountId }) {
  const [events, setEvents] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(null); // sessionId for expanded impersonation drill-in
  const [actions, setActions] = useState({});     // sessionId -> actions[]

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      try {
        const body = await listAccountEvents(accountId);
        if (cancelled) return;
        setEvents(body.events || []);
        setCounts(body.counts || {});
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [accountId]);

  const filtered = (() => {
    if (filter === 'all') return events;
    const group = FILTER_GROUPS.find(g => g.id === filter);
    if (!group) return events;
    if (group.prefix) return events.filter(e => e.type?.startsWith(group.prefix));
    if (group.types)  return events.filter(e => group.types.includes(e.type));
    return events;
  })();

  async function toggleImpersonationDetail(sessionId) {
    if (expanded === sessionId) { setExpanded(null); return; }
    setExpanded(sessionId);
    if (!actions[sessionId]) {
      try {
        const body = await getImpersonationDetail(sessionId);
        setActions(prev => ({ ...prev, [sessionId]: body.actions || [] }));
      } catch (e) {
        setActions(prev => ({ ...prev, [sessionId]: [{ error: e.message }] }));
      }
    }
  }

  if (loading) return <Loading />;
  if (error) return <div className="error-state">⚠ {error}</div>;

  return (
    <div>
      {/* Filter chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
        {FILTER_GROUPS.map(g => {
          const count = g.id === 'all' ? events.length : (counts[g.id] || 0);
          if (g.id !== 'all' && count === 0) return null;
          const active = filter === g.id;
          return (
            <button
              key={g.id}
              onClick={() => setFilter(g.id)}
              style={{
                padding: '6px 12px', borderRadius: 6,
                background: active ? 'var(--accent)' : 'var(--bg2)',
                color: active ? '#fff' : 'var(--text2)',
                border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                fontSize: 11, fontWeight: 600, fontFamily: 'var(--mono)', letterSpacing: 0.3,
                cursor: 'pointer',
              }}>
              {g.label} · {count}
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: 12, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg2)' }}>
          No events for this filter
        </div>
      ) : (
        <div className="table-card" style={{ padding: 0 }}>
          <div style={{ padding: '0 4px' }}>
            {filtered.map((e, i) => (
              <EventRow
                key={e.id}
                accountId={accountId}
                event={e}
                isLast={i === filtered.length - 1}
                expanded={expanded === e.meta?.sessionId}
                actions={actions[e.meta?.sessionId]}
                onExpand={() => e.type === 'impersonation.started' && toggleImpersonationDetail(e.meta.sessionId)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function stringifyActor(a) {
  if (a == null) return '—';
  if (typeof a === 'string') return a;
  if (typeof a === 'object') return a.email || a.name || a.type || '—';
  return String(a);
}

// Why an email has no copy to show. Keys are lib/email-copy.js's reasons in
// squish-backend; anything unlisted shows as a generic line.
const SECRET_NOT_KEPT = 'no copy kept (it carried a sign-in secret that could not be removed)';
const COPY_WITHHELD = {
  'not-customer-mail': 'not Squish mail, no copy kept',
  'secret-not-named': SECRET_NOT_KEPT,
  'secret-too-short': SECRET_NOT_KEPT,
  'secret-survived-redaction': SECRET_NOT_KEPT,
  'unclassified-kind': 'no copy kept for this email type',
  'too-large': 'no copy kept (too large)',
  'copy-error': 'no copy kept (copy failed)',
};

// The copy of one email, read on demand. Rendered in an iframe sandboxed with
// no permissions at all: the HTML is ours, but it is still mail markup, and
// nothing in it should run or navigate inside the CRM.
function EmailCopy({ accountId, logId }) {
  const [state, setState] = useState({ loading: true });
  const [view, setView] = useState('html');
  useEffect(() => {
    let cancelled = false;
    getAccountEmail(accountId, logId)
      .then(email => { if (!cancelled) setState({ email }); })
      .catch(e => { if (!cancelled) setState({ error: e.message }); });
    return () => { cancelled = true; };
  }, [accountId, logId]);

  const box = { marginTop: 10, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: 10, fontSize: 12 };
  if (state.loading) return <div style={{ ...box, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>Loading email…</div>;
  if (state.error) return <div style={{ ...box, color: '#f87171', fontFamily: 'var(--mono)' }}>⚠ {state.error}</div>;
  const { email } = state;
  const copy = email.copy;
  if (!copy) {
    return <div style={{ ...box, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{COPY_WITHHELD[email.copyWithheld] || 'No copy kept for this email'}</div>;
  }
  const showHtml = view === 'html' && copy.html;
  const row = (k, v) => v ? (
    <div key={k} style={{ display: 'flex', gap: 8 }}>
      <span style={{ color: 'var(--text3)', width: 64, flexShrink: 0 }}>{k}</span>
      <span style={{ color: 'var(--text)', overflowWrap: 'anywhere' }}>{v}</span>
    </div>
  ) : null;
  return (
    <div style={box}>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 11, display: 'grid', gap: 3, marginBottom: 8 }}>
        {row('From', copy.from)}
        {row('To', email.to)}
        {copy.replyTo && copy.replyTo !== copy.from ? row('Reply-To', copy.replyTo) : null}
        {row('Subject', email.subject)}
        {row('Sent', email.sentAt ? new Date(email.sentAt).toLocaleString() : null)}
        {email.attachments?.length > 0 ? row('Files', email.attachments.map(a => a.filename).join(', ')) : null}
        {copy.redacted > 0 && (
          <div style={{ color: '#fbbf24' }}>Sign-in secrets removed from this copy. The customer's email had them.</div>
        )}
      </div>
      {copy.html && copy.text && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          {['html', 'text'].map(v => (
            <button key={v} onClick={() => setView(v)} style={{
              padding: '2px 8px', borderRadius: 4, fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer',
              background: view === v ? 'var(--accent)' : 'var(--bg2)', color: view === v ? '#fff' : 'var(--text2)',
              border: `1px solid ${view === v ? 'var(--accent)' : 'var(--border)'}`,
            }}>{v === 'html' ? 'As sent' : 'Plain text'}</button>
          ))}
        </div>
      )}
      {showHtml ? (
        <iframe
          title={`Email: ${email.subject || ''}`}
          sandbox=""
          referrerPolicy="no-referrer"
          srcDoc={copy.html}
          style={{ width: '100%', height: 520, border: '1px solid var(--border)', borderRadius: 4, background: '#fff' }}
        />
      ) : (
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text)', maxHeight: 520, overflow: 'auto' }}>{copy.text || ''}</pre>
      )}
    </div>
  );
}

function EventRow({ accountId, event, isLast, expanded, actions, onExpand }) {
  const meta = EVENT_META[event.icon] || EVENT_META.default;
  const isImper = event.type === 'impersonation.started';
  const isEmail = event.type?.startsWith('email.');
  const [emailOpen, setEmailOpen] = useState(false);
  return (
    <div style={{ display: 'flex', gap: 12, padding: '14px 16px', borderBottom: isLast ? 'none' : '1px solid var(--border)' }}>
      {/* Icon / vertical line */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          background: meta.color + '22', color: meta.color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'var(--mono)', fontSize: 13, fontWeight: 700,
          border: `1px solid ${meta.color}55`,
        }}>{meta.glyph}</div>
        {!isLast && <div style={{ width: 1, flex: 1, background: 'var(--border)', marginTop: 4 }} />}
      </div>

      {/* Body */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
          <div style={{ color: 'var(--text)', fontWeight: 600, fontSize: 13 }}>
            {event.title}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', flexShrink: 0 }} title={event.timestamp}>
            {relativeTime(event.timestamp)}
          </div>
        </div>
        {event.subtitle && (
          <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>{event.subtitle}</div>
        )}
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6, fontFamily: 'var(--mono)', display: 'flex', gap: 10, alignItems: 'center' }}>
          <span>{stringifyActor(event.actor)}</span>
          <span style={{ padding: '1px 6px', borderRadius: 3, background: meta.color + '15', color: meta.color }}>{meta.label}</span>
          {isImper && (
            <button onClick={onExpand} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer', padding: 0 }}>
              {expanded ? 'hide audit log' : `view audit log (${event.meta?.actionCount || 0})`}
            </button>
          )}
          {isEmail && event.meta?.hasCopy && (
            <button onClick={() => setEmailOpen(o => !o)} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer', padding: 0 }}>
              {emailOpen ? 'hide email' : 'view email'}
            </button>
          )}
          {isEmail && !event.meta?.hasCopy && event.type === 'email.sent' && (
            <span>
              {event.meta?.copyWithheld
                ? (COPY_WITHHELD[event.meta.copyWithheld] || 'no copy kept')
                : 'no copy (sent before copies were kept)'}
            </span>
          )}
        </div>

        {isEmail && emailOpen && <EmailCopy accountId={accountId} logId={event.meta.logId} />}

        {/* Expanded impersonation audit log */}
        {isImper && expanded && (
          <div style={{ marginTop: 10, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: 10, fontFamily: 'var(--mono)', fontSize: 11 }}>
            {!actions ? (
              <div style={{ color: 'var(--text3)' }}>Loading actions…</div>
            ) : actions.length === 0 ? (
              <div style={{ color: 'var(--text3)' }}>No actions logged for this session</div>
            ) : actions[0]?.error ? (
              <div style={{ color: '#f87171' }}>⚠ {actions[0].error}</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ color: 'var(--text3)', textAlign: 'left' }}>
                    <th style={{ padding: '4px 8px' }}>Time</th>
                    <th style={{ padding: '4px 8px' }}>Method</th>
                    <th style={{ padding: '4px 8px' }}>Path</th>
                    <th style={{ padding: '4px 8px' }}>Status</th>
                    <th style={{ padding: '4px 8px' }}>Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {actions.map(a => (
                    <tr key={a.id} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '3px 8px', color: 'var(--text3)' }}>{relativeTime(a.timestamp)}</td>
                      <td style={{ padding: '3px 8px', color: 'var(--text2)' }}>{a.method}</td>
                      <td style={{ padding: '3px 8px', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 400 }}>{a.path}</td>
                      <td style={{ padding: '3px 8px', color: a.status >= 400 ? '#f87171' : 'var(--text2)' }}>{a.status}</td>
                      <td style={{ padding: '3px 8px', color: a.outcome === 'allowed' ? '#4ade80' : a.outcome?.startsWith('rejected') ? '#f87171' : 'var(--text3)' }}>{a.outcome}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Commerce Tab (merges Products + Carts) ──────────────────────────────────
// Products = activated Salesforce Orders (what the customer IS paying for).
// Carts    = draft Firestore Carts (what they MIGHT pay for — rep-built or
//            customer-self-served). Two stages of the same lifecycle; no
//            reason they were separate tabs.

// Unified commerce surface — merges the former Loot (Orders + Draft Carts) and
// Services (Subscriptions + Domains) tabs so there's ONE place for everything
// an account bought, instead of the same orders/products spread across tabs.
// Three sections, down from five (2026-08-25).
//
// Orders / Subscriptions / Domains were three views of ONE question — what does
// this customer have? — split by how we happened to store each thing rather
// than by anything a rep thinks about. A domain is a product; it was its own
// tab only because it arrived through a different pipe. Casey, looking at the
// first real customer: "why is domains by itself, why isn't it just their
// products?"
//
//   Products   what they own NOW, every kind in one list (product adapter)
//   History    what was bought and what was charged — orders + transactions
//   Draft Carts what they might buy. Sales, not ownership, so it stays apart.
function CommerceTab({ accountId, accountName, tenantId, onTenantSaved }) {
  const [section, setSection] = useState('products');

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: 4, marginBottom: 16, width: 'fit-content', flexWrap: 'wrap' }}>
        {[['products', 'Products'], ['history', 'History'], ['carts', 'Draft Carts']].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setSection(k)}
            style={{
              padding: '6px 16px', borderRadius: 6, border: 'none', cursor: 'pointer',
              fontFamily: 'var(--font)', fontSize: 12, fontWeight: 600,
              background: section === k ? 'var(--accent)' : 'transparent',
              color: section === k ? '#fff' : 'var(--text2)',
              transition: 'all 0.15s',
            }}
          >
            {label}
          </button>
        ))}
      </div>
      {section === 'products' && (
        <ProductsPanel accountId={accountId} tenantId={tenantId} accountName={accountName} onTenantSaved={onTenantSaved} />
      )}
      {section === 'history' && (
        <>
          <OrdersTable accountId={accountId} accountName={accountName} />
          <div style={{ height: 16 }} />
          <TransactionsTab accountId={accountId} />
        </>
      )}
      {section === 'carts' && (
        <>
          <CartsTab accountId={accountId} accountName={accountName} />
          <TransferDomainInCard accountId={accountId} />
        </>
      )}
    </div>
  );
}

// ─── Domains Tab ──────────────────────────────────────────────────────────────


// ─── Domain detail panel ──────────────────────────────────────────────────────
// Registry + registrar + live DNS + the registrant (WHOIS) contact for one
// domain, so a rep answers "what is going on with this domain?" without a
// NameSilo login.
//
// ⚠ Each of the four sections carries its own `available` flag and they are
// rendered SEPARATELY. A registrar timeout must show as a failed lookup, never
// as "no DNS records" — the domain list above already carries a comment about
// exactly this mistake, because a rep acts on a positive claim.
function DomainDetailPanel({ domain }) {
  const [state, setState] = useState({ loading: true, data: null, error: '' });

  useEffect(() => {
    let live = true;
    setState({ loading: true, data: null, error: '' });
    getDomainDetail(domain)
      .then(d => { if (live) setState({ loading: false, data: d, error: '' }); })
      .catch(e => { if (live) setState({ loading: false, data: null, error: e.message || 'Lookup failed' }); });
    return () => { live = false; };
  }, [domain]);

  if (state.loading) return <div style={{ padding: 16, color: 'var(--text3)', fontSize: 12 }}>Loading domain detail…</div>;
  if (state.error) return <div style={{ padding: 16, color: '#f87171', fontSize: 12 }}>⚠ {state.error}</div>;

  const { registry, registrar, dns, contact, transfer } = state.data || {};
  const reg = registrar?.data || {};
  // live | transfer_pending | not_in_account | failed — see lib/domainDetailState.
  // Only `failed` is a failed lookup; the other two are NameSilo answering
  // that the domain is not in our account, which mid-transfer is expected.
  const presence = registrarPresence(state.data);
  const pending = presence === 'transfer_pending';
  const latestTransfer = transfer?.latest || null;
  const factPlaceholder = pending ? 'after transfer' : presence === 'not_in_account' ? 'not in account' : undefined;
  const con = contact?.data || {};
  // Our stored auto-renew is an INTENT (a rep or customer asked for it); the
  // registrar's is the actual state. Showing one number for both would hide a
  // failed toggle, which is the exact case worth surfacing.
  const intent = registry?.data?.autoRenew;
  const actual = reg.autoRenew;
  const disagree = registry?.found && typeof intent === 'boolean'
    && typeof actual === 'boolean' && intent !== actual;

  return (
    <div style={{ padding: '4px 0 16px' }}>
      {pending && (
        <div style={{
          padding: '8px 12px', marginBottom: 14, borderRadius: 6, fontSize: 11,
          background: transferNeedsAction(latestTransfer) ? 'rgba(245,158,11,0.1)' : 'rgba(139,92,246,0.08)',
          border: `1px solid ${transferNeedsAction(latestTransfer) ? 'rgba(245,158,11,0.3)' : 'rgba(139,92,246,0.25)'}`,
          color: transferNeedsAction(latestTransfer) ? '#fbbf24' : 'var(--text2)',
        }}>
          <strong>Transferring in</strong> — {transferStageLabel(latestTransfer?.stage)}
          {latestTransfer?.submittedAt ? `, submitted ${formatDate(latestTransfer.submittedAt)}` : ''}.
          {' '}The domain is still at its current registrar, so expiry, lock, nameservers and the registrant
          contact show here once the registry moves it.
          {latestTransfer?.lastError && (
            <div style={{ marginTop: 4, fontFamily: 'var(--mono)' }}>Registrar said: {latestTransfer.lastError}</div>
          )}
        </div>
      )}

      {presence === 'not_in_account' && (
        <div style={{ padding: '8px 12px', marginBottom: 14, borderRadius: 6, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', fontSize: 11, color: '#fbbf24' }}>
          ⚠ The registrar says this domain is <strong>not in our account</strong>, and there is no open transfer for it
          {latestTransfer ? ` (last transfer: ${transferStageLabel(latestTransfer.stage)})` : ''}.
          {' '}It may have expired, moved to another registrar, or a transfer never landed — check before telling the customer anything about it.
          {transfer && !transfer.available && <> Our transfer record could not be read: {transfer.error}.</>}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 14 }}>
        <Fact label="Expires" value={formatDate(reg.expires || reg.expiresAt) || '—'} unavailable={!registrar?.available} placeholder={factPlaceholder} />
        <Fact label="Registrar status" value={reg.status || '—'} unavailable={!registrar?.available} placeholder={factPlaceholder} />
        <Fact label="Auto-renew (registrar)" value={typeof actual === 'boolean' ? (actual ? 'On' : 'Off') : '—'} unavailable={!registrar?.available} placeholder={factPlaceholder} />
        <Fact label="Transfer lock" value={typeof reg.locked === 'boolean' ? (reg.locked ? 'Locked' : 'UNLOCKED') : '—'}
          tone={reg.locked === false ? 'warn' : undefined} unavailable={!registrar?.available} placeholder={factPlaceholder} />
      </div>

      {disagree && (
        <div style={{ padding: '8px 12px', marginBottom: 14, borderRadius: 6, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', fontSize: 11, color: '#fbbf24' }}>
          ⚠ We recorded auto-renew <strong>{intent ? 'on' : 'off'}</strong> but the registrar says <strong>{actual ? 'on' : 'off'}</strong>.
          A toggle did not take — check before telling the customer it renews.
        </div>
      )}

      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text3)', marginBottom: 4 }}>NAMESERVERS</div>
        {presence === 'failed' && <span style={{ fontSize: 12, color: '#f87171' }}>⚠ Registrar lookup failed — {registrar?.error}</span>}
        {pending && <span style={{ fontSize: 12, color: 'var(--text3)' }}>Set at the current registrar until the transfer lands.</span>}
        {presence === 'not_in_account' && <span style={{ fontSize: 12, color: 'var(--text3)' }}>Not in our registrar account.</span>}
        {registrar?.available && (
          (reg.nameservers || []).length
            ? (reg.nameservers || []).map(ns => (
                <div key={ns} style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text2)' }}>{ns}</div>
              ))
            : <span style={{ fontSize: 12, color: 'var(--text3)' }}>—</span>
        )}
      </div>

      {/* Registrant (WHOIS) contact — the name and address the CUSTOMER typed
          when they bought the domain. It is the only place we hold a postal
          address for a self-serve buyer.

          ONE BLOCK. This panel used to render the registrant contact TWICE
          from this same `contact` object, with the NAMESERVERS block between
          them, in two different visual treatments and with slightly different
          privacy wording (introduced by the #67 merge). A rep read the
          customer's name, email, phone and postal address, then nameservers,
          then the same PII again — plausibly as two different records, a stale
          contact and a current one. The grid below is the one that survived
          because it carries what the other did not: the `whoisPrivacy: false`
          warning and the registrar's Contact ID.

          Three states, and the two that show no contact are NOT the same:
          `available: false` means the registrar could not be asked,
          `found: false` means it answered and holds none. Collapsing them
          would tell a rep a customer has no contact on file when the truth is
          a timeout.

          whoisPrivacy has three states too, for the same reason — carried over
          from the block that was removed, which was the only one that showed
          the unknown case. "We did not learn whether this is public" is not
          "it is private". */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text3)' }}>REGISTRANT CONTACT</span>
          {contact?.whoisPrivacy === true && (
            <span style={{ fontSize: 10, fontFamily: 'var(--mono)', padding: '1px 6px', borderRadius: 4, background: 'rgba(139,92,246,0.15)', color: 'var(--accent)' }}>
              WHOIS PRIVACY ON
            </span>
          )}
          {contact?.whoisPrivacy === false && (
            <span style={{ fontSize: 10, fontFamily: 'var(--mono)', padding: '1px 6px', borderRadius: 4, background: 'rgba(245,158,11,0.12)', color: '#fbbf24' }}>
              PUBLIC IN WHOIS
            </span>
          )}
          {/* The third state, carried over from the duplicate block that was
              removed — it was the only one that showed this. A missing answer
              must not render as the private one: a rep telling a customer
              their details are hidden when we never checked is worse than
              saying we do not know. */}
          {contact?.whoisPrivacy === null && contact?.found && (
            <span style={{ fontSize: 10, fontFamily: 'var(--mono)', padding: '1px 6px', borderRadius: 4, background: 'rgba(148,163,184,0.12)', color: 'var(--text3)' }}>
              PRIVACY UNKNOWN
            </span>
          )}
        </div>
        {!contact?.available && contact?.notInAccount && (
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>
            {pending ? 'Held by the current registrar until the transfer lands.' : 'Not in our registrar account, so no contact here.'}
          </div>
        )}
        {!contact?.available && !contact?.notInAccount && (
          <div style={{ fontSize: 12, color: '#f87171' }}>
            ⚠ Contact lookup failed — {contact?.error || 'unavailable'}. This is a failed lookup, not a domain without a contact.
          </div>
        )}
        {contact?.available && !contact.found && (
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>
            The registrar answered and holds no registrant contact for this domain.
          </div>
        )}
        {contact?.available && contact.found && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            <Fact label="Name" value={[con.firstName, con.lastName].filter(Boolean).join(' ') || '—'} />
            <Fact label="Company" value={con.company || '—'} />
            <Fact label="Email" value={con.email || '—'} />
            <Fact label="Phone" value={con.phone || '—'} />
            <Fact label="Address" value={[con.address1, con.city, con.state, con.postalCode, con.country].filter(Boolean).join(', ') || '—'} />
            <Fact label="Contact ID" value={con.contactId || '—'} />
          </div>
        )}
      </div>

      <div>
        <div style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text3)', marginBottom: 4 }}>
          DNS RECORDS {dns?.available ? `(${dns.records.length})` : ''}
        </div>
        {!dns?.available && (
          <div style={{ fontSize: 12, color: '#f87171' }}>
            ⚠ DNS lookup failed — {dns?.error}. This is a failed lookup, not an empty zone.
          </div>
        )}
        {dns?.available && dns.records.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>
            {pending
              ? 'No DNS zone with us yet — DNS stays with the current provider until the transfer lands.'
              : 'No records in our DNS — the domain may use external nameservers.'}
          </div>
        )}
        {dns?.available && dns.records.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ fontSize: 11 }}>
              <thead><tr><th>Type</th><th>Host</th><th>Value</th><th style={{ textAlign: 'right' }}>TTL</th></tr></thead>
              <tbody>
                {dns.records.map((r, i) => (
                  <tr key={r.recordId || r.id || i}>
                    <td style={{ fontFamily: 'var(--mono)', color: 'var(--accent)', fontWeight: 700 }}>{r.type}</td>
                    <td style={{ fontFamily: 'var(--mono)', color: 'var(--text2)' }}>{r.host || r.name || '@'}</td>
                    <td style={{ fontFamily: 'var(--mono)', color: 'var(--text2)', wordBreak: 'break-all', maxWidth: 420 }}>{r.value || r.target || '—'}</td>
                    <td style={{ fontFamily: 'var(--mono)', color: 'var(--text3)', textAlign: 'right' }}>{r.ttl ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// One labelled fact. `unavailable` is distinct from an empty value: it says the
// source could not be asked, which is not the same as "there is nothing here".
// `placeholder` replaces the word "unavailable" when the reason is known (a
// domain still transferring in), so a rep is not told a lookup failed.
function Fact({ label, value, tone, unavailable, placeholder }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--text3)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, fontFamily: 'var(--mono)', color: unavailable ? 'var(--text3)' : tone === 'warn' ? '#fbbf24' : 'var(--text)' }}>
        {unavailable ? (placeholder || 'unavailable') : value}
      </div>
    </div>
  );
}

// Transfer a domain IN from another registrar.
//
// Lives beside Draft Carts, not on the product list: a transfer is something
// the customer does not own yet, so it belongs with the other "might buy"
// surfaces rather than among the things they already have. It sat on the old
// Domains tab because that was the only domain-shaped screen, which is also
// why it greeted accounts that had never bought a domain.
function TransferDomainInCard({ accountId }) {
  const [transferOpen, setTransferOpen] = useState(false);
  return (
    <>
      <TransferAttemptsPanel accountId={accountId} />
      <div className="table-card" style={{ padding: 16, marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 4 }}>Transfer a domain in from another registrar</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', maxWidth: 560 }}>
              Customer unlocks the domain + provides the EPP/auth code. NameSilo adds 1 year to the registration at cost. ICANN window is ~5 days after submission.
            </div>
          </div>
          <button data-write
            onClick={() => setTransferOpen(true)}
            style={{ padding: '8px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
          >
            Transfer Domain →
          </button>
        </div>
      </div>
      {transferOpen && (
        <TransferDomainModal accountId={accountId} onClose={() => setTransferOpen(false)} />
      )}
    </>
  );
}

// ─── Transfer attempts (customer-initiated) ──────────────────────────────────
// The transfers the CUSTOMER started — from their dashboard or at checkout —
// as opposed to the rep-driven modal below, which calls NameSilo directly.
//
// This panel exists for one question: a customer writes in quoting "Transfer
// could not be submitted — please contact support." and the rep needs to know
// what actually happened. That sentence is deliberately generic — squish-backend
// maps every unrecognised registrar error to it so raw upstream text (which can
// name other customers' orders, and once echoed our own
// "Cannot read properties of undefined (reading 'create')") never reaches a
// customer. The real message is staff-only and shows up here as REGISTRAR SAID.
//
// Renders nothing at all when the account has no transfers, which is the common
// case — the Domains tab shouldn't grow a permanent empty table.

const XFER_STATUS_COLORS = {
  awaiting_epp: '#fbbf24',
  submitted:    '#60a5fa',
  completed:    '#4ade80',
  cancelled:    '#9ca3af',
};

function xferDate(v) {
  if (!v) return '—';
  const ms = v?._seconds ? v._seconds * 1000 : v?.seconds ? v.seconds * 1000 : Date.parse(v);
  return Number.isFinite(ms) ? new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}

// What NameSilo says about a frozen transfer, fetched on sight.
//
// Only frozen rows ask — they are rare by construction (a held submit claim on
// a record still awaiting its EPP), so this is at most a call or two, and the
// whole point is that the rep should not have to go and look it up. The answer
// decides what is safe to do next, so it is worth the wait rather than a click.
const VERDICT_STYLE = {
  owned:            { color: '#4ade80', label: 'THE DOMAIN IS ALREADY OURS' },
  transfer_pending: { color: '#60a5fa', label: 'ORDER IS IN FLIGHT AT THE REGISTRAR' },
  no_order_found:   { color: '#fbbf24', label: 'NAMESILO HAS NO TRANSFER FOR THIS DOMAIN' },
  unknown:          { color: '#9ca3af', label: 'NO CLEAR ANSWER FROM THE REGISTRAR' },
};

function RegistrarVerdict({ transferId }) {
  const [state, setState] = useState({ loading: true, data: null, error: '' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchTransferRegistrarStatus(transferId);
        if (!cancelled) setState({ loading: false, data, error: '' });
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, error: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, [transferId]);

  const { loading, data, error } = state;
  if (loading) {
    return <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', marginTop: 8 }}>asking NameSilo…</div>;
  }
  // A failed check is itself the "unknown" answer — say so rather than showing
  // nothing, because silence here reads as "no problem found".
  if (error) {
    return (
      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8, lineHeight: 1.5 }}>
        <span style={{ fontFamily: 'var(--mono)', color: '#9ca3af' }}>REGISTRAR CHECK FAILED</span> — {error}. Treat as unknown and leave the transfer frozen.
      </div>
    );
  }

  const v = VERDICT_STYLE[data.verdict] || VERDICT_STYLE.unknown;
  return (
    <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid rgba(248,113,113,0.25)' }}>
      <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)', letterSpacing: 0.4 }}>NAMESILO SAYS</div>
      <div style={{ fontSize: 11, fontWeight: 700, color: v.color, fontFamily: 'var(--mono)', marginTop: 2 }}>{v.label}</div>
      <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4, lineHeight: 1.5 }}>{data.recommendation}</div>
      {data.registrar?.status && (
        <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)', marginTop: 4 }}>
          registrar status: {data.registrar.status}
        </div>
      )}
    </div>
  );
}

// A transfer sold in a cart without its auth code (source:'cart', still
// awaiting_epp, no submit in flight): the rep types the code the customer read
// them and submits it. squish-backend takes the same single-use claim the
// customer's dashboard does, so a double-click cannot buy it twice. A refused
// code leaves it awaiting a code, and the registrar's reason shows here.
function canAddEpp(t) {
  return t.source === 'cart' && t.status === 'awaiting_epp' && !t.hasEppCode && !t.registrarSubmitClaimedAt;
}

// Extra years on a multi-year transfer. A transfer sold for N years is the
// transfer (+1 year) plus N-1 renewal years that squish-backend's completion
// sweep buys once the domain is in our account (lib/transfer-completion.js).
// The backend derives the state once (lib/transfer-view.js extraYearsState):
//   pending — paid for, not on the domain yet (transfer not landed, or the
//             renewal has not run)
//   applied — renewed onto the domain
//   failed  — the transfer LANDED but the renewal for those years failed
//             (often the registry's 10-year cap); the sweep keeps retrying
// Returns null for a plain one-year transfer, so those rows look as they did.
function extraYearsSummary(t) {
  const n = Math.floor(Number(t && t.extraYears));
  if (!Number.isFinite(n) || n <= 0) return null;
  const state = t.extraYearsState === 'applied' || t.extraYearsState === 'failed' ? t.extraYearsState : 'pending';
  const text = `+${n} extra year${n === 1 ? '' : 's'}`;
  const detail = state === 'applied' ? 'renewed onto the domain after it landed'
    : state === 'failed' ? 'transfer landed, renewal for these years failed'
    : t.status === 'completed' ? 'renewal runs on the next sweep'
    : 'paid at checkout, renewed on once the transfer lands';
  return { n, state, text, detail };
}

// How many rows a human has to look at: a frozen submit, or a landed transfer
// whose paid-for extra years could not be put on the domain. Counted from the
// rows themselves so the badge cannot disagree with the banners below it.
function transfersNeedingHuman(transfers) {
  return (transfers || []).filter(t => t && (t.needsHumanResolution || t.extraYearsNeedAttention)).length;
}

const EXTRA_YEARS_COLORS = { pending: '#60a5fa', applied: '#4ade80', failed: '#f87171' };
const EXTRA_YEARS_LABELS = { pending: 'PENDING', applied: 'APPLIED', failed: 'NOT APPLIED' };

function AddEppForm({ accountId, transferId, onSubmitted }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  async function submit() {
    if (!code.trim() || busy) return;
    setBusy(true); setErr('');
    try {
      await addTransferEpp(accountId, transferId, code.trim());
      setCode('');
      onSubmitted();
    } catch (e) {
      setErr(e.message);
    }
    setBusy(false);
  }
  return (
    <div style={{ marginBottom: 8, padding: '8px 10px', borderRadius: 6, background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.35)' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#fbbf24', fontFamily: 'var(--mono)' }}>PAID — WAITING FOR THE AUTH CODE</div>
      <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4, lineHeight: 1.5 }}>
        Sold in a cart without its code. Enter the EPP / auth code from the current registrar to submit the transfer.
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <input
          value={code}
          onChange={e => setCode(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') submit(); }}
          placeholder="EPP / auth code"
          aria-label="EPP / auth code"
          style={{ flex: 1, minWidth: 120, padding: '5px 8px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)', fontSize: 12, fontFamily: 'var(--mono)' }}
        />
        <button
          onClick={submit}
          disabled={!code.trim() || busy}
          style={{ padding: '5px 12px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: (!code.trim() || busy) ? 'not-allowed' : 'pointer', opacity: (!code.trim() || busy) ? 0.5 : 1 }}
        >
          {busy ? 'Submitting…' : 'Submit transfer'}
        </button>
      </div>
      {err && <div style={{ fontSize: 11, color: '#f87171', marginTop: 6 }}>{err}</div>}
    </div>
  );
}

function TransferAttemptsPanel({ accountId }) {
  const [state, setState] = useState({ loading: true, transfers: [], needsAttention: 0, error: '' });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchAccountDomainTransfers(accountId);
        // The badge counts BOTH human cases from the rows (transfersNeedingHuman),
        // not the server's needsAttention, which counts frozen submits only.
        if (!cancelled) setState({ loading: false, transfers: data.transfers || [], needsAttention: transfersNeedingHuman(data.transfers), error: '' });
      } catch (e) {
        // Say so rather than rendering an empty table — "no transfers" and "we
        // could not look" are different answers to give a waiting customer.
        if (!cancelled) setState({ loading: false, transfers: [], needsAttention: 0, error: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, [accountId, reloadKey]);

  const { loading, transfers, needsAttention, error } = state;
  if (loading || (!transfers.length && !error)) return null;

  return (
    <div className="table-card" style={{ marginTop: 16 }}>
      <div className="table-header">
        <span className="table-title">Transfer attempts</span>
        <span className="table-count">{transfers.length}</span>
        {needsAttention > 0 && (
          <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 4, background: 'rgba(248,113,113,0.15)', color: '#f87171', fontSize: 11, fontWeight: 700, fontFamily: 'var(--mono)' }}>
            {needsAttention} NEED{needsAttention === 1 ? 'S' : ''} A HUMAN
          </span>
        )}
      </div>

      {error && (
        <div style={{ padding: 16, fontSize: 12, color: '#f87171', fontFamily: 'var(--mono)' }}>
          Could not load transfers — {error}
        </div>
      )}

      {!error && (
        <table className="m-cards">
          <thead>
            <tr>
              <th>Domain</th>
              <th style={{ width: 130 }}>Status</th>
              <th style={{ width: 110 }}>Started</th>
              <th>Diagnosis</th>
            </tr>
          </thead>
          <tbody>
            {transfers.map(t => (
              <tr key={t.id}>
                <td className="m-card-title" style={{ fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--text)', fontWeight: 600, verticalAlign: 'top' }}>
                  {t.domain}
                  {t.registrarOrderId && (
                    <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 400, marginTop: 2 }}>
                      order {t.registrarOrderId}
                    </div>
                  )}
                </td>
                <td style={{ verticalAlign: 'top' }}>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700, color: XFER_STATUS_COLORS[t.status] || 'var(--text2)' }}>
                    {String(t.status || '—').toUpperCase()}
                  </span>
                  {t.hasEppCode && (
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>EPP on file</div>
                  )}
                  {extraYearsSummary(t) && (() => {
                    const x = extraYearsSummary(t);
                    return (
                      <div style={{ marginTop: 4 }} title={x.detail}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)' }}>{x.text}</div>
                        <div style={{ fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 700, color: EXTRA_YEARS_COLORS[x.state] }}>
                          {EXTRA_YEARS_LABELS[x.state]}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--text3)', lineHeight: 1.4 }}>{x.detail}</div>
                      </div>
                    );
                  })()}
                </td>
                <td style={{ fontSize: 12, color: 'var(--text2)', verticalAlign: 'top' }}>{xferDate(t.createdAt)}</td>
                <td style={{ verticalAlign: 'top' }}>
                  {canAddEpp(t) && (
                    <AddEppForm accountId={accountId} transferId={t.id} onSubmitted={() => setReloadKey(k => k + 1)} />
                  )}
                  {/* The frozen state. routes/transfers.js will not re-submit or
                      cancel this on its own, and a retry could double-bill, so
                      the banner says what to check rather than offering a button. */}
                  {t.needsHumanResolution && (
                    <div style={{ marginBottom: 8, padding: '8px 10px', borderRadius: 6, background: 'rgba(248,113,113,0.10)', border: '1px solid rgba(248,113,113,0.35)' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#f87171', fontFamily: 'var(--mono)' }}>FROZEN — SUBMIT CLAIM HELD</div>
                      <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4, lineHeight: 1.5 }}>
                        We asked NameSilo to transfer this and could not confirm whether the order went
                        through, so it will not be submitted or cancelled again automatically. A retry
                        could buy the domain twice — the registrar's current answer is below.
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 6, fontFamily: 'var(--mono)' }}>
                        claimed {t.registrarSubmitClaimedAt || '—'}
                      </div>
                      <RegistrarVerdict transferId={t.id} />
                    </div>
                  )}

                  {/* A landed transfer whose paid-for extra years are not on the
                      domain. The sweep retries every run and never renews twice
                      (extraYearsAppliedAt), so there is no retry button here:
                      the human decision is wait, or refund the years. */}
                  {extraYearsSummary(t)?.state === 'failed' && (
                    <div style={{ marginBottom: 8, padding: '8px 10px', borderRadius: 6, background: 'rgba(248,113,113,0.10)', border: '1px solid rgba(248,113,113,0.35)' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#f87171', fontFamily: 'var(--mono)' }}>EXTRA YEARS NOT APPLIED</div>
                      <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4, lineHeight: 1.5 }}>
                        The transfer landed, but renewing on the {extraYearsSummary(t).text.slice(1)} the customer
                        paid for failed. The daily sweep retries it every run and will never renew twice. If it
                        keeps failing (often the registry's 10-year cap on a domain already renewed far out),
                        refund the unapplied years from Billing. Nothing refunds automatically.
                      </div>
                    </div>
                  )}

                  {t.lastError ? (
                    <>
                      <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)', letterSpacing: 0.4 }}>REGISTRAR SAID</div>
                      <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: '#fca5a5', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 8px', marginTop: 3, wordBreak: 'break-word' }}>
                        {t.lastError}
                      </div>
                      {t.safeError && (
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6, lineHeight: 1.5 }}>
                          <span style={{ fontFamily: 'var(--mono)', letterSpacing: 0.4 }}>CUSTOMER SEES</span>{' '}
                          “{t.safeError}”
                        </div>
                      )}
                    </>
                  ) : (
                    <span style={{ fontSize: 12, color: 'var(--text3)' }}>No error recorded</span>
                  )}

                  {t.paymentIntentId && (
                    <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)', marginTop: 6 }}>
                      {t.paymentIntentId}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Transfer Domain Modal ────────────────────────────────────────────────────
function TransferDomainModal({ accountId, onClose }) {
  const [domain, setDomain] = useState('');
  const [eppCode, setEppCode] = useState('');
  const [comp, setComp] = useState(false);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null); // { available, transferPrice, price }
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  async function handleCheck() {
    if (!domain.trim()) return;
    setChecking(true); setError(''); setCheckResult(null);
    try {
      const r = await domainCheck(domain.trim().toLowerCase());
      setCheckResult(r);
    } catch (e) {
      setError(e.message);
    }
    setChecking(false);
  }

  async function handleSubmit() {
    if (!domain.trim() || !eppCode.trim()) return;
    setSubmitting(true); setError('');
    try {
      const r = await transferDomain(accountId, {
        domain: domain.trim().toLowerCase(),
        eppCode: eppCode.trim(),
        comp,
      });
      setResult(r);
    } catch (e) {
      setError(e.message);
    }
    setSubmitting(false);
  }

  const transferable = checkResult && checkResult.transferPrice != null && checkResult.available === false;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, backdropFilter: 'blur(2px)' }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 'min(520px, 94vw)', maxHeight: '85vh', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 14, zIndex: 101, display: 'flex', flexDirection: 'column', boxShadow: '0 16px 48px rgba(0,0,0,0.5)' }}>
        <div style={{ padding: '20px 24px 12px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>Transfer Domain to Squish</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>

        <div style={{ padding: '16px 24px', overflowY: 'auto', flex: 1 }}>
          {result ? (
            <div>
              <div style={{ padding: 16, background: 'rgba(100,220,140,0.08)', border: '1px solid rgba(100,220,140,0.3)', borderRadius: 8, marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#6fdc8c', marginBottom: 6 }}>✓ Transfer submitted</div>
                <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>
                  <strong>{result.domain}</strong> — recorded in Salesforce as Order <span style={{ fontFamily: 'var(--mono)' }}>{result.orderId}</span>.
                  NameSilo order ID: <span style={{ fontFamily: 'var(--mono)' }}>{result.registrarOrderId || '—'}</span>.
                  {result.comped && (
                    <> Booked as complimentary: $0, no rep credit{result.registrarCost ? `, $${Number(result.registrarCost).toFixed(2)} registrar cost as negative margin` : ''}.</>
                  )}
                  {comp && !result.comped && (
                    <div style={{ marginTop: 6, color: 'var(--accent2)' }}>
                      You ticked Complimentary, but the order was booked as a normal rep sale. Fix the Salesforce Order by hand.
                    </div>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.6 }}>
                {result.note}
              </div>
            </div>
          ) : (
            <>
              <label style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>Domain</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <input
                  value={domain}
                  onChange={e => setDomain(e.target.value.toLowerCase().trim())}
                  placeholder="example.com"
                  style={{ flex: 1, boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none' }}
                />
                <button
                  onClick={handleCheck}
                  disabled={!domain || checking}
                  style={{ padding: '8px 14px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text2)', borderRadius: 6, fontSize: 12, fontFamily: 'var(--mono)', cursor: domain && !checking ? 'pointer' : 'not-allowed', opacity: domain && !checking ? 1 : 0.5 }}
                >
                  {checking ? 'Checking…' : 'Check'}
                </button>
              </div>

              {checkResult && (
                <div style={{ padding: 12, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, marginBottom: 14, fontSize: 12, lineHeight: 1.7 }}>
                  {checkResult.available === true && (
                    <div style={{ color: 'var(--accent2)' }}>
                      Domain is available — you'd register it instead of transferring. Use the Cart builder for registration.
                    </div>
                  )}
                  {checkResult.available === false && checkResult.transferPrice != null && (
                    <div style={{ color: 'var(--text2)' }}>
                      Domain is registered elsewhere. Transfer cost:&nbsp;
                      <strong style={{ color: 'var(--text)' }}>${Number(checkResult.transferPrice).toFixed(2)}</strong> (at cost, +1 yr added by ICANN).
                    </div>
                  )}
                  {checkResult.available === false && checkResult.transferPrice == null && (
                    <div style={{ color: 'var(--accent2)' }}>
                      NameSilo doesn't surface transfer pricing for this TLD — transfer may not be supported.
                    </div>
                  )}
                </div>
              )}

              <label style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>EPP / Auth Code</label>
              <input
                value={eppCode}
                onChange={e => setEppCode(e.target.value)}
                placeholder="Paste from current registrar"
                style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none', marginBottom: 10 }}
              />
              <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.6, marginBottom: 14 }}>
                Customer retrieves this from their losing registrar (GoDaddy / Namecheap / etc). Domain must be unlocked and WHOIS privacy disabled for the transfer to succeed.
              </div>

              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, color: 'var(--text2)', lineHeight: 1.5, marginBottom: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={comp} onChange={e => setComp(e.target.checked)} style={{ marginTop: 2 }} />
                <span>
                  <strong style={{ color: 'var(--text)' }}>Complimentary (comp)</strong>
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--text3)' }}>
                    Books a $0 order credited to no rep. Squish pays the registrar, and that cost shows as negative margin.
                  </span>
                </span>
              </label>

              {error && (
                <div style={{ padding: 10, background: 'rgba(250,109,138,0.08)', border: '1px solid rgba(250,109,138,0.3)', borderRadius: 6, fontSize: 12, color: 'var(--accent2)', marginBottom: 12 }}>
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ padding: '12px 24px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text3)', borderRadius: 6, fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              onClick={handleSubmit}
              disabled={!domain.trim() || !eppCode.trim() || submitting || (checkResult && !transferable)}
              style={{ padding: '8px 20px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)', opacity: (!domain.trim() || !eppCode.trim() || submitting || (checkResult && !transferable)) ? 0.5 : 1 }}
            >
              {submitting ? 'Submitting…' : 'Submit Transfer'}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Carts Tab ────────────────────────────────────────────────────────────────

function CartsTab({ accountId, accountName }) {
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [building, setBuilding] = useState(false);
  const [sharing, setSharing] = useState(null); // cartId currently being shared
  const [shareResult, setShareResult] = useState(null); // { cartId, url, sentTo, emailed }
  const [checkingOut, setCheckingOut] = useState(null); // cart currently being checked out by rep
  const [billingSnapshot, setBillingSnapshot] = useState(null); // for picking shopper + existing cards when opening checkout

  async function load() {
    setLoading(true);
    setError('');
    try {
      const data = await listAccountCarts(accountId);
      setCarts(data.carts || []);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }

  useEffect(() => { load(); }, [accountId]);

  async function handleCancel(cartId) {
    if (!window.confirm('Cancel this cart? It will remain for audit but be marked cancelled.')) return;
    try {
      await cancelCart(cartId);
      load();
    } catch (e) {
      setError(e.message);
    }
  }

  async function handleShare(cartId) {
    setSharing(cartId);
    setError('');
    try {
      const result = await shareCart(cartId, { accountId, sendEmail: true });
      setShareResult({ ...result, cartId });
      load();  // refresh so the status pill flips to "shared"
    } catch (e) {
      setError(e.message);
    } finally {
      setSharing(null);
    }
  }

  function statusClass(s) {
    return ({
      draft: 'badge-gray',
      shared: 'badge-yellow',
      accepted: 'badge-blue',
      paid: 'badge-green',
      cancelled: 'badge-red',
    })[s] || 'badge-gray';
  }

  function fmtCartDate(v) {
    if (!v) return '—';
    // Firestore Timestamp from REST comes back as { _seconds, _nanoseconds }
    if (typeof v === 'object' && v._seconds) return formatDate(new Date(v._seconds * 1000));
    return formatDate(v);
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>Rep-built carts</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
            Drafts live in Firestore until checkout converts them to a Salesforce Order.
          </div>
        </div>
        <button data-write
          onClick={() => setBuilding(true)}
          style={{ padding: '8px 18px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
        >
          + Build Cart
        </button>
      </div>

      {error && <div className="error-state" style={{ marginBottom: 12 }}>⚠ {error}</div>}

      <div className="table-card">
        <div className="table-header">
          <span className="table-title">Carts</span>
          <span className="table-count">{carts.length}</span>
        </div>
        {loading && <Loading />}
        {!loading && (
          <table className="m-cards">
            <thead>
              <tr>
                <th>Cart ID</th>
                <th>Items</th>
                <th>Total</th>
                <th>Status</th>
                <th>Built by</th>
                <th>Created</th>
                <th style={{ width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {carts.map(c => (
                <tr key={c.id}>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)' }}>{c.id.slice(0, 8)}…</td>
                  <td className="m-card-title" style={{ color: 'var(--text)' }}>
                    {(c.items || []).slice(0, 3).map((i, n) => (
                      <div key={n} style={{ fontSize: 12, color: 'var(--text2)' }}>
                        {i.productName || i.productCode} × {i.quantity}
                      </div>
                    ))}
                    {(c.items || []).length > 3 && (
                      <div style={{ fontSize: 11, color: 'var(--text3)', fontStyle: 'italic' }}>
                        +{c.items.length - 3} more
                      </div>
                    )}
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', color: 'var(--accent3)' }}>{formatCurrency(c.total)}</td>
                  <td><span className={`badge ${statusClass(c.status)}`}>{c.status}</span></td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)' }}>{c.createdBy?.email || '—'}</td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)' }}>{fmtCartDate(c.createdAt)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      {!['paid', 'cancelled'].includes(c.status) && (c.items || []).length > 0 && (
                        <button data-write
                          onClick={() => setCheckingOut(c)}
                          style={{ background: '#059669', border: 'none', color: '#fff', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--mono)' }}
                          title="Charge a card with the customer on the phone (MOTO)"
                        >
                          Checkout
                        </button>
                      )}
                      {['draft', 'shared'].includes(c.status) && (c.items || []).length > 0 && (
                        <button data-write
                          onClick={() => handleShare(c.id)}
                          disabled={sharing === c.id}
                          style={{ background: 'var(--accent)', border: 'none', color: '#fff', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: sharing === c.id ? 'wait' : 'pointer', fontFamily: 'var(--mono)' }}
                        >
                          {sharing === c.id ? '…' : c.status === 'shared' ? 'Re-send' : 'Share'}
                        </button>
                      )}
                      {!['paid', 'cancelled'].includes(c.status) && (
                        <button data-write
                          onClick={() => handleCancel(c.id)}
                          style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text3)', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {carts.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text3)', padding: 32 }}>
                  No carts yet — click + Build Cart to create one.
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {building && (
        <CartBuilderModal
          accountId={accountId}
          accountName={accountName}
          onClose={() => setBuilding(false)}
          onSaved={() => load()}
        />
      )}

      {shareResult && (
        <ShareCartResultModal
          result={shareResult}
          onClose={() => setShareResult(null)}
        />
      )}

      {checkingOut && (
        <RepCheckoutLauncher
          accountId={accountId}
          cart={checkingOut}
          onClose={() => setCheckingOut(null)}
          onPaid={() => { setCheckingOut(null); load(); }}
        />
      )}
    </div>
  );
}

// ─── RepCheckoutLauncher ─────────────────────────────────────────────────────
// Small wrapper around RepCheckoutModal that first resolves the shopper
// (cart.customerEmail or primary contact on the account) and their existing
// card count, so the modal knows whether to force-default on save.
function RepCheckoutLauncher({ accountId, cart, onClose, onPaid }) {
  const [shopper, setShopper] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Try cart.customerEmail first (set by share flow / prospect cart)
        let email = cart.customerEmail || null;
        let name = null;
        let hasExistingCards = false;
        // Pull per-contact billing status to resolve email + card count
        const billing = await getAccountBillingStatus(accountId).catch(() => null);
        if (billing?.perContact) {
          const match = email
            ? billing.perContact.find(c => (c.email || '').toLowerCase() === email.toLowerCase())
            : billing.perContact[0];
          if (match) {
            email = email || match.email;
            name = [match.firstName, match.lastName].filter(Boolean).join(' ').trim() || null;
            hasExistingCards = match.hasCardOnFile;
          }
        }
        if (!email) {
          throw new Error('No shopper email on the cart or this account\u2019s contacts. Add a contact with an email first.');
        }
        if (!cancelled) setShopper({ email, name, hasExistingCards });
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [accountId, cart]);

  if (loading) {
    return (
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
        <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg2)', padding: 30, borderRadius: 12, color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: 13 }}>Loading shopper…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
        <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg2)', padding: 30, borderRadius: 12, maxWidth: 420, border: '1px solid rgba(248,113,113,0.3)' }}>
          <div style={{ color: '#f87171', fontSize: 14, marginBottom: 12 }}>⚠ {error}</div>
          <button onClick={onClose} style={{ background: 'var(--accent)', border: 'none', color: '#fff', padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>Close</button>
        </div>
      </div>
    );
  }
  return (
    <RepCheckoutModal
      cart={cart}
      shopperEmail={shopper.email}
      shopperName={shopper.name}
      hasExistingCards={shopper.hasExistingCards}
      onClose={onClose}
      onPaid={onPaid}
    />
  );
}

// ─── Share-cart confirmation modal ───────────────────────────────────────────
function ShareCartResultModal({ result, onClose }) {
  const [copied, setCopied] = useState(false);

  function copyUrl() {
    if (!result.url) return;
    try {
      navigator.clipboard.writeText(result.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) { /* fallback via select() below */ }
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, backdropFilter: 'blur(2px)' }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 'min(520px, 94vw)', maxHeight: '85vh', background: 'var(--bg)', border: '1px solid var(--border)',
        borderRadius: 14, zIndex: 101, display: 'flex', flexDirection: 'column', boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
      }}>
        <div style={{ padding: '20px 24px 12px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>Cart shared ✓</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>
        <div style={{ padding: '16px 24px' }}>
          {result.emailed && result.sentTo && (
            <div style={{
              padding: 12, background: 'rgba(100,220,140,0.08)', border: '1px solid rgba(100,220,140,0.3)',
              borderRadius: 8, marginBottom: 14, fontSize: 12, color: 'var(--text2)', lineHeight: 1.6,
            }}>
              ✉ Email sent to <strong style={{ color: 'var(--text)', fontFamily: 'var(--mono)' }}>{result.sentTo}</strong>
              {result.fallbackUsed && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Primary contact had no email — sent to account manager instead.</div>}
            </div>
          )}
          {!result.emailed && (
            <div style={{
              padding: 12, background: 'rgba(250,204,80,0.08)', border: '1px solid rgba(250,204,80,0.25)',
              borderRadius: 8, marginBottom: 14, fontSize: 12, color: 'var(--text2)', lineHeight: 1.6,
            }}>
              Couldn't send the email automatically{result.emailError ? ` (${result.emailError})` : ''}. Use the link below to share manually.
            </div>
          )}

          {result.diag && (
            <div style={{
              padding: '8px 10px', marginBottom: 10,
              fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--text3)',
              background: 'var(--bg2)', border: '1px dashed var(--border)', borderRadius: 6,
            }}>
              staff: <span style={{ color: result.diag.staffSource ? 'var(--accent)' : 'var(--accent2)' }}>{result.diag.staffSource || 'unresolved'}</span>
              {' · '}body: <span style={{ color: result.diag.emailSource === 'claude' ? 'var(--accent)' : 'var(--accent2)' }}>{result.diag.emailSource || '—'}</span>
            </div>
          )}

          <label style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>Customer link</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              readOnly
              value={result.url || ''}
              onFocus={e => e.target.select()}
              style={{ flex: 1, boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 12, outline: 'none' }}
            />
            <button onClick={copyUrl} style={{ padding: '8px 16px', background: 'var(--accent)', border: 'none', color: '#fff', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--mono)' }}>
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 10, lineHeight: 1.6 }}>
            Link is valid for 30 days. Sharing again rotates the token — the old URL stops working.
          </div>
        </div>
        <div style={{ padding: '12px 24px 16px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 20px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}>
            Done
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Main CustomerDetail ──────────────────────────────────────────────────────

// `accountToken` used to arrive as a prop and was attached to exactly one of
// this page's fetches. Every other call — events, charges, products, billing,
// orders, carts, cases, impersonation — went out without it, which is how the
// consent boundary ended up enforced on one endpoint out of a dozen. The token
// now lives in lib/accountSession and getHeaders() attaches it to everything,
// so a new call is covered by default instead of by remembering.
//
// App keeps one of these mounted per open account tab and hides the rest, so
// `active` says whether this is the one on screen, and `onLoaded` hands the
// sidebar tab its name once the record arrives (a Tickets click knows the id
// and nothing else).
export default function CustomerDetail({ accountId, onBack, onNavigateToTicket, active = true, onLoaded }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [tab, setTab]         = useState('overview');
  const [stageDeal, setStageDeal] = useState(null);
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [ticketOpened, setTicketOpened]   = useState(null); // the last ticket opened here, for the confirmation line

  const tabs = ['overview', 'loot', 'cases', 'chats', 'events'];

  function loadData() {
    setLoading(true);
    setError(null);
    fetch(`${API}/api/customer/${accountId}`, {
      headers: getHeaders(),
    })
      .then(r => r.json())
      .then(d => {
        if (d.error) throw new Error(d.error);
        setData(d);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { setData(null); loadData(); }, [accountId]);

  const loadedName = data?.account?.Name || null;
  const loadedNumber = data?.account?.AccountNumber || null;
  useEffect(() => {
    if (loadedName || loadedNumber) onLoaded?.({ name: loadedName, number: loadedNumber });
  }, [loadedName, loadedNumber]);

  // Tell the ~ console which account is on screen, so "this account" resolves
  // to an Id without the staffer retyping it. Contact emails ride along because
  // the console's FOS bridge is scoped by customer email. Only the tab on
  // screen publishes: hidden tabs stay mounted, and two accounts in the merge
  // would leave "this account" meaning whichever one mounted last.
  useViewContext(active && data?.account ? {
    label: data.account.Name || 'Account',
    view: 'account-detail',
    accountId,
    accountName: data.account.Name || null,
    accountNumber: data.account.AccountNumber || null,
    accountType: data.account.Type || null,
    accountPhone: data.account.Phone || null,
    isManaged: !!data.account.Is_Managed__c,
    referralCode: data.referralCode || null,
    contacts: (data.contacts || []).slice(0, 10).map(c => `${c.Name || '(no name)'} <${c.Email || 'no email'}>`),
    primaryContactEmail: (data.contacts || []).find(c => c.Email)?.Email || null,
    openCaseCount: (data.cases || []).filter(c => c.Status !== 'Closed').length,
    lifetimeSpend: data.commerce?.lifetimeSpend ?? null,
    orderCount: data.commerce?.orderCount ?? null,
    activeTab: tab,
  } : null);

  const transferNote = pendingTransferNote(data?.commerce);

  return (
    <div>
      {stageDeal && (
        <StageChangeModal
          opp={stageDeal}
          onClose={() => setStageDeal(null)}
          onSaved={() => { setStageDeal(null); loadData(); }}
        />
      )}
      {/* Tighter chrome than the global defaults — this page's Overview is
          meant to fit one screen, so every vertical px above the tabs counts. */}
      <div className="page-header" style={{ marginBottom: 14 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontFamily: 'var(--mono)', fontSize: 12, padding: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
          ← Back to search
        </button>
        {data?.account && (
          <>
            <h1 className="page-title">{data.account.Name}</h1>
            <p className="page-subtitle">
              Account #{data.account.AccountNumber || '—'} · {data.account.Type || 'Account'}
              {data.account.CreatedDate && (
                <> · Since {formatDate(data.account.CreatedDate)}</>
              )}
              {data.account.Is_Managed__c && <span className="badge badge-green" style={{ marginLeft: 10, verticalAlign: 'middle' }}>Managed</span>}
              {data.referralCode && (
                <span
                  className="badge badge-blue"
                  style={{ marginLeft: 10, verticalAlign: 'middle' }}
                  title={`Acquired via referral link ?ref=${data.referralCode} (first-touch, permanent). Economics on the Referrals page.`}
                >
                  ⇄ ref: {data.referralCode}
                </span>
              )}
            </p>
          </>
        )}
      </div>

      {loading && <Loading />}
      {error && <div className="error-state">⚠ {error}</div>}

      {data && (
        <>
          {/* Page-level stat strip. Account # moved into the subtitle so
              we free the slot for Lifetime Spend — the single most useful
              at-a-glance commerce metric on an account. Kept compact (3 cards
              usually) and shown on every tab. */}
          <div className="stat-grid m-tiles" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 14 }}>
            <div className="stat-card" style={{ padding: '12px 18px' }}><div className="stat-label" style={{ marginBottom: 4 }}>Lifetime Spend</div><div className="stat-value green" style={{ fontSize: 22 }}>{formatCurrency(data.commerce?.lifetimeSpend || 0)}<span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text3)', fontFamily: 'var(--mono)', letterSpacing: 0, marginLeft: 8 }}>{data.commerce?.orderCount || 0} order{data.commerce?.orderCount === 1 ? '' : 's'}</span></div>{transferNote && <div title={transferNote.title} style={{ fontSize: 11, color: 'var(--text2)', fontFamily: 'var(--mono)', marginTop: 3, cursor: 'help' }}>{transferNote.text}</div>}</div>
            <div className="stat-card" style={{ padding: '12px 18px' }}><div className="stat-label" style={{ marginBottom: 4 }}>Phone</div><div className="stat-value-sm" style={{ fontFamily: 'var(--mono)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginTop: 4 }}>{data.account?.Phone || '—'}</div></div>
            <div className="stat-card" style={{ padding: '12px 18px' }}><div className="stat-label" style={{ marginBottom: 4 }}>Open Cases</div><div className="stat-value pink" style={{ fontSize: 22 }}>{data.cases?.filter(c => c.Status !== 'Closed').length || 0}</div></div>
            <div className="stat-card" style={{ padding: '12px 18px' }}><div className="stat-label" style={{ marginBottom: 4 }}>Active Deals</div><div className="stat-value accent" style={{ fontSize: 22 }}>{data.opportunities?.filter(o => !o.StageName?.includes('Closed')).length || 0}</div></div>
          </div>

          {/* Tabs */}
          <div className="m-scroll-x" style={{ display: 'flex', gap: 4, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: 4, marginBottom: 14, width: 'fit-content' }}>
            {tabs.map(t => (
              <button key={t} onClick={() => setTab(t)} style={{ padding: '6px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontFamily: 'var(--font)', fontSize: 13, fontWeight: 600, textTransform: 'capitalize', background: tab === t ? 'var(--accent)' : 'transparent', color: tab === t ? 'white' : 'var(--text2)', transition: 'all 0.15s' }}>{t}</button>
            ))}
          </div>

          {/* Overview */}
          {tab === 'overview' && data.account && (
            <OverviewTab data={data} accountId={accountId} onSaved={loadData} onNavigateToTicket={onNavigateToTicket} />
          )}

          {/* Chats — every assistant conversation on this account, pinned to it.
              The same browser the Assistant page uses; a rep reading a case
              should be able to see what the bot already told this customer
              without leaving the record. Conversations only carry an Account
              once the visitor is signed in — an anonymous session before
              sign-up has nothing to attach to and will not appear here. */}
          {tab === 'chats' && (
            <ConversationsBrowser accountId={accountId} height="calc(100vh - 420px)" />
          )}

          {/* Events */}
          {tab === 'events' && (
            <EventsTab accountId={accountId} />
          )}

          {/* Cases */}
          {tab === 'cases' && (() => {
            // Blended work view: support Cases + sales Deals (Opportunities) in
            // one "Cases" stream, distinguished by a work-type badge. Both stay
            // their own Salesforce objects — this just unifies the display. (The
            // broader "every account session is a case you work" model is a
            // separate workflow design.)
            const work = [
              ...data.cases.map(c => ({
                id: c.Id, type: 'Support',
                ref: c.CaseNumber, title: c.Subject || '—',
                status: c.Status, statusCls: statusBadge(c.Status),
                detail: [c.Priority, c.Origin].filter(Boolean).join(' · ') || '—',
                date: c.CreatedDate,
                onClick: onNavigateToTicket ? () => onNavigateToTicket(c.Id) : null,
              })),
              ...data.opportunities.map(o => ({
                id: o.Id, type: 'Deal',
                ref: '—', title: o.Name || '—',
                status: o.StageName, statusCls: statusBadge(o.StageName),
                detail: [o.Amount != null ? formatCurrency(o.Amount) : null, o.Probability ? `${o.Probability}%` : null].filter(Boolean).join(' · ') || '—',
                date: o.CloseDate || o.CreatedDate,
                // Deals used to be inert rows here. Clicking one now opens the
                // stage change — the only place in this CRM that can ask why a
                // deal was lost at the moment it's lost.
                onClick: () => setStageDeal(o),
                hint: 'Change stage',
              })),
            ].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
            return (
              <>
              {newTicketOpen && (
                <NewTicketForm
                  accountId={accountId}
                  contacts={data.contacts || []}
                  onCancel={() => setNewTicketOpen(false)}
                  onCreated={(r) => { setNewTicketOpen(false); setTicketOpened(r); loadData(); }}
                />
              )}
              {ticketOpened && !newTicketOpen && (
                <div style={{ fontSize: 12, color: 'var(--accent3)', marginBottom: 10, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span>
                    ✓ Ticket #{ticketOpened.caseNumber || '…'} opened
                    {ticketOpened.visibility === 'staff'
                      ? ' as a silent ticket. The customer was not told.'
                      : ` for ${contactLabel(ticketOpened.contact) || 'the customer'}, ${deliveryText(ticketOpened)}.`}
                    {ticketOpened.warnings?.length ? ` ⚠ ${ticketOpened.warnings.join('; ')}` : ''}
                  </span>
                  {onNavigateToTicket && <button type="button" onClick={() => onNavigateToTicket(ticketOpened.caseId)} style={editBtn}>Open ticket</button>}
                  <button type="button" onClick={() => setTicketOpened(null)} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 12 }}>dismiss</button>
                </div>
              )}
              <div className="table-card">
                <div className="table-header">
                  <span className="table-title">Cases</span><span className="table-count">{work.length}</span>
                  {!newTicketOpen && (
                    <button data-write type="button" onClick={() => { setTicketOpened(null); setNewTicketOpen(true); }} style={{ ...editBtn, marginLeft: 'auto' }}>+ New ticket</button>
                  )}
                </div>
                <table className="m-cards">
                  <thead><tr><th>Type</th><th>Ref</th><th>Subject</th><th>Status</th><th>Detail</th><th>Date</th></tr></thead>
                  <tbody>
                    {work.map(w => (
                      <tr key={w.id}
                        onClick={w.onClick || undefined}
                        style={{ cursor: w.onClick ? 'pointer' : 'default' }}
                        title={w.onClick ? (w.hint || 'Open ticket') : undefined}
                      >
                        <td><span className="badge" style={{ background: w.type === 'Deal' ? 'rgba(124,109,250,0.15)' : 'rgba(236,72,153,0.15)', color: w.type === 'Deal' ? 'var(--accent)' : '#f9a8d4' }}>{w.type}</span></td>
                        <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--accent)' }}>{w.ref}</td>
                        <td className="m-card-title" style={{ color: 'var(--text)', fontWeight: 600 }}>{w.title}</td>
                        <td>{w.status ? <span className={`badge ${w.statusCls}`}>{w.status}</span> : '—'}</td>
                        <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)' }}>{w.detail}</td>
                        <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)' }}>{formatDate(w.date)}</td>
                      </tr>
                    ))}
                    {work.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text3)', padding: 32 }}>No cases or deals</td></tr>}
                  </tbody>
                </table>
              </div>
              </>
            );
          })()}

          {tab === 'loot' && (
            <CommerceTab
              accountId={accountId}
              accountName={data.account?.Name || ''}
              tenantId={data.account?.M365_Tenant_ID__c || ''}
              onTenantSaved={() => loadData()}
            />
          )}
        </>
      )}
    </div>
  );
}